import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
// import { MeasurementUnits, InventoryCategories } from "../utils/enums";
import { 
  DSCommunitySchema, CredentialSchema, KitchenSpaceSchema, 
  ResourcePlanSchema, PlanSchema, ResourceType, 
  ResourceSchema, RequirementSchema, ClosedDatesType, ClosedTimeBlockType,
  ResourceOptionsPropsType, PlanRecurrenceOptions, selectedDaysType, 
  ResourceOptionType, MembershipSchema, MessageSchema, FeeSchema, DiscountSchema, 
  InviteTeamMemberFormDataSchema, TeamMemberSchema, PermissionCategory, TeamRoleTypes, FloorPlanSchema,
  PlanResourceAssociation, InventorySchema } from "../data/types";
// import { useResourceState } from "../contexts/kitchencontext";
import { useNavigate } from "react-router-dom";
import { AddCircleOutline, Add, AddCircle, OpenInNew, 
  CheckBox, CheckBoxOutlineBlank, CheckCircle, 
  Cancel, Delete, RadioButtonUnchecked, RadioButtonChecked } from "@mui/icons-material";
import { toast } from "sonner";
// import StartBooking from "../components/StartBooking";
import CreateMembership from "../components/CreateMembership";
// import AddMessageList from "../molecules/addMessageList";
import LoadingScreen from "../pages/shared/LoadingScreen"
import { ReactComponent as InfoIcon } from '../icons/InfoIcon.svg'
import { ReactComponent as UploadIcon } from '../icons/UploadIcon.svg'
import UserAvatar from '../components/UserAvatar';
import { ReactComponent as AccountIcon } from '../icons/AccountIcon.svg'
import { ReactComponent as PermissionsIcon } from '../icons/PermissionsIcon.svg'
// import { ReactComponent as AddImageIcon } from '../icons/AddImageIcon.svg'
import { ReactComponent as RightArrow } from "../icons/ScrollRightArrow.svg";
import { ReactComponent as LeftArrow } from "../icons/ScrollLeftArrow.svg";

import { ReactComponent as UpArrow } from "../icons/ScrollUpIcon.svg";
import { ReactComponent as DownArrow } from "../icons/ScrollDownIcon.svg";

import { ReactComponent as DeleteIcon } from "../icons/DeleteIcon.svg";
// import { useMemberState } from "../contexts/membercontext";
import { useLoadMemberships, useLoadMessages, useLoadRequests, useSendMessage } from "../hooks/memberhooks";
import { useResourceHook } from "../hooks/resourcehooks";
import { useLoadPlans } from "../hooks/communityhook";
import { maxHours, capitalizeString, capitalizeWords, getChangedAttributes, styleThousandCommas, areObjectsEqual } from "../utils/helpers";
import { FoodBusinessFinancialIntelligenceCategories, FoodBusinessFundingCategories, usStates, USStatesEnum } from "../utils/enums";
import { AIRIWHEELS_RATE_PLAN, FINANCIAL_WHEEL_OPTIONS, FULFILLMENT_MODE_OPTIONS, buildFundingPricingSummary, isAiriWheelsResource } from "../utils/fundingPartners";
import { FieldLabelWithTooltip } from "../components/FieldLabelWithTooltip";
import DscvryWebhookConnect from "../components/DscvryWebhookConnect";
import { DSCVRY_REGISTER_URL, isContactBuilderResource } from "../utils/contactBuilder";
import { FinancialWheelOption } from "../components/FinancialWheelOption";
import { CustomAddImageBox } from "../components/AddImageBox";
import OperatingHoursList from "./OperatingHoursList";
import { useMemberState } from "../contexts/membercontext";
import StartKitchenBooking from "../components/StartBooking";
import { TeamPermissionsForm } from "./TeamRoles";
import { useManageTeam } from "../hooks/communityhook";
// import { useLoadCommunity } from "../hooks/authhooks";
import { useForm, useWatch, Control, UseFormSetValue } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AddKitchenSchema, AddCredentialSchema, 
  AddFeeSchema, AddDiscountSchema, AddPlanSchema, AddRequirementSchema, EditPlanSchema, InviteTeamMemberSchema } from "../utils/validationSchemas";
import {
  PlanTierResourceFeaturesField,
  PlanResourceInventoryFeaturesField,
  allowsTierResourceFeatures,
  getResourceInventoryItems,
  resolveMembershipPlanType,
  resolveResourcePlanType,
  resourcePlanIncludesAssociatedResources,
  resourcePlanHasValidAssociatedResources,
  sanitizeAssociatedResources,
  sanitizeResourcePlanAssociations,
  // sanitizeResourcePlanInventory,
} from "./forms/PlanTierResourceFeatures";

// const EMPTY_PLAN_ASSOCIATIONS: PlanResourceAssociation[] = [];
const EMPTY_PLAN_FEATURES: string[] = [];

/** Clears linked resources/inventory and resets the tier back to membership-only. */
const dissociateTierResources = (
  setValue: (name: "associated_resources" | "plan_type", value: unknown) => void,
  associationsRef: React.MutableRefObject<PlanResourceAssociation[]>
) => {
  associationsRef.current = [];
  setValue("associated_resources", []);
  setValue("plan_type", "membership");
};

/** Syncs linked-resource edits into react-hook-form so submit payload matches associationsRef. */
const syncMembershipAssociationsToForm = (
  setValue: (name: "associated_resources" | "plan_type", value: unknown) => void,
  associations: PlanResourceAssociation[]
) => {
  const planType = resolveMembershipPlanType(associations);
  setValue("plan_type", planType);
  setValue("associated_resources", associations.length > 0 ? associations : []);
};

/** Applies linked-resource payload only when the tier period supports resource features. */
const applyMembershipPlanAssociations = (
  data: PlanSchema,
  recurrence: PlanRecurrenceOptions | string | undefined,
  associationsRef: React.MutableRefObject<PlanResourceAssociation[]>
) => {
  if (allowsTierResourceFeatures(recurrence as PlanRecurrenceOptions)) {
    const isDual = resolveMembershipPlanType(associationsRef.current) === "dual";
    const sanitizedAssociations = sanitizeAssociatedResources(
      associationsRef.current,
      undefined,
      isDual ? (data.plan_max_hours ?? 0) : undefined
    );
    data.plan_type = resolveMembershipPlanType(sanitizedAssociations);
    data.associated_resources = data.plan_type === "dual" ? sanitizedAssociations : [];
    return;
  }

  data.plan_type = "membership";
  data.associated_resources = [];
};

/** Applies perks and bundled inventory for a resource booking plan */
const applyResourcePlanAssociations = (
  data: ResourcePlanSchema,
  resource: ResourceSchema | undefined,
  associationsRef: React.MutableRefObject<PlanResourceAssociation[]>,
  featuresRef: React.MutableRefObject<string[]>,
  includeAssociatedResources: boolean,
  existingPlanType?: ResourcePlanSchema["plan_type"]
) => {
  data.features = featuresRef.current.filter((feature) => feature.trim().length > 0);
  const keepResourcePlanType = !existingPlanType || existingPlanType === "resource";

  if (!includeAssociatedResources) {
    data.associated_resources = [];
    if (keepResourcePlanType) {
      data.plan_type = "resource";
      if (data.is_fulltime_hours) {
        data.is_fulltime_hours = false;
      }
    }
    return;
  }

  const inventoryItemsById = new Map<string, InventorySchema>();
  if (resource) {
    getResourceInventoryItems(resource).forEach((item) => {
      if (item.id) {
        inventoryItemsById.set(item.id, item);
      }
    });
  }

  const sanitizedAssociations = resource
    ? sanitizeResourcePlanAssociations(
      resource,
      associationsRef.current,
      inventoryItemsById,
      data.plan_max_hours ?? 0
    )
    : [];
  data.associated_resources = sanitizedAssociations;
  if (keepResourcePlanType) {
    data.plan_type = "resource";
  } else if (
    includeAssociatedResources &&
    (resourcePlanHasValidAssociatedResources(sanitizedAssociations) || Boolean(resource?.id))
  ) {
    if (!resourcePlanHasValidAssociatedResources(sanitizedAssociations) && resource?.id) {
      data.associated_resources = [{
        resource_id: resource.id,
        resource_name: resource.name,
        max_duration: data.plan_max_hours ?? 0,
        inventory: [],
      }];
    }
    data.plan_type = "dual";
  } else {
    data.plan_type = resolveResourcePlanType(sanitizedAssociations);
  }
};

type PlanTierFeaturesFieldProps = {
  featuresRef: React.MutableRefObject<string[]>;
  initialFeatures?: string[];
};

/** Isolated tier-features editor - avoids parent watch("features") re-rendering the full plan form. */
const PlanTierFeaturesField = memo<PlanTierFeaturesFieldProps>(({
  featuresRef,
  initialFeatures = EMPTY_PLAN_FEATURES,
}) => {
  const [features, setFeatures] = useState(initialFeatures);

  useEffect(() => {
    setFeatures(initialFeatures);
    featuresRef.current = initialFeatures;
  }, [initialFeatures, featuresRef]);

  const updateFeatures = useCallback((next: string[]) => {
    featuresRef.current = next;
    setFeatures(next);
  }, [featuresRef]);

  return (
    <div className="flex flex-col">
      <span className="flex flex-row text-[#4D4D4D] text-[14px] font-raleway font-medium mb-1">
        Perks &amp; benefits
      </span>
      {/* <p className="text-[13px] text-[#808080] font-open-sans mb-2">
        Highlights members get with this tier (e.g. priority booking, discounts).
      </p> */}
      <div className="grid grid-flow-row gap-y-2">
        {features.length > 0 && features.map((feature, index) => (
          <div key={index} className="flex flex-row items-center gap-x-2">
            <input
              className="border-[1px] text-sm border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg px-3 py-2"
              value={feature}
              onChange={(e) => {
                const next = [...features];
                next[index] = e.target.value;
                updateFeatures(next);
              }}
              placeholder={`Add perk or benefit #${index + 1}`}
            />
            <button
              type="button"
              className="flex items-center justify-center rounded-lg h-[34px] w-[34px] border-[1px] border-[#E9E9E9]/90 hover:bg-gray-200"
              onClick={() => {
                const next = [...features];
                next.splice(index + 1, 0, "");
                updateFeatures(next);
              }}
              title="Add feature below"
            >
              <AddCircleOutline className="text-[#A1A1A1] m-auto" style={{ height: 18, width: 18 }} />
            </button>
            <button
              type="button"
              className="flex items-center justify-center rounded-lg h-[34px] w-[34px] border-[1px] border-[#E9E9E9]/90 hover:bg-gray-200"
              onClick={() => {
                const next = [...features];
                next.splice(index, 1);
                updateFeatures(next);
              }}
              title="Remove feature"
            >
              <DeleteIcon className="text-[#A1A1A1] m-auto" style={{ height: 18, width: 18 }} />
            </button>
          </div>
        ))}
        {features.length < 1 && (
          <button
            type="button"
            className="w-fit border-0 text-primary-300 font-raleway font-medium rounded-md px-3 py-1 text-sm"
            onClick={() => updateFeatures([""])}
          >
            <Add className="text-primary-300 m-auto" style={{ height: 18, width: 18 }} /> Add New Perk or Benefit
          </button>
        )}
      </div>
    </div>
  );
});
PlanTierFeaturesField.displayName = "PlanTierFeaturesField";

type PlanFulltimeHoursFieldProps = {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  /** When set, only show for these plan types. Defaults to membership + dual. */
  allowedPlanTypes?: Array<PlanSchema["plan_type"]>;
};

/** Toggle for round-the-clock booking on membership and dual tiers */
const PlanFulltimeHoursField = memo<PlanFulltimeHoursFieldProps>(({
  control,
  setValue,
  allowedPlanTypes = ["membership", "dual"],
}) => {
  const planType = useWatch({ control, name: "plan_type" }) as PlanSchema["plan_type"];
  const isFulltimeHours = Boolean(useWatch({ control, name: "is_fulltime_hours" }));
  const showField = allowedPlanTypes.includes(planType);

  if (!showField) {
    return null;
  }

  return (
    <div className="flex flex-row items-center justify-between gap-4 rounded-lg bg-[#FAFAFA] px-4 py-3">
      <div className="flex flex-col flex-1 min-w-0">
        <span className="text-[#4D4D4D] text-[14px] font-raleway font-medium">
          {/* Round-the-clock booking */}
          24/7 hours access
        </span>
        <p className="text-[13px] text-[#808080] font-open-sans mt-0.5">
          {/* Members can book at any hour within their tier allowance. */}
          Members can use their tier membership allowance at any time of day.
        </p>
      </div>
      <label className="relative inline-flex items-center cursor-pointer shrink-0">
        <input
          type="checkbox"
          className="sr-only peer"
          checked={isFulltimeHours}
          onChange={(event) => {
            setValue("is_fulltime_hours", event.target.checked, { shouldDirty: true, shouldValidate: true });
          }}
          data-testid="fulltime-hours-toggle"
        />
        <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-0 peer-focus:ring-primary-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[50%] after:translate-y-[-50%] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-400 min-w-[36px] min-h-[20px]" />
      </label>
    </div>
  );
});
PlanFulltimeHoursField.displayName = "PlanFulltimeHoursField";

/** Clears bundled inventory refs while keeping resource booking plans as resource-only */
const clearResourcePlanAssociatedResources = (
  setValue: UseFormSetValue<any>,
  associationsRef: React.MutableRefObject<PlanResourceAssociation[]>
) => {
  associationsRef.current = [];
  setValue("plan_type", "resource", { shouldDirty: true });
  setValue("is_fulltime_hours", false, { shouldDirty: true });
};

// type InviteTeamMemberFormData = z.infer<typeof InviteTeamMemberSchema>;
// import { z } from "zod";


// TO BE LOADED FROM COMMUNITIES / REQUIRED DOCS
// const credentialCategories = ["ID", "license", "certificate", "insurance", "other"]


// const capitalizeString = (string: string) => string?.replace(/\w/, c => c?.toUpperCase())
type InviteTeamMemberFormProps = {
  setShowInviteModal: (show: boolean) => void
  showSuccessModal: boolean
  setShowSuccessModal: (show: boolean) => void
}

// type TeamRoleTypes = 'viewer' | 'editor' | 'admin' | 'owner';

// type InviteForm = {
//   fullName: string;
//   email: string;
//   lastName: string;
//   role: TeamRoleTypes;
// }

export const InviteTeamMemberForm: React.FC<InviteTeamMemberFormProps> = ({setShowInviteModal, showSuccessModal, setShowSuccessModal}) => {
  const { inviteTeamMemberData } = useManageTeam()
  const [inviteStep, setInviteStep] = useState<"details" | "permissions">("details");
  const [teamMemberForm, setTeamMemberForm] = useState<TeamMemberSchema>({
    id: "",
    first_name: "",
    last_name: "",
    username: "",
    email: "",
    role: "viewer",
    avatar: "",
    status: "invited",
    community_id: "",
    permissions: {
      community: {
        expanded: false,
        permissions: {
          manageResources: false,
          manageRequests: false,
          manageMembers: false,
          manageCommunity: false,
          managePayments: false,
          viewMessages: false,
          accessSettings: false,
        }
      }
    }
  });

  const { register, handleSubmit, formState: { errors }, reset } = useForm<InviteTeamMemberFormDataSchema>({
    resolver: zodResolver(InviteTeamMemberSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      first_name: teamMemberForm.first_name,
      last_name: teamMemberForm.last_name,
      email: teamMemberForm.email,
      role: teamMemberForm.role,
    },
  });

  const togglePermission = (memberEmail: string, category: string, permission: string) => {
    if (memberEmail === teamMemberForm.email) {
      setTeamMemberForm(prev => ({
        ...prev,
        permissions: {
          ...prev.permissions,
          [category]: {
            ...prev.permissions[category as keyof typeof prev.permissions],
            permissions: {
              ...prev.permissions[category as keyof typeof prev.permissions].permissions,
              [permission]: !(prev.permissions[category as keyof typeof prev.permissions].permissions as any)[permission]
            }
          }
        }
      }));
    }
  };
  

  const handleInviteSubmit = async () => {
    console.log("submitting...")
    console.log("inviteStep", inviteStep)
    console.log("errors", errors)
    console.log("teamMemberForm", teamMemberForm)
    const response = await inviteTeamMemberData(teamMemberForm)
    console.log("inviteTeamMemberData response", response)
    if (response) {
      // setShowInviteModal(false);      
      setInviteStep("details");
      setTeamMemberForm({first_name: "", last_name: "", username: "", email: "", role: "viewer", avatar: "", permissions: { community: { expanded: false, permissions: { manageResources: false, manageRequests: false, manageMembers: false, manageCommunity: false, managePayments: false, viewMessages: false, accessSettings: false } } }, status: "invited", community_id: "" });
      reset({ first_name: "", last_name: "", email: "", role: "viewer" })
      setShowSuccessModal(true);
      toast.success("Invite sent successfully")
    } else {
      toast.error("Failed to send invite")
    }
  };
  return (
    <>
      {/* <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"> */}

      {/* </div> */}

      {/* Success Modal */}
      {showSuccessModal ? (
            <div className="px-6 py-8 bg-white text-center rounded-lg mx-[10px]">
              {/* Success Icon */}
              <div className="flex flex-col w-16 h-16 bg-white border-[2px] border-primary-400 rounded-full items-center justify-center mx-auto mb-4">
                <div className="w-14 h-14 bg-primary-400 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              
              {/* Success Message */}
              <h3 className="text-xl font-raleway font-bold text-black mb-2">Invite Sent</h3>
              <p className="text-gray-600 font-open-sans mb-6">
                Your team member has been invited and will receive an email with instructions to join.
              </p>
              
              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setShowInviteModal(true);
                  }}
                  className="w-full bg-primary-200 font-raleway font-semibold text-white py-3 rounded-md hover:bg-primary-400"
                >
                  Add another member
                </button>
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setShowInviteModal(false);
                  }}
                  className="w-full border font-raleway font-semibold border-[#E9E9E9]/90 text-black py-3 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
      ) : (
        <div className="bg-white rounded-lg w-full">
          {/* Modal Header */}
          <div className="px-2 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-black font-raleway">Invite team member</h2>
            <p className="text-[#4D4D4D]/70 text-sm mt-1 font-open-sans">Fill in the details below to invite a team member</p>
          </div>
          
          {/* Navigation Tabs */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setInviteStep("details")}
              className={`flex-1 py-3 px-2 flex items-center justify-center space-x-2 font-semibold font-raleway text-sm ${
                inviteStep === "details" 
                  ? "text-primary-300 border-b-2 border-primary-300" 
                  : "text-[#4D4D4D]/70"
              }`}
            >
              <AccountIcon className="w-4 h-4" />
              <span>Member Details</span>
            </button>
            <button
              onClick={() => setInviteStep("permissions")}
              className={`flex-1 py-3 px-2 flex items-center justify-center space-x-2 font-semibold font-raleway text-sm ${
                inviteStep === "permissions" 
                  ? "text-primary-300 border-b-2 border-primary-300" 
                  : "text-[#4D4D4D]/70"
              }`}
            >
              <PermissionsIcon className="w-4 h-4" />
              <span>Permissions</span>
            </button>
          </div>
          
          {/* Modal Content */}
          <div className="px-2 py-4">
            {inviteStep === "details" ? (
              <form id="invite-member-form" className="space-y-4" onSubmit={handleSubmit(() => setInviteStep("permissions"))}>
                <div className="flex flex-row gap-x-4">
                  <div className="w-1/2">
                    <label className="block text-sm font-medium text-[#808080]/50 font-raleway mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      placeholder="Enter first name"
                      className="w-full h-[51px] px-3 py-2 text-sm font-open-sans border border-[#E9E9E9]/90 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300"
                      {...register("first_name")}
                      onChange={(e) => setTeamMemberForm(prev => ({ ...prev, first_name: e.target.value }))}
                    />
                    {errors.first_name && <span className="text-red-500 text-sm font-open-sans">{errors.first_name.message as string}</span>}
                  </div>
                  <div className="w-1/2">
                    <label className="block text-sm font-medium text-[#808080]/50 font-raleway mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      placeholder="Enter last name"
                      className="w-full h-[51px] px-3 py-2 text-sm font-open-sans border border-[#E9E9E9]/90 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300"
                      {...register("last_name")}
                      onChange={(e) => setTeamMemberForm(prev => ({ ...prev, last_name: e.target.value }))}
                    />
                    {errors.last_name && <span className="text-red-500 text-sm font-open-sans">{errors.last_name.message as string}</span>}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#808080]/50 font-raleway mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    placeholder="Enter email address"
                    className="w-full h-[51px] px-3 py-2 text-sm font-open-sans border border-[#E9E9E9]/90 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300"
                    {...register("email")}
                    onChange={(e) => setTeamMemberForm(prev => ({ ...prev, email: e.target.value }))}
                  />
                  {errors.email && <span className="text-red-500 text-sm font-open-sans">{errors.email.message as string}</span>}
                </div>
                {/* <div>
                  <label className="block text-sm font-medium text-[#808080]/50 font-raleway mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    placeholder="Enter username"
                    className="w-full h-[51px] px-3 py-2 text-sm font-open-sans border border-[#E9E9E9]/90 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300"
                    {...register("username")}
                    onChange={(e) => setTeamMemberForm(prev => ({ ...prev, username: e.target.value }))}
                  />
                  {errors.username && <span className="text-red-500 text-sm font-open-sans">{errors.username.message as string}</span>}
                </div> */}
                <div>
                  <label className="block text-sm font-medium text-[#808080]/50 font-raleway mb-1">
                    Select role 
                  </label>
                  <select
                    // placeholder="Select the team member's role"
                    className="w-full h-[51px] px-3 py-2 text-sm font-open-sans border border-[#E9E9E9]/90 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300"
                    {...register("role")}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTeamMemberForm(prev => ({ ...prev, role: e.target.value as TeamRoleTypes }))}
                  >
                    {/* <option value="">-- Select Role --</option> */}
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                    <option value="owner" disabled>Owner</option>
                  </select>
                  {errors.role && <span className="text-red-500 text-sm font-open-sans">{errors.role.message as string}</span>}
                </div>
              </form>
            ) : (
              <div className="space-y-3">
                <div className="text-sm text-gray-600 mb-3">Select permissions for the new team member:</div>
                {/* {['kitchen Spaces', 'Fresh Food', 'Documentations', 'Funding', 'Culinary Training'].map((category) => (
                  <div key={category} className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
                    <span className="text-sm text-gray-700">{category}</span>
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                ))} */}

                <div className="p-2 border border-[#E9E9E9] rounded-md"> 
                  <TeamPermissionsForm member={teamMemberForm} category="community" categoryData={teamMemberForm.permissions.community as PermissionCategory} togglePermission={togglePermission} />
                </div>
              </div>
            )}
          </div>
          
          {/* Modal Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
            <button
              onClick={() => setShowInviteModal(false)}
              className="text-[#4D4D4D]/70 hover:text-gray-800 font-raleway font-medium"
            >
              Cancel
            </button>
            <div className="flex space-x-2">
              {inviteStep === "permissions" && (
                <button
                  onClick={() => setInviteStep("details")}
                  className="text-primary-200 hover:text-primary-400 font-raleway mr-4 font-medium"
                >
                  Back
                </button>
              )}
              <button
                type={inviteStep === "details" ? "submit" : "button"}
                form={inviteStep === "details" ? "invite-member-form" : undefined}
                onClick={inviteStep === "details" ? undefined : handleInviteSubmit}
                className={`px-4 py-2 rounded-md bg-primary-200 font-raleway font-medium text-white hover:bg-primary-400`}
              >
                {inviteStep === "details" ? "Next" : "Send invite"}
              </button>
            </div>
          </div>
        </div>
      )
      }
  </>
  )
}



type AddCredentialFormProps = {
  handleClose: () => void;
  handleSubmitCredential: (credential: CredentialSchema) => void;
};

const AddCredentialForm: React.FC<AddCredentialFormProps> = (props) => {
  var { handleSubmitCredential, handleClose } = props;
  // var { requirements } = useResourceState();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset
  } = useForm<any>({
    resolver: zodResolver(AddCredentialSchema),
    defaultValues: {
      category: "",
      file: undefined
    }
  });

  const [selectedCategory, setSelectedCategory] = useState<string>("");

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(e.target.value);
    setValue("category", e.target.value);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setValue("file", file);
    }
  };

  const onSubmit = (data: CredentialSchema) => {
    handleSubmitCredential(data);
    reset();
    setSelectedCategory("");
  };

  return (
    <div className="flex flex-col justify-evenly items-center h-full">
      <span onClick={handleClose} className="ml-auto w-full flex cursor-pointer">
          <Cancel className="text-[#A1A1A1] hover:text-[#777] h-[18px] w-[18px] ml-auto" />
      </span>
      <div className="flex flex-col w-11/12">
        <h1 className="text-[18px] text-black font-raleway font-bold text-left my-2">Add Credential</h1>
        <h1 className="text-[16px] font-open-sans text-[#4D4D4D] text-left mb-2">Upload your credential to continue.</h1>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="font-open-sans w-11/12 grid grid-flow-row gap-y-5">
        <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">Credential Category 
            <InfoIcon className="h-3 w-3 my-auto mx-1 text-primary-300" />
          </span>
          <select
            {...register("category")}
            value={selectedCategory}
            onChange={handleCategoryChange}
            className="border-[1px] border-[#E9E9E9] text-slate-500 bg-inherit w-full my-1 rounded-lg"
          >
            <option value="">-- Category --</option>
            <option value="ID">ID</option>
            <option value="license">License</option>
            <option value="certificate">Certificate</option>
            <option value="insurance">Insurance</option>
            <option value="other">Other</option>
          </select>
          {errors.category && <span className="text-red-500 text-sm font-open-sans">{String(errors.category?.message)}</span>}
        </label>

        <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">
            Upload File 
            <InfoIcon className="h-3 w-3 my-auto mx-1 text-primary-300" />
          </span>
          <div className="flex flex-col border-primary-200 border-[1px] rounded-xl text-center my-1 p-1 cursor-pointer">
            <UploadIcon className="text-primary-200 hover:text-primary-300 m-auto" />
            Choose File 
            <input 
              {...register("file")}
              type="file" 
              className="invisible" 
              onChange={handleFileChange}
            />
          </div>
          {errors.file && <span className="text-red-500 text-sm font-open-sans">{String(errors.file?.message)}</span>}
        </label>

        <button
          type="submit"
          className="border-0 bg-primary-200 text-white font-raleway rounded-md py-2 px-4 text-[18px] w-auto ml-auto"
          data-testid="submit-button"
        >
          Submit Credential
        </button>
      </form>
    </div>
  );
};

type AddKitchenFormProps = {
  handleClose: () => void;
  handleAddKitchen: (kitchen: KitchenSpaceSchema | {}) => void;
};

const AddKitchenForm: React.FC<AddKitchenFormProps> = (props) => {
  var { handleAddKitchen, handleClose } = props;
  const [selectedState, setSelectedState] = useState<string>("");
  const [width, setWidth] = useState<string>("");
  const [length, setLength] = useState<string>("");

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset
  } = useForm<any>({
    resolver: zodResolver(AddKitchenSchema),
    defaultValues: {
      name: "",
      description: "",
      address: "",
      city: "",
      state: "",
      zipcode: "",
      capacity: "",
      image_url: undefined
    }
  });

  const handleStateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedState(event.target.value);
    setValue("state", event.target.value);
  };

  // const handleInputChange = (
  //   event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  // ) => {
  //   const { name, value } = event.target;
  //   setValue(name, value);
  // };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = event.target;
    if (name && files && files.length > 0) {
      let file_data = files[0];
      setValue(name, file_data);
    }
  };

  const handleLengthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLength(event.target.value);
    setValue("length", event.target.value);
  };

  const handleWidthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWidth(event.target.value);
    setValue("width", event.target.value);
  };

  const onSubmit = (data: any) => {
    handleAddKitchen(data);
    reset();
    setSelectedState("");
    setWidth("");
    setLength("");
  };

  return (
    <div className="flex flex-col justify-evenly items-center h-full">
      <span onClick={handleClose} className="ml-auto w-full flex cursor-pointer">
          <Cancel className="text-[#A1A1A1] hover:text-[#777] h-[18px] w-[18px] ml-auto" />
      </span>
      <div className="flex flex-col w-11/12">
        <h1 className="text-[18px] text-black font-raleway font-bold text-left my-2">Add Kitchen</h1>
        <h1 className="text-[16px] font-open-sans text-[#4D4D4D] text-left mb-2">Fill in the details below to add your kitchen.</h1>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="font-open-sans w-11/12 grid grid-flow-row gap-y-5">
        <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">Kitchen Name 
            <InfoIcon className="h-3 w-3 my-auto mx-1 text-primary-300" />
          </span>
          <input
            {...register("name")}
            placeholder="Name"
            className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
          />
          {errors.name && <span className="text-red-500 text-sm font-open-sans">{String(errors.name?.message)}</span>}
        </label>

        <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">
            Description 
          </span>
          <textarea
            {...register("description")}
            placeholder="Description"
            className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
            rows={3}
          />
          {errors.description && <span className="text-red-500 text-sm font-open-sans">{String(errors.description?.message)}</span>}
        </label>

        <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">
            Address 
          </span>
          <input
            {...register("address")}
            placeholder="Address"
            className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
          />
          {errors.address && <span className="text-red-500 text-sm font-open-sans">{String(errors.address?.message)}</span>}
        </label>

        <div className="grid grid-cols-3 gap-2">
          <label className="text-primary-200 text-xs">
            <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">
              City 
            </span>
            <input
              {...register("city")}
              placeholder="City"
              className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
            />
            {errors.city && <span className="text-red-500 text-sm font-open-sans">{String(errors.city?.message)}</span>}
          </label>

          <label className="text-primary-200 text-xs">
            <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">
              State 
            </span>
            <select
              {...register("state")}
              value={selectedState}
              onChange={handleStateChange}
              className="border-[1px] border-[#E9E9E9] text-slate-500 bg-inherit w-full my-1 rounded-lg"
            >
              <option value="">-- State --</option>
              {Object.values(usStates).map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
            {errors.state && <span className="text-red-500 text-sm font-open-sans">{String(errors.state?.message)}</span>}
          </label>

          <label className="text-primary-200 text-xs">
            <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">
              Zip Code 
            </span>
            <input
              {...register("zipcode")}
              placeholder="Zip Code"
              className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
            />
            {errors.zipcode && <span className="text-red-500 text-sm font-open-sans">{String(errors.zipcode?.message)}</span>}
          </label>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <label className="text-primary-200 text-xs">
            <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">
              Capacity 
            </span>
            <input
              {...register("capacity")}
              placeholder="Capacity"
              type="number"
              min="1"
              className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
            />
            {errors.capacity && <span className="text-red-500 text-sm font-open-sans">{String(errors.capacity?.message)}</span>}
          </label>

          <label className="text-primary-200 text-xs">
            <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">
              Length (ft) 
            </span>
            <input
              {...register("length")}
              placeholder="Length"
              type="number"
              min="1"
              value={length}
              onChange={handleLengthChange}
              className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
            />
            {errors.length && <span className="text-red-500 text-sm font-open-sans">{String(errors.length?.message)}</span>}
          </label>

          <label className="text-primary-200 text-xs">
            <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">
              Width (ft) 
            </span>
            <input
              {...register("width")}
              placeholder="Width"
              type="number"
              min="1"
              value={width}
              onChange={handleWidthChange}
              className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
            />
            {errors.width && <span className="text-red-500 text-sm font-open-sans">{String(errors.width?.message)}</span>}
          </label>
        </div>

        <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">
            Kitchen Image 
            <InfoIcon className="h-3 w-3 my-auto mx-1 text-primary-300" />
          </span>
          <div className="flex flex-col border-primary-200 border-[1px] rounded-xl text-center my-1 p-1 cursor-pointer">
            <UploadIcon className="text-primary-200 hover:text-primary-300 m-auto" />
            Choose File 
            <input 
              {...register("image_url")}
              type="file" 
              className="invisible" 
              onChange={handleFileChange}
            />
          </div>
          {errors.image_url && <span className="text-red-500 text-sm font-open-sans">{String(errors.image_url?.message)}</span>}
        </label>

        <button
          type="submit"
          className="border-0 bg-primary-200 text-white font-raleway rounded-md py-2 px-4 text-[18px] w-auto ml-auto"
          data-testid="submit-button"
        >
          Add Kitchen
        </button>
      </form>
    </div>
  );
};



type AddDiscountProps = {
  resource: ResourceSchema
  handleClose: () => void;
  handleAddDiscount: (discount: DiscountSchema) => void;
};

export const AddDiscountForm: React.FC<AddDiscountProps> = (props) => {
  var { resource, handleClose, handleAddDiscount } = props;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset
  } = useForm<any>({
    resolver: zodResolver(AddDiscountSchema),
    defaultValues: {
      name: "",
      rate: 0,
      description: "",
      discount_type: "fixed",
      start_date: "",
      end_date: ""
    }
  });

  const discountType = watch("discount_type");

  const handleInputIncrement = () => {
    const currentRate = watch("rate") || 0;
    const step = discountType === 'percentage' ? 1 : 0.01;
    setValue("rate", currentRate + step);
  };

  const handleInputDecrement = () => {
    const currentRate = watch("rate") || 0;
    const step = discountType === 'percentage' ? 1 : 0.01;
    setValue("rate", Math.max(0, currentRate - step));
  };

  const onSubmit = (data: any) => {
    handleAddDiscount(data);
    reset();
  };


  return (
    <div className="flex flex-col justify-evenly items-center h-full">
      <span onClick={handleClose} className="ml-auto w-full flex cursor-pointer">
          <Cancel className="text-[#A1A1A1] hover:text-[#777] h-[18px] w-[18px] ml-auto" />
      </span>
      <div className="flex flex-col w-11/12">
        <h1 className="text-[18px] text-black font-raleway font-bold text-left my-2">Add {capitalizeString(resource?.resource_type || "resource")} Discount</h1>
        <h1 className="text-[16px] font-open-sans text-[#4D4D4D] text-left mb-2">Fill in the details below to add your resource discount.</h1>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="font-open-sans w-11/12 grid grid-flow-row gap-y-5">
        <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">Discount Name 
            {/* <InfoIcon className="h-3 w-3 my-auto mx-1 text-primary-300" /> */}
          </span>
          <input
            {...register("name")}
            placeholder="Name"
            className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
          />
          {errors.name && <span className="text-red-500 text-sm font-open-sans">{String(errors.name?.message)}</span>}
        </label>

        <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">
            Description 
          </span>
          <input
            {...register("description")}
            placeholder="Description"
            className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
          />
          {errors.description && <span className="text-red-500 text-sm font-open-sans">{String(errors.description?.message)}</span>}
        </label>

        <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">
            Start Date 
          </span>
          <input
            {...register("start_date")}
            type="date"
            className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
          />
          {errors.start_date && <span className="text-red-500 text-sm font-open-sans">{String(errors.start_date?.message)}</span>}
        </label>

        <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">
            End Date 
          </span>
          <input
            {...register("end_date")}
            type="date"
            className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
          />
          {errors.end_date && <span className="text-red-500 text-sm font-open-sans">{String(errors.end_date?.message)}</span>}
        </label>

        <div className="flex flex-row w-full">
          <div className="flex flex-col w-full">
            <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">
              Discount Rate
            </span>
            <div className="flex flex-row items-center mb-2">
              <label className="flex items-center mr-4">
                <input
                  type="radio"
                  value="fixed"
                  checked={discountType === 'fixed'}
                  onChange={() => setValue("discount_type", "fixed")}
                  className="mr-1 border-[1px] border-[#E9E9E9] checked:bg-primary-300 outline-none"
                />
                <span className="text-[#808080] text-[16px] font-open-sans">$</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="percentage"
                  checked={discountType === 'percentage'}
                  onChange={() => setValue("discount_type", "percentage")}
                  className="mr-1 border-[1px] border-[#E9E9E9] checked:bg-primary-300 outline-none"
                />
                <span className="text-[#808080] text-[16px] font-open-sans">%</span>
              </label>
            </div>
            {errors.discount_type && <span className="text-red-500 text-sm font-open-sans">{String(errors.discount_type?.message)}</span>}
            <div className="flex flex-row justify-between w-full">
              <label className="flex flex-row text-xs text-primary-200 border-[1px] border-[#E9E9E9] h-[50px] rounded-lg w-full">
                <span className="flex items-center pl-3 pr-1 text-[#222] text-[16px] font-raleway">
                  {discountType === 'percentage' ? '%' : '$'}
                </span>
                <input
                  {...register("rate", { valueAsNumber: true })}
                  placeholder={discountType === 'percentage' ? '0' : '0.00'}
                  className="border-0 bg-inherit w-full my-1"
                  type="number"
                  step={discountType === 'percentage' ? 1 : 0.01}
                  min={0}
                  max={discountType === 'percentage' ? 100 : 1000000}
                />
                <div
                  className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  onClick={handleInputDecrement}
                >
                  <DownArrow className="text-black rounded-full m-auto" />
                </div>
                <div
                  className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  onClick={handleInputIncrement}
                >
                  <UpArrow className="text-black rounded-full my-auto" />
                </div>
              </label>
            </div>
            {errors.rate && <span className="text-red-500 text-sm font-open-sans">{String(errors.rate?.message)}</span>}
          </div>
        </div>

        

        <button
          type="submit"
          className="border-0 bg-primary-200 text-white font-raleway rounded-md py-2 px-4 text-[18px] w-auto ml-auto"
          data-testid="submit-button"
        >
          Add Discount
        </button>
      </form>
    </div>
  );
};


type AddResourceRuleFormProps = {
  resource: ResourceSchema
  handleClose: () => void;
  handleAddRule: (rule: string) => void;
  // handleDeleteRuleModal: (rule: string) => void;
};

export const AddResourceRuleForm: React.FC<AddResourceRuleFormProps> = (props) => {
  var {  handleClose, handleAddRule } = props;
  const { register, handleSubmit, formState: { errors } } = useForm();
  
  const onSubmit = (data: any) => {
    handleAddRule(data.rule);
  };

  return (
    <div className="flex flex-col justify-evenly items-center h-full">
      <span onClick={handleClose} className="ml-auto w-full flex cursor-pointer">
          <Cancel className="text-[#A1A1A1] hover:text-[#777] h-[18px] w-[18px] ml-auto" />
      </span>

      <div className="flex flex-col w-11/12">
        <h1 className="text-[18px] text-black font-raleway font-bold text-left my-2">Add Rule</h1>
        <h1 className="text-[16px] font-open-sans text-[#4D4D4D] text-left mb-2">Fill in the details below to add your resource rule.</h1>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="font-open-sans w-11/12 grid grid-flow-row gap-y-5">
        <label className="text-primary-200 text-xs">
          <input
            {...register("rule")}
            placeholder="Describe the rule"
            className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
          />
          {errors.rule && <span className="text-red-500 text-sm font-open-sans">{String(errors.rule?.message)}</span>}
        </label>

        <button
          type="submit"
          className="border-0 bg-primary-200 text-white font-raleway rounded-md py-2 px-4 text-[18px] w-auto ml-auto"
          data-testid="submit-button"
        >
          Add Rule
        </button>
      </form>
    </div>
  );
};


type AddFeeProps = {
  resource?: ResourceSchema
  handleClose: () => void;
  handleAddFee: (fee: FeeSchema) => void;
};

export const AddFeeForm: React.FC<AddFeeProps> = (props) => {
  var { resource, handleClose, handleAddFee } = props;
  // const recurrences: PlanRecurrenceOptions[] = ["hourly", "daily", "weekly", "monthly", "annual"];
  // const [selectedRecurrence, setSelectedRecurrence] = useState<string | PlanRecurrenceOptions>("");

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset
  } = useForm<any>({
    resolver: zodResolver(AddFeeSchema),
    defaultValues: {
      name: "",
      rate: 0,
      description: ""
    }
  });

  const rate = watch("rate");

  // const handleRecurrenceChange = async (
  //   event: React.ChangeEvent<HTMLSelectElement>
  // ) => {
  //   const {name, value} = event.target
  //   setSelectedRecurrence(value);
  //   setPlan((prevPlan) => ({
  //     ...prevPlan,
  //     [name]: value,
  //     plan_max_hours: maxHours((resource?.capacity) ? parseInt(resource.capacity) : 1, value)
  //   }));
  // };

  // const handleRecurrenceChange = (recurrence: PlanRecurrenceOptions) => {
  //   setSelectedRecurrence(recurrence);
  //   setFee((prevFee) => ({
  //     ...prevFee,
  //     'recurrence': recurrence,
  //     'max_hours': maxHours((resource?.capacity) ? parseInt(resource.capacity) : 1, recurrence)
  //   }));
  // };

  const handleInputIncrement = (name: keyof any, step: number) => {
    if (name === "rate") {
      const currentValue = Number(rate || 0);
      setValue("rate", roundTo(currentValue + step, 2));
    }
  };

  const handleInputDecrement = (name: keyof any, step: number) => {
    if (name === "rate") {
      const currentValue = Number(rate || 0);
      setValue("rate", currentValue === 0.00 ? 0.00 : roundTo(currentValue - step, 2));
    }
  };

  const onSubmit = (data: any) => {
    handleAddFee(data);
    reset();
  };

  // const capitalizeString = (string: string) => string?.replace(/\w/, c => c?.toUpperCase())

  return (
    <div className="flex flex-col justify-evenly items-center h-full">
      <span onClick={handleClose} className="ml-auto w-full flex cursor-pointer">
          <Cancel className="text-[#A1A1A1] hover:text-[#777] h-[18px] w-[18px] ml-auto" />
      </span>
      <div className="flex flex-col w-11/12">
        <h1 className="text-[18px] text-black font-raleway font-bold text-left my-2">Add {capitalizeString(resource?.resource_type || "resource")} Fee</h1>
        <h1 className="text-[16px] font-open-sans text-[#4D4D4D] text-left mb-2">Fill in the details below to add your resource fee.</h1>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="font-open-sans w-11/12 grid grid-flow-row gap-y-5">
        <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">Fee Name 
            {/* <InfoIcon className="h-3 w-3 my-auto mx-1 text-primary-300" /> */}
          </span>
          <input
            {...register("name")}
            placeholder="Name"
            className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
          />
          {errors.name && <span className="text-red-500 text-sm font-open-sans">{String(errors.name?.message)}</span>}
        </label>

        <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">
            Fee Description 
          </span>
          <input
            {...register("description")}
            placeholder="Description"
            className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
          />
          {errors.description && <span className="text-red-500 text-sm font-open-sans">{String(errors.description?.message)}</span>}
        </label>

        <div className="flex flex-row w-full">
          <div className="flex flex-col">
            <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">
              Fee Rate ($) 
            </span>
            <div className="flex flex-row justify-between w-full">
              <label className="flex flex-row text-xs text-primary-200 border-[1px] border-[#E9E9E9] h-[50px] rounded-lg w-full">
                <input
                  {...register("rate", { valueAsNumber: true })}
                  placeholder="0.00"
                  className="border-0 bg-inherit w-full my-1 "
                  type="number"
                  step={0.01}
                  min={0}
                />

                <div
                  className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  onClick={() => handleInputDecrement('rate', 0.01)}
                >
                  <DownArrow className=" text-black rounded-full  m-auto" />
                </div>
                <div
                  className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  onClick={() => handleInputIncrement('rate', 0.01)}
                >
                    <UpArrow className="text-black rounded-full  my-auto" />
                </div>
              </label>
            </div>
            {errors.rate && <span className="text-red-500 text-sm font-open-sans">{String(errors.rate?.message)}</span>}
          </div>

        </div>

        

        <button
          type="submit"
          className="border-0 bg-primary-200 text-white font-raleway rounded-md py-2 px-4 text-[18px] w-auto ml-auto"
          data-testid="submit-button"
        >
          Add Fee
        </button>
      </form>
    </div>
  );
};

type AddPlanProps = {
  communityId: string;
  resource?: ResourceSchema
  isDuplicate: {existingPlan: PlanSchema, checked: boolean}
  handleClose: () => void;
  handleAddPlan: (plan: PlanSchema) => void;
};

export const AddPlanForm = memo<AddPlanProps>((props) => {
  var { communityId, resource, isDuplicate, handleClose, handleAddPlan } = props;
  const recurrences: PlanRecurrenceOptions[] = ["hourly", "daily", "weekly", "monthly", "annual"];
  const [selectedRecurrence, setSelectedRecurrence] = useState<string | PlanRecurrenceOptions>(
    isDuplicate?.existingPlan?.plan_recurrence || "hourly"
  );

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    control,
    formState: { errors },
    reset
  } = useForm<any>({
    resolver: zodResolver(AddPlanSchema),
            defaultValues: {
          plan_description: isDuplicate?.existingPlan?.plan_description || "",
          plan_name: isDuplicate?.existingPlan?.plan_name || "",
          plan_rate: isDuplicate?.existingPlan?.plan_rate || 0,
          plan_recurrence: isDuplicate?.existingPlan?.plan_recurrence || "hourly",
          plan_max_hours: isDuplicate?.existingPlan?.plan_max_hours || 0,
          pricing_type: isDuplicate?.existingPlan?.pricing_type || "fixed",
          features: isDuplicate?.existingPlan?.features || [],
          plan_type: allowsTierResourceFeatures(isDuplicate?.existingPlan?.plan_recurrence)
            ? (isDuplicate?.existingPlan?.plan_type || "membership")
            : "membership",
          is_fulltime_hours: isDuplicate?.existingPlan?.is_fulltime_hours ?? false,
          associated_resources: allowsTierResourceFeatures(isDuplicate?.existingPlan?.plan_recurrence)
            ? (isDuplicate?.existingPlan?.associated_resources || [])
            : []
        }
  });


    // // Reset form with existingPlan data when it changes
    useEffect(() => {
      const existingPlan = isDuplicate?.existingPlan
      // const derivedResourceType = existingPlan?.resource_type
      if (existingPlan) {
        const recurrence = existingPlan?.plan_recurrence || "hourly";
        const canLinkResources = allowsTierResourceFeatures(recurrence);
        const associations = canLinkResources ? (existingPlan?.associated_resources || []) : [];

        featuresRef.current = existingPlan?.features || [];
        associatedResourcesRef.current = associations;
        setSelectedRecurrence(recurrence);
        reset({
          plan_description: existingPlan?.plan_description || "",
          plan_name: existingPlan?.plan_name || "",
          plan_rate: existingPlan?.plan_rate || 0,
          plan_recurrence: recurrence,
          plan_max_hours: existingPlan?.plan_max_hours || 0,
          pricing_type: existingPlan?.pricing_type || "fixed",
          // resource_type: derivedResourceType || "kitchen",
          // overage_rate: existingPlan?.overage_rate || 0,
          // peak_rate: existingPlan?.peak_rate || 0,
          // off_peak_rate: existingPlan?.off_peak_rate || 0,
          features: existingPlan?.features || [],
          plan_type: canLinkResources
            ? (existingPlan?.plan_type || resolveMembershipPlanType(associations))
            : "membership",
          is_fulltime_hours: existingPlan?.is_fulltime_hours ?? false,
          associated_resources: associations
        });
      }
    }, [isDuplicate, reset]);

  const associatedResourcesRef = useRef<PlanResourceAssociation[]>(
    allowsTierResourceFeatures(isDuplicate?.existingPlan?.plan_recurrence)
      ? (isDuplicate?.existingPlan?.associated_resources || [])
      : []
  );
  const featuresRef = useRef<string[]>(isDuplicate?.existingPlan?.features || []);
  const showTierResourceFeatures = allowsTierResourceFeatures(selectedRecurrence as PlanRecurrenceOptions);
  const planMaxHours = watch("plan_max_hours");
  const handleAssociationsChange = useCallback(
    (associations: PlanResourceAssociation[]) => {
      syncMembershipAssociationsToForm(setValue, associations);
    },
    [setValue]
  );
  const associationSyncKey = isDuplicate.checked
    ? (isDuplicate.existingPlan?.plan_id ?? "duplicate")
    : "add";

  useEffect(() => {
    const associations = isDuplicate?.existingPlan?.associated_resources || [];
    const recurrence = isDuplicate?.existingPlan?.plan_recurrence;
    associatedResourcesRef.current = allowsTierResourceFeatures(recurrence)
      ? associations
      : [];
  }, [isDuplicate?.existingPlan?.associated_resources, isDuplicate?.existingPlan?.plan_recurrence]);

  const handleRecurrenceChange = (recurrence: PlanRecurrenceOptions) => {
    setSelectedRecurrence(recurrence);
    setValue("plan_recurrence", recurrence);
    const calculatedMaxHours = maxHours((resource?.resource_options?.floor_plan?.max_capacity) ? Number(resource.resource_options?.floor_plan?.max_capacity) : 1, recurrence);
    // Allow 0 for max hours as per validation requirements
    setValue("plan_max_hours", Math.max(0, calculatedMaxHours));
    if (!allowsTierResourceFeatures(recurrence)) {
      dissociateTierResources(setValue, associatedResourcesRef);
    }
  };


  const handleInputIncrement = (
    name: keyof any,
    step: number
  ) => {
    if (name === "plan_rate") {
      const currentValue = Number(getValues("plan_rate") || 0);
      setValue("plan_rate", roundTo(currentValue + step, 2));
    } else if (name === "plan_max_hours") {
      const currentValue = Number(getValues("plan_max_hours") || 0);
      setValue("plan_max_hours", currentValue + step);
    }
  };

  const handleInputDecrement = (
    name: keyof any,
    step: number
  ) => {
    if (name === "plan_rate") {
      const currentValue = Number(getValues("plan_rate") || 0);
      // Allow rate to go down to 0
      setValue("plan_rate", Math.max(0, roundTo(currentValue - step, 2)));
    } else if (name === "plan_max_hours") {
      const currentValue = Number(getValues("plan_max_hours") || 0);
      // Allow max hours to go down to 0
      setValue("plan_max_hours", Math.max(0, currentValue - step));
    }
  };

  const onSubmit = (data: PlanSchema) => {
    applyMembershipPlanAssociations(data, data.plan_recurrence || selectedRecurrence, associatedResourcesRef);
    data.features = featuresRef.current;
    handleAddPlan(data);
    reset();
    setSelectedRecurrence("hourly");
    associatedResourcesRef.current = [];
  };

  


  // const capitalizeString = (string: string) => string?.replace(/\w/, c => c?.toUpperCase())

  return (
    <div className="flex flex-col justify-evenly items-center h-full">
      <span onClick={handleClose} className="ml-auto w-full flex cursor-pointer">
          <Cancel className="text-[#A1A1A1] hover:text-[#777] h-[18px] w-[18px] ml-auto" />
      </span>
      <div className="flex flex-col w-11/12">
        <h1 className="text-[18px] text-black font-raleway font-bold text-left my-2">{isDuplicate.checked ? 'Duplicate' : 'Add'} Membership Tier</h1>
        <h1 className="text-[16px] font-open-sans text-[#4D4D4D] text-left mb-2">Fill in the details below to add your community membership tier.</h1>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="font-open-sans w-11/12 grid grid-flow-row gap-y-5">
        <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">Tier Name 
            {/* <InfoIcon className="h-3 w-3 my-auto mx-1 text-primary-300" /> */}
          </span>
          <input
            {...register("plan_name")}
            placeholder="Name"
            className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
          />
          {errors.plan_name && <span className="text-red-500 text-sm font-open-sans">{String(errors.plan_name?.message)}</span>}
        </label>

        <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">
            Tier Description 
          </span>
          <input
            {...register("plan_description")}
            placeholder="Description"
            className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
          />
          {errors.plan_description && <span className="text-red-500 text-sm font-open-sans">{String(errors.plan_description?.message)}</span>}
        </label>

        <label className="flex flex-col text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">
            Tier Period
            {/* <InfoIcon className="h-3 w-3 my-auto mx-1 text-primary-300 cursor-pointer" /> */}
          </span>

          <span className="flex flex-row my-1 w-full">
            {
              Object.values(recurrences).map((recurrence) => (
                (selectedRecurrence===recurrence) ? (
                <span 
                  key={recurrence} 
                  className={`flex flex-row text-white font-open-sans text-[16px] mr-2 bg-primary-200 border-0 p-2 rounded-md w-auto cursor-pointer`}
                >
                  <RadioButtonChecked className="text-white my-auto" />
                  <p className="my-auto mx-1">{capitalizeString(recurrence)}</p>
                </span>
                ) : (
                  <span 
                    key={recurrence} 
                    className={`flex flex-row text-black font-open-sans text-[16px] mr-3 border-[1px] border-[#E9E9E9] p-2 rounded-md w-auto cursor-pointer`}
                    onClick={()=>handleRecurrenceChange(recurrence)}
                  >
                    <RadioButtonUnchecked className="text-[#CFCFCF] my-auto" />
                    <p className="my-auto mx-1">{capitalizeString(recurrence)}</p>
                  </span>
                )
              ))
            }
          </span>
          {errors.plan_recurrence && <span className="text-red-500 text-sm font-open-sans">{String(errors.plan_recurrence?.message)}</span>}

          
          {/* <select
            name="plan_recurrence"
            value={selectedRecurrence}
            onChange={handleRecurrenceChange}
            className="border-[1px] border-[#E9E9E9] text-slate-500 bg-inherit w-full my-1 rounded-lg"
          >
            <option value="">-- Recurrence --</option>
            {Object.values(recurrences).map((recurrence) => (
              <option key={recurrence} value={recurrence}>
                {capitalizeString(recurrence)}
              </option>
            ))}
          </select> */}
        </label>



        <div className="grid grid-flow-row grid-cols-3 space-x-2">
          <div className="flex flex-col">
            <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">
              Tier Rate ($) 
            </span>
            <div className="flex flex-row justify-between">
              <label className="flex flex-row text-primary-200 border-[1px] border-[#E9E9E9] h-[50px] rounded-lg">
                <input
                  {...register("plan_rate", { valueAsNumber: true })}
                  placeholder="0"
                  className="border-0 bg-inherit w-full my-1 "
                  type="number"
                  step={0.01}
                  min={0}
                />

                <div
                  className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  onClick={() => handleInputDecrement('plan_rate', 0.01)}
                >
                  <DownArrow className=" text-black rounded-full  m-auto" />
                </div>
                <div
                  className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  onClick={() => handleInputIncrement('plan_rate', 0.01)}
                >
                    <UpArrow className="text-black rounded-full  my-auto" />
                </div>
              </label>
            </div>
            {errors.plan_rate && <span className="text-red-500 text-sm font-open-sans">{String(errors.plan_rate?.message)}</span>}
          </div>

          {/* <div className="flex flex-col">
            <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">
              Max Hours 
            </span>
            <div className="flex flex-row justify-between">
              <label className="flex flex-row text-primary-200 border-[1px] border-[#E9E9E9] h-[50px] rounded-lg">
                <input
                  {...register("plan_max_hours", { valueAsNumber: true })}
                  placeholder="0"
                  className="border-0 bg-inherit w-full my-1 "
                  type="number"
                  step={1}
                  min={0}
                />

                <div
                  className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  onClick={() => handleInputDecrement('plan_max_hours', 1)}
                >
                  <DownArrow className=" text-black rounded-full  m-auto" />
                </div>
                <div
                  className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  onClick={() => handleInputIncrement('plan_max_hours', 1)}
                >
                    <UpArrow className="text-black rounded-full  my-auto" />
                </div>
              </label>
            </div>
            {errors.plan_max_hours && <span className="text-red-500 text-sm font-open-sans">{String(errors.plan_max_hours?.message)}</span>}
          </div> */}
        </div>

        {/* Tier Features: perks, booking access, linked resources & inventory */}
        <div className="flex flex-col gap-y-3 py-2">
          <div className="flex flex-col">
            <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway">
              Tier Features
            </span>
            <p className="text-[13px] text-[#808080] font-normal font-open-sans mt-1">
              Everything included with this tier{" "}
              <span className="font-light text-xs text-[#808080]/80">
                (perks like priority booking, 24/7 access, or included resources and inventory items)
              </span>
            </p>
          </div>

          <div className="flex flex-col gap-y-5 border-[1px] border-[#E9E9E9] rounded-lg p-4">
            <PlanTierFeaturesField
              featuresRef={featuresRef}
              initialFeatures={isDuplicate?.existingPlan?.features ?? EMPTY_PLAN_FEATURES}
            />

            <PlanFulltimeHoursField control={control} setValue={setValue} />

            {showTierResourceFeatures && (
              <PlanTierResourceFeaturesField
                communityId={communityId}
                planRecurrence={selectedRecurrence as PlanRecurrenceOptions}
                associationsRef={associatedResourcesRef}
                initialAssociatedResources={associatedResourcesRef.current}
                associationSyncKey={associationSyncKey}
                planMaxHours={Number(planMaxHours) || 0}
                onAssociationsChange={handleAssociationsChange}
              />
            )}
          </div>
        </div>

        <button
          type="submit"
          className="font-medium border-0 bg-primary-200 text-white font-raleway rounded-md py-2 px-4 text-[18px] w-full ml-auto"
          data-testid="submit-button"
        >
          Add Membership Tier
        </button>
      </form>
    </div>
  );
});
AddPlanForm.displayName = "AddPlanForm";



type EditPlanProps = {
  communityId: string;
  existingPlan: PlanSchema
  resource?: ResourceSchema
  handleClose: () => void;
  handleEditPlan: (planId: string, plan: PlanSchema) => void;
};

export const EditPlanForm: React.FC<EditPlanProps> = (props) => {
  var { communityId, existingPlan, resource, handleClose, handleEditPlan } = props;
  const planId = existingPlan?.plan_id;
  const recurrences: PlanRecurrenceOptions[] = ["hourly", "daily", "weekly", "monthly", "annual"];
  const [selectedRecurrence, setSelectedRecurrence] = useState<string | PlanRecurrenceOptions>(
    existingPlan?.plan_recurrence || "hourly"
  );

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    control,
    formState: { errors },
    reset
  } = useForm<any>({
    resolver: zodResolver(AddPlanSchema),
            defaultValues: {
          plan_description: existingPlan?.plan_description || "",
          plan_name: existingPlan?.plan_name || "",
          plan_rate: existingPlan?.plan_rate || 0,
          plan_recurrence: existingPlan?.plan_recurrence || "hourly",
          plan_max_hours: existingPlan?.plan_max_hours || 0,
          pricing_type: existingPlan?.pricing_type || "fixed",
          features: existingPlan?.features || [],
          plan_type: allowsTierResourceFeatures(existingPlan?.plan_recurrence)
            ? (existingPlan?.plan_type || "membership")
            : "membership",
          is_fulltime_hours: existingPlan?.is_fulltime_hours ?? false,
          associated_resources: allowsTierResourceFeatures(existingPlan?.plan_recurrence)
            ? (existingPlan?.associated_resources || [])
            : []
        }
  });


    const associatedResourcesRef = useRef<PlanResourceAssociation[]>(
      allowsTierResourceFeatures(existingPlan?.plan_recurrence)
        ? (existingPlan?.associated_resources || [])
        : []
    );
    const featuresRef = useRef<string[]>(existingPlan?.features || []);
    const showTierResourceFeatures = allowsTierResourceFeatures(selectedRecurrence as PlanRecurrenceOptions);
    const planMaxHours = watch("plan_max_hours");
    const handleAssociationsChange = useCallback(
      (associations: PlanResourceAssociation[]) => {
        syncMembershipAssociationsToForm(setValue, associations);
      },
      [setValue]
    );
    const associationSyncKey = existingPlan?.plan_id ?? "edit";
    const loadedEditPlanIdRef = useRef<string | null>(null);
    const loadedAssociationSignatureRef = useRef<string>("");
    const [associationsFieldKey, setAssociationsFieldKey] = useState(0);

    const getAssociationsSignature = (associations: PlanResourceAssociation[]) =>
      associations.map((association) => association.resource_id ?? "").join("|");

    // Load plan data once per open; re-init when linked resources change on the same tier.
    useEffect(() => {
      if (!existingPlan?.plan_id) {
        loadedEditPlanIdRef.current = null;
        loadedAssociationSignatureRef.current = "";
        return;
      }

      const recurrence = existingPlan?.plan_recurrence || "hourly";
      const canLinkResources = allowsTierResourceFeatures(recurrence);
      const associations = canLinkResources ? (existingPlan?.associated_resources || []) : [];
      const associationSignature = getAssociationsSignature(associations);

      if (
        loadedEditPlanIdRef.current === existingPlan.plan_id &&
        loadedAssociationSignatureRef.current === associationSignature
      ) {
        return;
      }

      loadedEditPlanIdRef.current = existingPlan.plan_id;
      loadedAssociationSignatureRef.current = associationSignature;

      associatedResourcesRef.current = associations;
      featuresRef.current = existingPlan?.features || [];
      setSelectedRecurrence(recurrence);
      reset({
          plan_description: existingPlan?.plan_description || "",
          plan_name: existingPlan?.plan_name || "",
          plan_rate: existingPlan?.plan_rate || 0,
          plan_recurrence: recurrence,
          plan_max_hours: existingPlan?.plan_max_hours || 0,
          pricing_type: existingPlan?.pricing_type || "fixed",
          features: existingPlan?.features || [],
          plan_type: canLinkResources
            ? (existingPlan?.plan_type || resolveMembershipPlanType(associations))
            : "membership",
          is_fulltime_hours: existingPlan?.is_fulltime_hours ?? false,
          associated_resources: associations
        });
      setAssociationsFieldKey((key) => key + 1);
    }, [existingPlan, reset]);

  const handleRecurrenceChange = (recurrence: PlanRecurrenceOptions) => {
    setSelectedRecurrence(recurrence);
    setValue("plan_recurrence", recurrence);
    const calculatedMaxHours = maxHours((resource?.resource_options?.floor_plan?.max_capacity) ? Number(resource.resource_options?.floor_plan?.max_capacity) : 1, recurrence);
    // Allow 0 for max hours as per validation requirements
    setValue("plan_max_hours", Math.max(0, calculatedMaxHours));
    if (!allowsTierResourceFeatures(recurrence)) {
      dissociateTierResources(setValue, associatedResourcesRef);
    }
  };


  const handleInputIncrement = (
    name: keyof any,
    step: number
  ) => {
    if (name === "plan_rate") {
      const currentValue = Number(getValues("plan_rate") || 0);
      setValue("plan_rate", roundTo(currentValue + step, 2));
    } else if (name === "plan_max_hours") {
      const currentValue = Number(getValues("plan_max_hours") || 0);
      setValue("plan_max_hours", currentValue + step);
    }
  };

  const handleInputDecrement = (
    name: keyof any,
    step: number
  ) => {
    if (name === "plan_rate") {
      const currentValue = Number(getValues("plan_rate") || 0);
      // Allow rate to go down to 0
      setValue("plan_rate", Math.max(0, roundTo(currentValue - step, 2)));
    } else if (name === "plan_max_hours") {
      const currentValue = Number(getValues("plan_max_hours") || 0);
      // Allow max hours to go down to 0
      setValue("plan_max_hours", Math.max(0, currentValue - step));
    }
  };

  const onSubmit = (data: any) => {
    const recurrence = data.plan_recurrence || selectedRecurrence;
    applyMembershipPlanAssociations(data, recurrence, associatedResourcesRef);
    data.features = featuresRef.current;

    const changedAttributes = getChangedAttributes(existingPlan, data) as PlanSchema;
    if (allowsTierResourceFeatures(recurrence as PlanRecurrenceOptions)) {
      changedAttributes.plan_type = data.plan_type;
      changedAttributes.associated_resources = data.associated_resources ?? [];
    }

    handleEditPlan(planId as string, changedAttributes);
    reset();
    setSelectedRecurrence("hourly");
    associatedResourcesRef.current = [];
  };

  


  // const capitalizeString = (string: string) => string?.replace(/\w/, c => c?.toUpperCase())

  return (
    <div className="flex flex-col justify-evenly items-center h-full">
      <span onClick={handleClose} className="ml-auto w-full flex cursor-pointer">
          <Cancel className="text-[#A1A1A1] hover:text-[#777] h-[18px] w-[18px] ml-auto" />
      </span>
      <div className="flex flex-col w-11/12">
        <h1 className="text-[18px] text-black font-raleway font-bold text-left my-2">Edit Membership Tier</h1>
        <h1 className="text-[16px] font-open-sans text-[#4D4D4D] text-left mb-2">Fill in the details below to edit your community membership tier.</h1>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="font-open-sans w-11/12 grid grid-flow-row gap-y-5">
        <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">Tier Name 
            {/* <InfoIcon className="h-3 w-3 my-auto mx-1 text-primary-300" /> */}
          </span>
          <input
            {...register("plan_name")}
            placeholder="Name"
            className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
          />
          {errors.plan_name && <span className="text-red-500 text-sm font-open-sans">{String(errors.plan_name?.message)}</span>}
        </label>

        <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">
            Tier Description 
          </span>
          <input
            {...register("plan_description")}
            placeholder="Description"
            className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
          />
          {errors.plan_description && <span className="text-red-500 text-sm font-open-sans">{String(errors.plan_description?.message)}</span>}
        </label>

        <label className="flex flex-col text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">
            Tier Period
            {/* <InfoIcon className="h-3 w-3 my-auto mx-1 text-primary-300 cursor-pointer" /> */}
          </span>

          <span className="flex flex-row my-1 w-full">
            {
              Object.values(recurrences).map((recurrence) => (
                (selectedRecurrence===recurrence) ? (
                <span 
                  key={recurrence} 
                  className={`flex flex-row text-white font-open-sans text-[16px] mr-2 bg-primary-200 border-0 p-2 rounded-md w-auto cursor-pointer`}
                >
                  <RadioButtonChecked className="text-white my-auto" />
                  <p className="my-auto mx-1">{capitalizeString(recurrence)}</p>
                </span>
                ) : (
                  <span 
                    key={recurrence} 
                    className={`flex flex-row text-black font-open-sans text-[16px] mr-3 border-[1px] border-[#E9E9E9] p-2 rounded-md w-auto cursor-pointer`}
                    onClick={()=>handleRecurrenceChange(recurrence)}
                  >
                    <RadioButtonUnchecked className="text-[#CFCFCF] my-auto" />
                    <p className="my-auto mx-1">{capitalizeString(recurrence)}</p>
                  </span>
                )
              ))
            }
          </span>
          {errors.plan_recurrence && <span className="text-red-500 text-sm font-open-sans">{String(errors.plan_recurrence?.message)}</span>}

          
          {/* <select
            name="plan_recurrence"
            value={selectedRecurrence}
            onChange={handleRecurrenceChange}
            className="border-[1px] border-[#E9E9E9] text-slate-500 bg-inherit w-full my-1 rounded-lg"
          >
            <option value="">-- Recurrence --</option>
            {Object.values(recurrences).map((recurrence) => (
              <option key={recurrence} value={recurrence}>
                {capitalizeString(recurrence)}
              </option>
            ))}
          </select> */}
        </label>



        <div className="grid grid-flow-row grid-cols-3 space-x-2">
          <div className="flex flex-col">
            <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">
              Tier Rate ($) 
            </span>
            <div className="flex flex-row justify-between">
              <label className="flex flex-row text-primary-200 border-[1px] border-[#E9E9E9] h-[50px] rounded-lg">
                <input
                  {...register("plan_rate", { valueAsNumber: true })}
                  placeholder="0"
                  className="border-0 bg-inherit w-full my-1 "
                  type="number"
                  step={0.01}
                  min={0}
                />

                <div
                  className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  onClick={() => handleInputDecrement('plan_rate', 0.01)}
                >
                  <DownArrow className=" text-black rounded-full  m-auto" />
                </div>
                <div
                  className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  onClick={() => handleInputIncrement('plan_rate', 0.01)}
                >
                    <UpArrow className="text-black rounded-full  my-auto" />
                </div>
              </label>
            </div>
            {errors.plan_rate && <span className="text-red-500 text-sm font-open-sans">{String(errors.plan_rate?.message)}</span>}
          </div>

          {/* <div className="flex flex-col">
            <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">
              Max Hours 
            </span>
            <div className="flex flex-row justify-between">
              <label className="flex flex-row text-primary-200 border-[1px] border-[#E9E9E9] h-[50px] rounded-lg">
                <input
                  {...register("plan_max_hours", { valueAsNumber: true })}
                  placeholder="0"
                  className="border-0 bg-inherit w-full my-1 "
                  type="number"
                  step={1}
                  min={0}
                />

                <div
                  className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  onClick={() => handleInputDecrement('plan_max_hours', 1)}
                >
                  <DownArrow className=" text-black rounded-full  m-auto" />
                </div>
                <div
                  className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  onClick={() => handleInputIncrement('plan_max_hours', 1)}
                >
                    <UpArrow className="text-black rounded-full  my-auto" />
                </div>
              </label>
            </div>
            {errors.plan_max_hours && <span className="text-red-500 text-sm font-open-sans">{String(errors.plan_max_hours?.message)}</span>}
          </div> */}
        </div>

        {/* Tier Features: perks, booking access, linked resources & inventory */}
        <div className="flex flex-col gap-y-3 py-2">
          <div className="flex flex-col">
            <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway">
              Tier Features
            </span>
            <p className="text-[13px] text-[#808080] font-normal font-open-sans mt-1">
              Everything included with this tier{" "}
              <span className="font-light text-xs text-[#808080]/80">
                (perks like priority booking, 24/7 access, or included resources and inventory items)
              </span>
            </p>
          </div>
          <div className="flex flex-col gap-y-5 border-[1px] border-[#E9E9E9] rounded-lg p-4">
            
            {/* Tier Features: perks */}
            <PlanTierFeaturesField
              featuresRef={featuresRef}
              initialFeatures={existingPlan?.features ?? EMPTY_PLAN_FEATURES}
            />
            <PlanFulltimeHoursField control={control} setValue={setValue} />

            {showTierResourceFeatures && (
              <PlanTierResourceFeaturesField
                key={associationsFieldKey}
                communityId={communityId}
                planRecurrence={selectedRecurrence as PlanRecurrenceOptions}
                associationsRef={associatedResourcesRef}
                initialAssociatedResources={associatedResourcesRef.current}
                associationSyncKey={`${associationSyncKey}-${associationsFieldKey}`}
                planMaxHours={Number(planMaxHours) || 0}
                onAssociationsChange={handleAssociationsChange}
              />
            )}
          </div>
        </div>

        <button
          type="submit"
          className="font-medium border-0 bg-primary-200 text-white font-raleway rounded-md py-2 px-4 text-[18px] w-full ml-auto"
          data-testid="submit-button"
        >
          Update Tier
        </button>
      </form>
    </div>
  );
};



type EditResourcePlanFormProps = {
  resource?: ResourceSchema
  existingPlan: ResourcePlanSchema
  handleClose: () => void;
  handleEditPlan: (planId: string, plan: ResourcePlanSchema) => void;
  resourceType?: ResourceType;
};

export const EditResourcePlanForm: React.FC<EditResourcePlanFormProps> = (props) => {
  var { resource, resourceType, existingPlan, handleClose, handleEditPlan } = props;
  const planId = existingPlan?.plan_id;
  const derivedResourceType = (resourceType ?? ((resource as any)?.resource_type)) as ResourceType | undefined;
  /** Funding offers price a recurring access fee, so slot-booking controls do not apply. */
  const isFundingPlan = derivedResourceType === "funding";
  const recurrences: PlanRecurrenceOptions[] = isFundingPlan
    ? ["monthly", "annual"]
    : ["hourly", "daily", "weekly", "monthly", "annual"];
  const [selectedRecurrence, setSelectedRecurrence] = useState<string | PlanRecurrenceOptions>(
    isFundingPlan ? "monthly" : "hourly"
  );
  const [hasMaxHours, setHasMaxHours] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    // getValues,
    control,
    formState: { errors },
    reset
  } = useForm<any>({
    resolver: zodResolver(EditPlanSchema),
            defaultValues: {
          plan_description: existingPlan?.plan_description || "",
          plan_name: existingPlan?.plan_name || "",
          plan_rate: existingPlan?.plan_rate || 0,
          plan_recurrence: existingPlan?.plan_recurrence || "hourly",
          plan_max_hours: existingPlan?.plan_max_hours || 0,
          pricing_type: existingPlan?.pricing_type || "fixed",
          resource_type: derivedResourceType || "kitchen",
          plan_type: existingPlan?.plan_type || "resource",
          is_fulltime_hours: existingPlan?.is_fulltime_hours ?? false,
          overage_rate: existingPlan?.overage_rate || 0,
          peak_rate: existingPlan?.peak_rate || 0,
          off_peak_rate: existingPlan?.off_peak_rate || 0,
          features: existingPlan?.features || []
        }
  });

  const planRate = watch("plan_rate");
  const planMaxHours = watch("plan_max_hours");
  const pricingType = watch("pricing_type");
  const [displayMaxDaysOrHours, setDisplayMaxDaysOrHours] = useState<number>(0);
  const [includeAssociatedResources, setIncludeAssociatedResources] = useState(
    () => resourcePlanIncludesAssociatedResources(existingPlan)
  );
  const featuresRef = useRef<string[]>(existingPlan?.features || []);
  const associatedResourcesRef = useRef<PlanResourceAssociation[]>(
    existingPlan?.associated_resources || []
  );

  useEffect(() => {
    if (!existingPlan) {
      return;
    }

    featuresRef.current = existingPlan.features || [];
    associatedResourcesRef.current = existingPlan.associated_resources || [];
    setIncludeAssociatedResources(resourcePlanIncludesAssociatedResources(existingPlan));
  }, [existingPlan]);

  const handleIncludeAssociatedResourcesChange = useCallback((included: boolean) => {
    setIncludeAssociatedResources(included);
    if (!included) {
      clearResourcePlanAssociatedResources(setValue, associatedResourcesRef);
    }
  }, [setValue]);

  // Stable input for the inventory field, sourced from the plan itself rather than the
  // mutable output ref, so the field's own writes never feed back and clear the selection.
  const initialResourceAssociations = useMemo<PlanResourceAssociation[]>(
    () => existingPlan?.associated_resources ?? [],
    [existingPlan]
  );

  // Ensure selectedRecurrence state is synchronized with form value
  const currentRecurrence = watch("plan_recurrence");
  useEffect(() => {
    if (currentRecurrence && currentRecurrence !== selectedRecurrence) {
      setSelectedRecurrence(currentRecurrence);
    }

    setHasMaxHours(!isFundingPlan && existingPlan?.plan_max_hours ? true : false);
  }, [currentRecurrence, selectedRecurrence, existingPlan, isFundingPlan, watch]);

  // Sync display value when underlying hours or recurrence changes
  useEffect(() => {
    if (["weekly", "monthly", "annual"].includes(currentRecurrence)) {
      setDisplayMaxDaysOrHours(convertHoursToDays(Number(planMaxHours || 0), currentRecurrence));
    } else {
      setDisplayMaxDaysOrHours(Number(planMaxHours || 0));
    }
  }, [planMaxHours, currentRecurrence]);

  // Reset form with existingPlan data when it changes
  useEffect(() => {
    if (existingPlan) {
      reset({
        plan_description: existingPlan?.plan_description || "",
        plan_name: existingPlan?.plan_name || "",
        plan_rate: existingPlan?.plan_rate || 0,
        plan_recurrence: existingPlan?.plan_recurrence || "hourly",
        plan_max_hours: existingPlan?.plan_max_hours || 0,
        pricing_type: existingPlan?.pricing_type || "fixed",
        resource_type: derivedResourceType || "kitchen",
        plan_type: existingPlan?.plan_type || "resource",
        is_fulltime_hours: existingPlan?.is_fulltime_hours ?? false,
        overage_rate: existingPlan?.overage_rate || 0,
        peak_rate: existingPlan?.peak_rate || 0,
        off_peak_rate: existingPlan?.off_peak_rate || 0,
        features: existingPlan?.features || []
      });
    }
  }, [existingPlan, derivedResourceType, reset]);

  // Clear max hours fields when hasMaxHours is false
  useEffect(() => {
    if (!hasMaxHours) {
      setValue('plan_max_hours', 0);
      setValue('overage_rate', 0);
    }
  }, [hasMaxHours, setValue]);

  // Helper function to convert days to hours based on recurrence
  const convertDaysToHours = (days: number, recurrence: string): number => {
    // For weekly/monthly/annual, user inputs days. Store as hours.
    switch (recurrence) {
      case 'weekly':
      case 'monthly':
      case 'annual':
        return days * 24;
      default:
        return days; // For hourly and daily, value is already hours
    }
  };

  // Helper function to convert hours to days based on recurrence
  const convertHoursToDays = (hours: number, recurrence: string): number => {
    // For weekly/monthly/annual, display days to the user.
    switch (recurrence) {
      case 'weekly':
      case 'monthly':
      case 'annual':
        return Math.floor((hours || 0) / 24);
      default:
        return hours; // For hourly and daily, display hours
    }
  };

  // const handleRecurrenceChange = async (
  //   event: React.ChangeEvent<HTMLSelectElement>
  // ) => {
  //   const {name, value} = event.target
  //   setSelectedRecurrence(value);
  //   setPlan((prevPlan) => ({
  //     ...prevPlan,
  //     [name]: value,
  //     plan_max_hours: maxHours((resource?.capacity) ? parseInt(resource.capacity) : 1, value)
  //   }));
  // };

  const handleRecurrenceChange = (recurrence: PlanRecurrenceOptions) => {
    setSelectedRecurrence(recurrence);
    setValue("plan_recurrence", recurrence);
    if (isFundingPlan) {
      setValue("plan_max_hours", 0);
      return;
    }
    const calculatedMaxHours = maxHours((resource?.resource_options?.floor_plan?.max_capacity) ? Number(resource.resource_options?.floor_plan?.max_capacity) : 1, recurrence);
    // Allow 0 for max hours as per validation requirements
    setValue("plan_max_hours", Math.max(0, calculatedMaxHours));
  };

  // Keep standard_rate in sync with plan_rate for both pricing types
  useEffect(() => {
    setValue('plan_rate', Number(planRate || 0));
  }, [planRate, setValue]);

  const handleInputIncrement = (
    name: keyof any,
    step: number
  ) => {
    if (name === "plan_rate") {
      const currentValue = Number(planRate || 0);
      setValue("plan_rate", roundTo(currentValue + step, 2));
    } else if (name === "plan_max_hours") {
      const currentValue = Number(planMaxHours || 0);
      setValue("plan_max_hours", currentValue + step);
    } 
    // else if (name ===  "peak_rate" || name ===  "off_peak_rate"){
    //   const currentValue = Number(watch(name) || 0);
    //   setValue(name, currentValue + step);
    // } else if (name ===  "overage_rate"){
    //   const currentValue = Number(watch(name) || 0);
    //   setValue(name, currentValue + step);
    // } 
    else if (name ===  "peak_rate" || name ===  "off_peak_rate" || name === "overage_rate"){
      const currentValue = Number(watch(name) || 0);
      if (currentValue >= 0.00 && step > 0.00) {
        setValue(name, (currentValue + step).toFixed(2));
      }
    }
  };

  const handleInputDecrement = (
    name: keyof any,
    step: number
  ) => {
    if (name === "plan_rate") {
      const currentValue = Number(planRate || 0);
      // Allow rate to go down to 0
      setValue("plan_rate", Math.max(0, roundTo(currentValue - step, 2)));
    } else if (name === "plan_max_hours") {
      const currentValue = Number(planMaxHours || 0);
      // Allow max hours to go down to 0
      setValue("plan_max_hours", Math.max(0, currentValue - step));
    } else if (name ===  "peak_rate" || name ===  "off_peak_rate" || name === "overage_rate"){
      const currentValue = Number(watch(name) || 0);
      if (currentValue > 0.00 && currentValue - step >= 0.00) {
        setValue(name, (currentValue - step).toFixed(2));
      }
    }
  };

  const onSubmit = (data: ResourcePlanSchema) => {
    if (!data.resource_type && derivedResourceType) {
      data.resource_type = derivedResourceType;
    }
    if (data.plan_recurrence === 'monthly' || data.plan_recurrence === 'annual') {
      data.pricing_type = "fixed";
    }
    if (data.pricing_type === 'fixed') {
      data.plan_rate = Number(data.plan_rate || 0);
      data.peak_rate = undefined;
      data.off_peak_rate = undefined;
    }

    applyResourcePlanAssociations(
      data,
      resource,
      associatedResourcesRef,
      featuresRef,
      includeAssociatedResources,
      existingPlan?.plan_type
    );

    const changedAttributes = getChangedAttributes(existingPlan, data);
    handleEditPlan(planId as string, changedAttributes as ResourcePlanSchema);
    reset();
    setSelectedRecurrence("hourly");
  };

  


  // const capitalizeString = (string: string) => string?.replace(/\w/, c => c?.toUpperCase())

  return (
    <div className="flex flex-col justify-evenly items-center h-full">
      <span onClick={handleClose} className="ml-auto w-full flex cursor-pointer">
          <Cancel className="text-[#A1A1A1] hover:text-[#777] h-[18px] w-[18px] ml-auto" />
      </span>
      <div className="flex flex-col w-11/12">
        <h1 className="text-[18px] text-black font-raleway font-bold text-left my-2">Edit {isFundingPlan ? 'Rate Plan' : 'Booking Plan'}</h1>
        <h1 className="text-[16px] font-open-sans text-[#4D4D4D] text-left mb-2">
          {isFundingPlan
            ? 'Update the access fee members see for this funding offer.'
            : `Fill in the details below to edit your ${resourceType ? resourceType : 'resource'} booking plan.`}
        </h1>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="font-open-sans w-11/12 grid grid-flow-row gap-y-5">
        <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">Plan Name 
            {/* <InfoIcon className="h-3 w-3 my-auto mx-1 text-primary-300" /> */}
          </span>
          <input
            {...register("plan_name")}
            placeholder="Name"
            className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
          />
          {errors.plan_name && <span className="text-red-500 text-sm font-open-sans">{String(errors.plan_name?.message)}</span>}
        </label>

        <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">
            Plan Description 
          </span>
          <textarea
            {...register("plan_description")}
            placeholder="Description"
            className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
          />
          {errors.plan_description && <span className="text-red-500 text-sm font-open-sans">{String(errors.plan_description?.message)}</span>}
        </label>

        {/* <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">Resource Type</span>
          <select
            {...register("resource_type")}
            disabled={true}
            className="border-[1px] border-[#E9E9E9] text-slate-500 bg-inherit w-full my-1 rounded-lg"
          >
            <option value="">-- Resource type --</option>
            <option value="">{resourceType ? capitalizeString(resourceType) : 'Resource'}</option>
          </select>
        </label> */}

        <div className="grid grid-cols-2 gap-4">
          <label className="text-primary-200 text-xs">
            <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">
              Plan Period
              {/* <InfoIcon className="h-3 w-3 my-auto mx-1 text-primary-300 cursor-pointer" /> */}
            </span>
            <select
              {...register("plan_recurrence")}
              value={selectedRecurrence}
              onChange={(e) => handleRecurrenceChange(e.target.value as PlanRecurrenceOptions)}
              className="border-[1px] border-[#E9E9E9] text-slate-500 bg-inherit w-full my-1 rounded-lg h-[50px] px-3"
            >
              <option value="">-- Select Period --</option>
              {Object.values(recurrences).map((recurrence) => (
                <option key={recurrence} value={recurrence}>
                  {capitalizeString(recurrence)}
                </option>
              ))}
            </select>
            {errors.plan_recurrence && <span className="text-red-500 text-sm font-open-sans">{String(errors.plan_recurrence?.message)}</span>}
          </label>

          {(["hourly", "daily"].includes(currentRecurrence)) && (<label className="text-primary-200 text-xs">
            <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">Pricing Type</span>
            <select
              {...register("pricing_type")}
              value={pricingType}
              onChange={(e) => setValue('pricing_type', e.target.value)}
              className="border-[1px] border-[#E9E9E9] text-slate-500 bg-inherit w-full my-1 rounded-lg h-[50px] px-3"
            >
              <option value="">-- Select Pricing Type --</option>
              <option value="fixed">Fixed</option>
              <option value="dynamic">Dynamic</option>
            </select>
            {errors.pricing_type && <span className="text-red-500 text-sm font-open-sans">{String(errors.pricing_type?.message)}</span>}
          </label>)}
        </div>

        
        <div className="grid grid-flow-row grid-cols-3 space-x-2">
          <div className="flex flex-col">
            <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">
              Standard Rate ($)
            </span>
            <div className="flex flex-row justify-between">
              <label className="flex flex-row text-primary-200 border-[1px] border-[#E9E9E9] h-[50px] rounded-lg">
                <input
                  {...register("plan_rate", { valueAsNumber: true })}
                  placeholder="0"
                  className="border-0 bg-inherit w-full my-1 "
                  type="number"
                  step={0.01}
                  min={0}
                />

                <div
                  className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  onClick={() => handleInputDecrement('plan_rate', 0.01)}
                >
                  <DownArrow className=" text-black rounded-full  m-auto" />
                </div>
                <div
                  className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  onClick={() => handleInputIncrement('plan_rate', 0.01)}
                >
                    <UpArrow className="text-black rounded-full  my-auto" />
                </div>
              </label>
            </div>
            {errors.plan_rate && <span className="text-red-500 text-sm font-open-sans">{String(errors.plan_rate?.message)}</span>}
          </div>
          {(pricingType === 'dynamic' && ["hourly", "daily"].includes(currentRecurrence)) && (
            <>
              <div className="flex flex-col">
                <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">Peak Rate ($)</span>
                <label className="flex flex-row text-primary-200 border-[1px] border-[#E9E9E9] h-[50px] rounded-lg">
                  <input
                    {...register("peak_rate", { valueAsNumber: true })}
                    placeholder="0"
                    className="border-0 bg-inherit w-full my-1 "
                    type="number"
                    step={0.01}
                    min={0}
                  />

                  <div
                    className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                    onClick={() => handleInputDecrement('peak_rate', 0.01)}
                  >
                    <DownArrow className=" text-black rounded-full  m-auto" />
                  </div>
                  <div
                    className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                    onClick={() => handleInputIncrement('peak_rate', 0.01)}
                  >
                      <UpArrow className="text-black rounded-full  my-auto" />
                  </div>
                </label>
                {errors.peak_rate && <span className="text-red-500 text-sm font-open-sans">{String(errors.peak_rate?.message)}</span>}
              </div>
              
              <div className="flex flex-col">
                <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">Off-Peak Rate ($)</span>
                <label className="flex flex-row text-primary-200 border-[1px] border-[#E9E9E9] h-[50px] rounded-lg">
                  <input
                    {...register("off_peak_rate", { valueAsNumber: true })}
                    placeholder="0"
                    className="border-0 bg-inherit w-full my-1 "
                    type="number"
                    step={0.01}
                    min={0}
                  />

                  <div
                    className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                    onClick={() => handleInputDecrement('off_peak_rate', 0.01)}
                  >
                    <DownArrow className=" text-black rounded-full  m-auto" />
                  </div>
                  <div
                    className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                    onClick={() => handleInputIncrement('off_peak_rate', 0.01)}
                  >
                      <UpArrow className="text-black rounded-full  my-auto" />
                  </div>
                </label>
                {errors.off_peak_rate && <span className="text-red-500 text-sm font-open-sans">{String(errors.off_peak_rate?.message)}</span>}
              </div>
            </>
          )}
        </div>
       

        {!isFundingPlan && (<label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080] text-[15px] font-medium font-raleway my-1">Include Maximum Duration?</span>
          <span className="flex flex-row my-1 w-full">
            {(["no", "yes"] as const).map((option) => (
              (hasMaxHours === (option === "yes")) ? (
              <span 
                key={option} 
                className={`flex flex-row text-white font-open-sans text-[16px] mr-2 bg-primary-200 border-0 p-2 rounded-md w-auto cursor-pointer`}
              >
                <RadioButtonChecked className="text-white my-auto" />
                <p className="my-auto mx-1">{capitalizeString(option)}</p>
              </span>
              ) : (
                <span 
                  key={option} 
                  className={`flex flex-row text-black font-open-sans text-[16px] mr-3 border-[1px] border-[#E9E9E9] p-2 rounded-md w-auto cursor-pointer`}
                  onClick={() => setHasMaxHours(option === "yes")}
                >
                  <RadioButtonUnchecked className="text-[#CFCFCF] my-auto" />
                  <p className="my-auto mx-1">{capitalizeString(option)}</p>
                </span>
              )
            ))}
          </span>
        </label>)}

        {hasMaxHours && !isFundingPlan && (
          <div className="grid grid-flow-row grid-cols-3 space-x-2">
            <div className="flex flex-col">
              <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">
                {(["hourly", "daily"].includes(currentRecurrence)) ? "Max Hours" : "Max Days"}
              </span>
              <div className="flex flex-row justify-between">
                <label className="flex flex-row text-primary-200 border-[1px] border-[#E9E9E9] h-[50px] rounded-lg">
                  <input
                    placeholder="0"
                    className="border-0 bg-inherit w-full my-1 "
                    type="number"
                    step={1}
                    min={0}
                    value={displayMaxDaysOrHours}
                    onChange={(e)=>{
                      const numeric = Number(e.target.value || 0);
                      setDisplayMaxDaysOrHours(numeric);
                      if (["weekly", "monthly", "annual"].includes(currentRecurrence)) {
                        setValue('plan_max_hours', convertDaysToHours(numeric, currentRecurrence));
                      } else {
                        setValue('plan_max_hours', numeric);
                      }
                    }}
                  />

                  <div
                    className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                    onClick={() => {
                      const newValue = Math.max(0, Number(displayMaxDaysOrHours || 0) - 1);
                      setDisplayMaxDaysOrHours(newValue);
                      if (["weekly", "monthly", "annual"].includes(currentRecurrence)) {
                        setValue('plan_max_hours', convertDaysToHours(newValue, currentRecurrence));
                      } else {
                        setValue('plan_max_hours', newValue);
                      }
                    }}
                  >
                    <DownArrow className=" text-black rounded-full  m-auto" />
                  </div>
                  <div
                    className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                    onClick={() => {
                      const newValue = Number(displayMaxDaysOrHours || 0) + 1;
                      setDisplayMaxDaysOrHours(newValue);
                      if (["weekly", "monthly", "annual"].includes(currentRecurrence)) {
                        setValue('plan_max_hours', convertDaysToHours(newValue, currentRecurrence));
                      } else {
                        setValue('plan_max_hours', newValue);
                      }
                    }}
                  >
                      <UpArrow className="text-black rounded-full  my-auto" />
                  </div>
                </label>
              </div>
              {errors.plan_max_hours && <span className="text-red-500 text-sm font-open-sans">{String(errors.plan_max_hours?.message)}</span>}
            </div>

            <div className="flex flex-col">
              <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">Overage Rate ($/hour)</span>
              <label className="flex flex-row text-primary-200 border-[1px] border-[#E9E9E9] h-[50px] rounded-lg">
                <input
                  {...register("overage_rate", { valueAsNumber: true })}
                  placeholder="0"
                  className="border-0 bg-inherit w-full my-1 "
                  type="number"
                  step={0.01}
                  min={0}
                />

                <div
                  className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  onClick={() => handleInputDecrement('overage_rate', 0.01)}
                >
                  <DownArrow className=" text-black rounded-full  m-auto" />
                </div>
                <div
                  className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  onClick={() => handleInputIncrement('overage_rate', 0.01)}
                >
                    <UpArrow className="text-black rounded-full  my-auto" />
                </div>
              </label>
              {errors.overage_rate && <span className="text-red-500 text-sm font-open-sans">{String(errors.overage_rate?.message)}</span>}
            </div>
          </div>
        )}

        {/* Plan Features: perks, booking access, and bundled inventory */}
        <div className="flex flex-col gap-y-3 py-2">
          <div className="flex flex-col">
            <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway">
              Plan Features
            </span>
            <p className="text-[13px] text-[#808080] font-open-sans mt-1">
              {isFundingPlan ? (
                <>
                  Everything included with this rate{" "}
                  <span className="font-light text-xs text-[#808080]/80">
                    (allowances like included tokens, seats, and overage terms)
                  </span>
                </>
              ) : (
                <>
                  Everything included with this booking plan{" "}
                  <span className="font-light text-xs text-[#808080]/80">
                    (perks like priority booking, 24/7 access, and optional inventory items)
                  </span>
                </>
              )}
            </p>
          </div>

          <div className="flex flex-col gap-y-5 border-[1px] border-[#E9E9E9] rounded-lg p-4">

            {/* Plan Features: perks */}
            <PlanTierFeaturesField
              featuresRef={featuresRef}
              initialFeatures={existingPlan?.features ?? EMPTY_PLAN_FEATURES}
            />

            {resource?.id && !isFundingPlan && (
              <PlanResourceInventoryFeaturesField
                resource={resource}
                includeAssociatedResources={includeAssociatedResources}
                onIncludeAssociatedResourcesChange={handleIncludeAssociatedResourcesChange}
                associationsRef={associatedResourcesRef}
                initialAssociatedResources={initialResourceAssociations}
                planMaxHours={Number(planMaxHours) || 0}
                planType={existingPlan?.plan_type || "resource"}
              />
            )}

            {!isFundingPlan && (
              includeAssociatedResources ||
              (
                existingPlan?.plan_type === "dual" &&
                resourcePlanHasValidAssociatedResources(existingPlan?.associated_resources)
              )
            ) && (
              <PlanFulltimeHoursField
                control={control}
                setValue={setValue}
                allowedPlanTypes={["resource", "dual"]}
              />
            )}
          </div>
        </div>

        <button
          type="submit"
          className="border-0 font-medium bg-primary-200 text-white font-raleway rounded-md py-2 px-4 text-[18px] w-full ml-auto"
          data-testid="submit-button"
        >
          Update Plan
        </button>
      </form>
    </div>
  );
};



type AddResourcePlanProps = {
  resource?: ResourceSchema
  isDuplicate: {existingPlan: ResourcePlanSchema, checked: boolean}
  handleClose: () => void;
  handleAddPlan: (plan: ResourcePlanSchema) => void;
  resourceType?: ResourceType;
};

export const AddResourcePlanForm: React.FC<AddResourcePlanProps> = (props) => {
  var { resource, isDuplicate, resourceType, handleClose, handleAddPlan } = props;
  // const resourceOptions = resource?.resource_options as ResourceOptionType;
  const allRecurrences: PlanRecurrenceOptions[] = ["hourly", "daily", "weekly", "monthly", "annual"];
  const derivedResourceType = (resourceType ?? ((resource as any)?.resource_type)) as ResourceType | undefined;
  /** Funding offers price a recurring access fee, so slot-booking controls do not apply. */
  const isFundingPlan = derivedResourceType === "funding";
  // Access fees bill on a subscription cadence; hourly/daily slot rates do not apply.
  const recurrences = isFundingPlan
    ? (["monthly", "annual"] as PlanRecurrenceOptions[])
    : allRecurrences;
  // The AiriWheels listing prices a published access fee, so seed it from that pricing.
  const seededPlan = (!isDuplicate.checked && isAiriWheelsResource(resource))
    ? AIRIWHEELS_RATE_PLAN
    : undefined;
  const [selectedRecurrence, setSelectedRecurrence] = useState<string | PlanRecurrenceOptions>(
    seededPlan?.plan_recurrence || (isFundingPlan ? "monthly" : "hourly")
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    // getValues,
    control,
    formState: { errors },
    reset
  } = useForm<any>({
    resolver: zodResolver(AddPlanSchema),
            defaultValues: {
          plan_description: seededPlan?.plan_description || "",
          plan_name: seededPlan?.plan_name || "",
          plan_rate: seededPlan?.plan_rate || 0,
          plan_recurrence: seededPlan?.plan_recurrence || (isFundingPlan ? "monthly" : "hourly"),
          plan_max_hours: 0,
          pricing_type: "fixed",
          resource_type: derivedResourceType,
          plan_type: isDuplicate.checked ? (isDuplicate.existingPlan?.plan_type || "resource") : "resource",
          is_fulltime_hours: isDuplicate.checked ? (isDuplicate.existingPlan?.is_fulltime_hours ?? false) : false,
          overage_rate: seededPlan?.overage_rate || 0,
          // standard_rate: 0,
          peak_rate: 0,
          off_peak_rate: 0,
          features: seededPlan?.features || []
        }
  });

  const planRate = watch("plan_rate");
  const planMaxHours = watch("plan_max_hours");
  const pricingType = watch("pricing_type");
  const [includeAssociatedResources, setIncludeAssociatedResources] = useState(
    () => isDuplicate.checked && resourcePlanIncludesAssociatedResources(isDuplicate.existingPlan)
  );
  const featuresRef = useRef<string[]>(isDuplicate.existingPlan?.features || seededPlan?.features || []);
  const associatedResourcesRef = useRef<PlanResourceAssociation[]>(
    isDuplicate.checked ? (isDuplicate.existingPlan?.associated_resources || []) : []
  );

  // Ensure selectedRecurrence state is synchronized with form value
  const currentRecurrence = watch("plan_recurrence");
  useEffect(() => {
    if (currentRecurrence && currentRecurrence !== selectedRecurrence) {
      setSelectedRecurrence(currentRecurrence);
    }
  }, [currentRecurrence, selectedRecurrence, watch]);


    // Reset form with existingPlan data when it changes
    useEffect(() => {
      if (isDuplicate.existingPlan) {
        reset({
          plan_description: isDuplicate.existingPlan?.plan_description || seededPlan?.plan_description || "",
          plan_name: isDuplicate.existingPlan?.plan_name || seededPlan?.plan_name || "",
            plan_rate: isDuplicate.existingPlan?.plan_rate || seededPlan?.plan_rate || 0,
          plan_recurrence: isDuplicate.existingPlan?.plan_recurrence || seededPlan?.plan_recurrence || (isFundingPlan ? "monthly" : "hourly"),
          plan_max_hours: isDuplicate.existingPlan?.plan_max_hours || 0,
          pricing_type: isDuplicate.existingPlan?.pricing_type || "fixed",
          resource_type: derivedResourceType || "kitchen",
          plan_type: isDuplicate.existingPlan?.plan_type || "resource",
          is_fulltime_hours: isDuplicate.existingPlan?.is_fulltime_hours ?? false,
          overage_rate: isDuplicate.existingPlan?.overage_rate || seededPlan?.overage_rate || 0,
          peak_rate: isDuplicate.existingPlan?.peak_rate || 0,
          off_peak_rate: isDuplicate.existingPlan?.off_peak_rate || 0,
          features: isDuplicate.existingPlan?.features || seededPlan?.features || []
        });
      }
    }, [isDuplicate.existingPlan, derivedResourceType, isFundingPlan, reset, seededPlan]);

  useEffect(() => {
    if (isDuplicate.checked && isDuplicate.existingPlan) {
      featuresRef.current = isDuplicate.existingPlan.features || [];
      associatedResourcesRef.current = isDuplicate.existingPlan.associated_resources || [];
      setIncludeAssociatedResources(resourcePlanIncludesAssociatedResources(isDuplicate.existingPlan));
      return;
    }

    // Keep AiriWheels / funding seed perks when adding a new rate; never wipe them on mount.
    featuresRef.current = seededPlan?.features || [];
    associatedResourcesRef.current = [];
    setIncludeAssociatedResources(false);
  }, [isDuplicate.checked, isDuplicate.existingPlan, seededPlan]);

  const handleIncludeAssociatedResourcesChange = useCallback((included: boolean) => {
    setIncludeAssociatedResources(included);
    if (!included) {
      clearResourcePlanAssociatedResources(setValue, associatedResourcesRef);
    }
  }, [setValue]);

  // Stable input for the inventory field, decoupled from the mutable output ref so the
  // field's own writes never feed back and wipe the selection.
  const initialResourceAssociations = useMemo<PlanResourceAssociation[]>(
    () => (isDuplicate.checked ? (isDuplicate.existingPlan?.associated_resources ?? []) : []),
    [isDuplicate.checked, isDuplicate.existingPlan]
  );

  // const handleRecurrenceChange = async (
  //   event: React.ChangeEvent<HTMLSelectElement>
  // ) => {
  //   const {name, value} = event.target
  //   setSelectedRecurrence(value);
  //   setPlan((prevPlan) => ({
  //     ...prevPlan,
  //     [name]: value,
  //     plan_max_hours: maxHours((resource?.capacity) ? parseInt(resource.capacity) : 1, value)
  //   }));
  // };

  const handleRecurrenceChange = (recurrence: PlanRecurrenceOptions) => {
    setSelectedRecurrence(recurrence);
    setValue("plan_recurrence", recurrence);
    if (isFundingPlan) {
      setValue("plan_max_hours", 0);
      return;
    }
    const calculatedMaxHours = maxHours((resource?.resource_options?.floor_plan?.max_capacity) ? Number(resource.resource_options?.floor_plan?.max_capacity) : 1, recurrence);
    // Allow 0 for max hours as per validation requirements
    setValue("plan_max_hours", Math.max(0, calculatedMaxHours));
  };

  // Keep standard_rate in sync with plan_rate for both pricing types
  useEffect(() => {
    setValue('plan_rate', Number(planRate || 0));
  }, [planRate, setValue]);

  const handleInputIncrement = (
    name: keyof any,
    step: number
  ) => {
    if (name === "plan_rate") {
      const currentValue = Number(planRate || 0);
      setValue("plan_rate", roundTo(currentValue + step, 2));
    } else if (name === "plan_max_hours") {
      const currentValue = Number(planMaxHours || 0);
      setValue("plan_max_hours", currentValue + step);
    } 
    // else if (name ===  "peak_rate" || name ===  "off_peak_rate"){
    //   const currentValue = Number(watch(name) || 0);
    //   setValue(name, currentValue + step);
    // } else if (name ===  "overage_rate"){
    //   const currentValue = Number(watch(name) || 0);
    //   setValue(name, currentValue + step);
    // } 
    else if (name ===  "peak_rate" || name ===  "off_peak_rate" || name === "overage_rate"){
      const currentValue = Number(watch(name) || 0);
      if (currentValue >= 0.00 && step > 0.00) {
        setValue(name, (currentValue + step).toFixed(2));
      }
    }
  };

  const handleInputDecrement = (
    name: keyof any,
    step: number
  ) => {
    if (name === "plan_rate") {
      const currentValue = Number(planRate || 0);
      // Allow rate to go down to 0
      setValue("plan_rate", Math.max(0, roundTo(currentValue - step, 2)));
    } else if (name === "plan_max_hours") {
      const currentValue = Number(planMaxHours || 0);
      // Allow max hours to go down to 0
      setValue("plan_max_hours", Math.max(0, currentValue - step));
    } else if (name ===  "peak_rate" || name ===  "off_peak_rate" || name === "overage_rate"){
      const currentValue = Number(watch(name) || 0);
      if (currentValue > 0.00 && currentValue - step >= 0.00) {
        setValue(name, (currentValue - step).toFixed(2));
      }
    }
  };

  const onSubmit = (data: ResourcePlanSchema) => {
    if (!data.resource_type && derivedResourceType) {
      data.resource_type = derivedResourceType;
    }
    if (data.plan_recurrence === 'monthly' || data.plan_recurrence === 'annual') {
      data.pricing_type = "fixed";
    }
    if (data.pricing_type === 'fixed') {
      data.plan_rate = Number(data.plan_rate || 0);
      data.peak_rate = undefined;
      data.off_peak_rate = undefined;
    }

    applyResourcePlanAssociations(
      data,
      resource,
      associatedResourcesRef,
      featuresRef,
      includeAssociatedResources,
      "resource"
    );

    handleAddPlan(data);
    reset();
    setSelectedRecurrence(isFundingPlan ? "monthly" : "hourly");
  };

  const [hasMaxHours, setHasMaxHours] = useState(false);

  // Clear max hours fields when hasMaxHours is false
  useEffect(() => {
    if (!hasMaxHours) {
      setValue('plan_max_hours', 0);
      setValue('overage_rate', 0);
    }
  }, [hasMaxHours, setValue]);

  // Helper function to convert days to hours based on recurrence
  const convertDaysToHours = (days: number, recurrence: string): number => {
    switch (recurrence) {
      case 'weekly':
      case 'monthly':
      case 'annual':
        return days * 24;
      default:
        return days; // For hourly and daily, the value represents hours already
    }
  };

  // Helper function to convert hours to days based on recurrence
  const convertHoursToDays = (hours: number, recurrence: string): number => {
    switch (recurrence) {
      case 'weekly':
      case 'monthly':
      case 'annual':
        return Math.floor((hours || 0) / 24);
      default:
        return hours; // For hourly and daily, keep hours
    }
  };

  // const capitalizeString = (string: string) => string?.replace(/\w/, c => c?.toUpperCase())

  return (
    <div className="flex flex-col justify-evenly items-center h-full">
      <span onClick={handleClose} className="ml-auto w-full flex cursor-pointer">
          <Cancel className="text-[#A1A1A1] hover:text-[#777] h-[18px] w-[18px] ml-auto" />
      </span>
      <div className="flex flex-col w-11/12">
        <h1 className="text-[18px] text-black font-raleway font-bold text-left my-2">{isDuplicate.checked ? 'Duplicate' : 'Add'} {isFundingPlan ? 'Rate Plan' : 'Booking Plan'}</h1>
        <h1 className="text-[16px] font-open-sans text-[#4D4D4D] text-left mb-2">
          {isFundingPlan
            ? 'Set the access fee members see for this funding offer.'
            : `Fill in the details below to add your ${resourceType ? resourceType : 'resource'} booking plan.`}
        </h1>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="font-open-sans w-11/12 grid grid-flow-row gap-y-5">
        <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">Plan Name 
            {/* <InfoIcon className="h-3 w-3 my-auto mx-1 text-primary-300" /> */}
          </span>
          <input
            {...register("plan_name")}
            placeholder="Name"
            className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
          />
          {errors.plan_name && <span className="text-red-500 text-sm font-open-sans">{String(errors.plan_name?.message)}</span>}
        </label>

        <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">
            Plan Description 
          </span>
          <textarea
            {...register("plan_description")}
            placeholder="Description"
            className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
          />
          {errors.plan_description && <span className="text-red-500 text-sm font-open-sans">{String(errors.plan_description?.message)}</span>}
        </label>

        {/* <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">Resource Type</span>
          <select
            {...register("resource_type")}
            disabled={true}
            className="border-[1px] border-[#E9E9E9] text-slate-500 bg-inherit w-full my-1 rounded-lg"
          >
            <option value="">-- Resource type --</option>
            <option value="">{resourceType ? capitalizeString(resourceType) : 'Resource'}</option>
          </select>
        </label> */}

        <div className="grid grid-cols-2 gap-4">
          <label className="text-primary-200 text-xs">
            <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">
              Plan Period
              {/* <InfoIcon className="h-3 w-3 my-auto mx-1 text-primary-300 cursor-pointer" /> */}
            </span>
            <select
              {...register("plan_recurrence")}
              value={selectedRecurrence}
              onChange={(e) => handleRecurrenceChange(e.target.value as PlanRecurrenceOptions)}
              className="border-[1px] border-[#E9E9E9] text-slate-500 bg-inherit w-full my-1 rounded-lg h-[50px] px-3"
            >
              <option value="">-- Select Period --</option>
              {Object.values(recurrences).map((recurrence) => (
                <option key={recurrence} value={recurrence}>
                  {capitalizeString(recurrence)}
                </option>
              ))}
            </select>
            {errors.plan_recurrence && <span className="text-red-500 text-sm font-open-sans">{String(errors.plan_recurrence?.message)}</span>}
          </label>

          {(["hourly", "daily"].includes(currentRecurrence)) && (<label className="text-primary-200 text-xs">
            <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">Pricing Type</span>
            <select
              {...register("pricing_type")}
              value={pricingType}
              onChange={(e) => setValue('pricing_type', e.target.value)}
              className="border-[1px] border-[#E9E9E9] text-slate-500 bg-inherit w-full my-1 rounded-lg h-[50px] px-3"
            >
              <option value="">-- Select Pricing Type --</option>
              <option value="fixed">Fixed</option>
              <option value="dynamic">Dynamic</option>
            </select>
            {errors.pricing_type && <span className="text-red-500 text-sm font-open-sans">{String(errors.pricing_type?.message)}</span>}
          </label>)}
        </div>

        
        <div className="grid grid-flow-row grid-cols-3 space-x-2">
          <div className="flex flex-col">
            <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">
              Standard Rate ($)
            </span>
            <div className="flex flex-row justify-between">
              <label className="flex flex-row text-primary-200 border-[1px] border-[#E9E9E9] h-[50px] rounded-lg">
                <input
                  {...register("plan_rate", { valueAsNumber: true })}
                  placeholder="0"
                  className="border-0 bg-inherit w-full my-1 "
                  type="number"
                  step={0.01}
                  min={0}
                />

                <div
                  className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  onClick={() => handleInputDecrement('plan_rate', 0.01)}
                >
                  <DownArrow className=" text-black rounded-full  m-auto" />
                </div>
                <div
                  className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  onClick={() => handleInputIncrement('plan_rate', 0.01)}
                >
                    <UpArrow className="text-black rounded-full  my-auto" />
                </div>
              </label>
            </div>
            {errors.plan_rate && <span className="text-red-500 text-sm font-open-sans">{String(errors.plan_rate?.message)}</span>}
          </div>
          {(pricingType === 'dynamic' && ["hourly", "daily"].includes(currentRecurrence)) && (
            <>
              <div className="flex flex-col">
                <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">Peak Rate ($)</span>
                <label className="flex flex-row text-primary-200 border-[1px] border-[#E9E9E9] h-[50px] rounded-lg">
                  <input
                    {...register("peak_rate", { valueAsNumber: true })}
                    placeholder="0"
                    className="border-0 bg-inherit w-full my-1 "
                    type="number"
                    step={0.01}
                    min={0}
                  />

                  <div
                    className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                    onClick={() => handleInputDecrement('peak_rate', 0.01)}
                  >
                    <DownArrow className=" text-black rounded-full  m-auto" />
                  </div>
                  <div
                    className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                    onClick={() => handleInputIncrement('peak_rate', 0.01)}
                  >
                      <UpArrow className="text-black rounded-full  my-auto" />
                  </div>
                </label>
                {errors.peak_rate && <span className="text-red-500 text-sm font-open-sans">{String(errors.peak_rate?.message)}</span>}
              </div>
              
              <div className="flex flex-col">
                <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">Off-Peak Rate ($)</span>
                <label className="flex flex-row text-primary-200 border-[1px] border-[#E9E9E9] h-[50px] rounded-lg">
                  <input
                    {...register("off_peak_rate", { valueAsNumber: true })}
                    placeholder="0"
                    className="border-0 bg-inherit w-full my-1 "
                    type="number"
                    step={0.01}
                    min={0}
                  />

                  <div
                    className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                    onClick={() => handleInputDecrement('off_peak_rate', 0.01)}
                  >
                    <DownArrow className=" text-black rounded-full  m-auto" />
                  </div>
                  <div
                    className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                    onClick={() => handleInputIncrement('off_peak_rate', 0.01)}
                  >
                      <UpArrow className="text-black rounded-full  my-auto" />
                  </div>
                </label>
                {errors.off_peak_rate && <span className="text-red-500 text-sm font-open-sans">{String(errors.off_peak_rate?.message)}</span>}
              </div>
            </>
          )}
        </div>
       
        {!isFundingPlan && (<label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">Include Maximum Duration?</span>
          <span className="flex flex-row my-1 w-full">
            {(["no", "yes"] as const).map((option) => (
              (hasMaxHours === (option === "yes")) ? (
              <span 
                key={option} 
                className={`flex flex-row text-white font-open-sans text-[16px] mr-2 bg-primary-200 border-0 p-2 rounded-md w-auto cursor-pointer`}
              >
                <RadioButtonChecked className="text-white my-auto" />
                <p className="my-auto mx-1">{capitalizeString(option)}</p>
              </span>
              ) : (
                <span 
                  key={option} 
                  className={`flex flex-row text-black font-open-sans text-[16px] mr-3 border-[1px] border-[#E9E9E9] p-2 rounded-md w-auto cursor-pointer`}
                  onClick={() => setHasMaxHours(option === "yes")}
                >
                  <RadioButtonUnchecked className="text-[#CFCFCF] my-auto" />
                  <p className="my-auto mx-1">{capitalizeString(option)}</p>
                </span>
              )
            ))}
          </span>
        </label>)}
        {(hasMaxHours && !isFundingPlan) && (
          <div className="grid grid-flow-row grid-cols-3 space-x-2">
            <div className="flex flex-col">
              <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">
                {(["hourly", "daily"].includes(currentRecurrence)) ? "Max Hours" : "Max Days"}
              </span>
              <div className="flex flex-row justify-between">
                <label className="flex flex-row text-primary-200 border-[1px] border-[#E9E9E9] h-[50px] rounded-lg">
                  <input
                    {...register("plan_max_hours", { 
                      valueAsNumber: true,
                      onChange: (e) => {
                        const value = Number(e.target.value);
                        if (["weekly", "monthly", "annual"].includes(currentRecurrence)) {
                          // Convert days to hours for storage
                          const hours = convertDaysToHours(value, currentRecurrence);
                          setValue('plan_max_hours', hours);
                        } else {
                          setValue('plan_max_hours', value);
                        }
                      }
                    })}
                    placeholder="0"
                    className="border-0 bg-inherit w-full my-1 "
                    type="number"
                    step={1}
                    min={0}
                    value={["weekly", "monthly", "annual"].includes(currentRecurrence) 
                      ? convertHoursToDays(planMaxHours || 0, currentRecurrence)
                      : planMaxHours || 0
                    }
                  />

                  <div
                    className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                    onClick={() => {
                      const currentValue = ["weekly", "monthly", "annual"].includes(currentRecurrence) 
                        ? convertHoursToDays(planMaxHours || 0, currentRecurrence)
                        : planMaxHours || 0;
                      const newValue = Math.max(0, currentValue - 1);
                      if (["weekly", "monthly", "annual"].includes(currentRecurrence)) {
                        setValue('plan_max_hours', convertDaysToHours(newValue, currentRecurrence));
                      } else {
                        setValue('plan_max_hours', newValue);
                      }
                    }}
                  >
                    <DownArrow className=" text-black rounded-full  m-auto" />
                  </div>
                  <div
                    className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                    onClick={() => {
                      const currentValue = ["weekly", "monthly", "annual"].includes(currentRecurrence) 
                        ? convertHoursToDays(planMaxHours || 0, currentRecurrence)
                        : planMaxHours || 0;
                      const newValue = currentValue + 1;
                      if (["weekly", "monthly", "annual"].includes(currentRecurrence)) {
                        setValue('plan_max_hours', convertDaysToHours(newValue, currentRecurrence));
                      } else {
                        setValue('plan_max_hours', newValue);
                      }
                    }}
                  >
                      <UpArrow className="text-black rounded-full  my-auto" />
                  </div>
                </label>
              </div>
              {errors.plan_max_hours && <span className="text-red-500 text-sm font-open-sans">{String(errors.plan_max_hours?.message)}</span>}
            </div>

            <div className="flex flex-col">
              <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway my-1">Overage Rate ($/hour)</span>
              <label className="flex flex-row text-primary-200 border-[1px] border-[#E9E9E9] h-[50px] rounded-lg">
                <input
                  {...register("overage_rate", { valueAsNumber: true })}
                  placeholder="0"
                  className="border-0 bg-inherit w-full my-1 "
                  type="number"
                  step={0.01}
                  min={0}
                />

                <div
                  className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  onClick={() => handleInputDecrement('overage_rate', 0.01)}
                >
                  <DownArrow className=" text-black rounded-full  m-auto" />
                </div>
                <div
                  className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  onClick={() => handleInputIncrement('overage_rate', 0.01)}
                >
                    <UpArrow className="text-black rounded-full  my-auto" />
                </div>
              </label>
              {errors.overage_rate && <span className="text-red-500 text-sm font-open-sans">{String(errors.overage_rate?.message)}</span>}
            </div>
          </div>
        )}

        {/* Plan Features: perks, booking access, and bundled inventory */}
        <div className="flex flex-col gap-y-3 py-2">
          <div className="flex flex-col">
            <span className="flex flex-row text-[#808080]/80 text-[15px] font-medium font-raleway">
              Plan Features
            </span>
            <p className="text-[13px] text-[#808080] font-open-sans mt-1">
              {isFundingPlan ? (
                <>
                  Everything included with this rate{" "}
                  <span className="font-light text-xs text-[#808080]/80">
                    (allowances like included tokens, seats, and overage terms)
                  </span>
                </>
              ) : (
                <>
                  Everything included with this booking plan{" "}
                  <span className="font-light text-xs text-[#808080]/80">
                    (perks like priority booking, 24/7 access, and optional inventory items)
                  </span>
                </>
              )}
            </p>
          </div>

          <div className="flex flex-col gap-y-5 border-[1px] border-[#E9E9E9] rounded-lg p-4">
            {/* Plan Features: perks */}
            <PlanTierFeaturesField
              featuresRef={featuresRef}
              initialFeatures={
                isDuplicate.checked
                  ? (isDuplicate.existingPlan?.features ?? EMPTY_PLAN_FEATURES)
                  : (seededPlan?.features ?? EMPTY_PLAN_FEATURES)
              }
            />

            {resource?.id && !isFundingPlan && (
              <PlanResourceInventoryFeaturesField
                resource={resource}
                includeAssociatedResources={includeAssociatedResources}
                onIncludeAssociatedResourcesChange={handleIncludeAssociatedResourcesChange}
                associationsRef={associatedResourcesRef}
                initialAssociatedResources={initialResourceAssociations}
                planMaxHours={Number(planMaxHours) || 0}
                planType="resource"
              />
            )}

            {includeAssociatedResources && !isFundingPlan && (
              <PlanFulltimeHoursField
                control={control}
                setValue={setValue}
                allowedPlanTypes={["resource"]}
              />
            )}
          </div>
        </div>

        <button
          type="submit"
          className="border-0 font-medium bg-primary-200 text-white font-raleway rounded-md py-2 px-4 text-[18px] w-full ml-auto"
          data-testid="submit-button"
        >
          {isFundingPlan ? 'Add Rate' : 'Add Plan'}
        </button>
      </form>
    </div>
  );
};

type AddRequirementProps = {
  handleClose: () => void;
  handleAddRequirement: (kitchen: DSCommunitySchema | {}) => void;
};

const AddRequirementForm: React.FC<AddRequirementProps> = (props) => {
  var { handleAddRequirement, handleClose } = props;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<any>({
    resolver: zodResolver(AddRequirementSchema),
    defaultValues: {
      name: "",
      validity: "",
      is_orientation_training: false,
    }
  });

  const onSubmit = (data: any) => {
    const { is_orientation_training, ...requirementData } = data;
    handleAddRequirement(
      is_orientation_training
        ? { ...requirementData, category: "orientation_training" }
        : requirementData
    );
    reset();
  };

  return (
    <div className="flex flex-col justify-evenly items-center h-full">
      <span onClick={handleClose} className="ml-auto w-full flex cursor-pointer">
          <Cancel className="text-[#A1A1A1] hover:text-[#777] h-[18px] w-[18px] ml-auto" />
      </span>

      <form onSubmit={handleSubmit(onSubmit)} className="font-open-sans w-11/12 grid grid-flow-row gap-y-5 mt-4">
        <label className="text-primary-200 text-xs">
          <span className="flex flex-row text-[#808080] text-[16px] font-raleway my-1">Requirement
          </span>
          <input
            {...register("name")}
            placeholder="Enter your requirement"
            className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-1 rounded-lg"
          />
          {errors.name && <span className="text-red-500 text-sm font-open-sans">{String(errors.name?.message)}</span>}
        </label>
        <label className="flex flex-row items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            {...register("is_orientation_training")}
            data-testid="orientation-training-checkbox"
            className="border-[#E9E9E9] text-primary-300 border-[1px] rounded-md h-[18px] w-[18px]"
          />
          <span className="text-[#808080] text-[15px] font-raleway">
            This is an orientation or training requirement
          </span>
        </label>
        <button
          type="submit"
          className="border-0 bg-primary-200 text-white font-raleway rounded-md p-2 text-[18px] w-1/3 h-[54px] ml-auto"
          data-testid="submit-button"
        >
          Add Requirement
        </button>
      </form>
    </div>
  );
};


// type AddInventoryFormProps = {
//   kitchen?: KitchenSpaceSchema
//   handleClose: () => void;
//   handleAddInventory: (inventory: InventorySchema) => void;
// };

// const AddInventoryForm: React.FC<AddInventoryFormProps> = (props) => {
//   var { kitchen, handleAddInventory } = props;
//   // const recurrences = ["hourly", "daily", "weekly", "monthly", "annual"];
//   // const measureUnits = ["hourly", "daily", "weekly", "monthly", "annual"];
//   // const [selectedRecurrence, setSelectedRecurrence] = useState<'hourly' | 'daily' | 'weekly' | 'monthly' | 'annual'>();

//   const {
//     register,
//     handleSubmit,
//     setValue,
//     watch,
//     formState: { errors },
//     reset
//   } = useForm<any>({
//     resolver: zodResolver(AddInventorySchema),
//     defaultValues: {
//       item_name: "",
//       total_quantity: 0,
//       category: 'equipment',
//       measure_unit: 'kg',
//       unit_price: 0,
//       image_url: undefined
//     }
//   });

//   const [selectedImage, setSelectedImage] = useState<string>("");
//   const [selectedCategory, setSelectedCategory] = useState<string>("equipment");
//   const [selectedMeasureUnit, setSelectedMeasureUnit] = useState<string>("kg");
//   const { searchInventoryCatalog } = useAddInventory()

//   const [suggestions, setSuggestions] = useState<string[]>([])

//   const itemName = watch("item_name");

//   const handleImageChange = (
//     event: React.ChangeEvent<HTMLInputElement>
//   ) => {
//     const file = event.target.files?.[0];
//     if (file) {
//       // Read the selected image file as a data URL
//       const reader = new FileReader();
//       reader.onload = (e) => {
//         setSelectedImage(e.target?.result as string);
//       };
//       reader.readAsDataURL(file);
//     } else {
//       setSelectedImage("");
//     }
//     handleFileChange(event);
//   };

//   const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
//     const { name, files } = event.target;
//     if (name && files && files.length > 0) {
//       let file_data = files[0];
//       setValue(name, file_data);
//     }
//   };

//   const handleSelectChange = (
//     event: React.ChangeEvent<HTMLSelectElement>
//   ) => {
//     const { name, value } = event.target;
//     setValue(name, value);

//     if(name === 'category' ){
//       setSelectedCategory(value)
//     } else if(name === 'measure_unit' ){
//       setSelectedMeasureUnit(value)
//     }
//   };

//   const getEnumValue = (key: string): string =>  {
//     return MeasurementUnits[key as keyof typeof MeasurementUnits].toLowerCase() as string
//   }

//   const handleSuggestion = (query: string) => {
//     if (query && selectedCategory){
//       let suggestionList = searchInventoryCatalog(selectedCategory, query)
//       setSuggestions(suggestionList)
//     } else {
//       setSuggestions([])
//     }
//   }

//   const onSubmit = (data: any) => {
//     if (kitchen?.id){
//       data.resource_id = kitchen.id
//     }
//     handleAddInventory(data);
//     reset();
//     setSelectedImage("");
//     setSelectedCategory("equipment");
//     setSelectedMeasureUnit("kg");
//     setSuggestions([]);
//   };

//   return (
//     <div className="flex flex-col space-y-2 md:justify-center items-center md:gap-x-2 m-10 md:mx-0">
//       <div className="flex flex-col">
//         <img src="/logo192.png" alt="homecook-logo" className="w-[110px] h-[82px] self-center"/>
//         <h1 className="text-2xl text-primary-200 text-center">Add Inventory Item</h1>
//         <h1 className="text-base text-back-200 text-center">Enter information below</h1>

//       </div>
//       <form onSubmit={handleSubmit(onSubmit)} className="font-open-sans w-full px-5 grid grid-flow-row gap-y-5">
//         <label className="text-primary-200 text-xs">
//           <span className="flex flex-row font-bold">Item Name <span className='text-red-900 text-sm mx-2'>*</span>
//             {/* <InfoIcon className="h-3 w-3 my-auto mx-1 text-primary-300" /> */}
//           </span>
//           <input
//             {...register("item_name")}
//             placeholder="Name"
//             className="formInput bg-inherit w-full my-1"
//             onChange={(e) => {
//               if (e.target.value) {
//                 handleSuggestion(e.target.value);
//               } else {
//                 setSuggestions([]);
//               }
//             }}
//           />
//           {errors.item_name && <span className="text-red-500 text-sm font-open-sans">{String(errors.item_name?.message)}</span>}
//           {
//             (suggestions && suggestions.length > 0 && itemName) && 
//             (
//               <div className="bg-gray-200 h-auto w-auto fixed">
//               {
//                 suggestions.slice(0, 3).map((suggestion, i) => (
//                     <p 
//                       key={i} 
//                       className="font-normal text-[16px] text-primary-300 cursor-pointer mb-1 p-1 bg-gray-200"
//                       onClick={()=> {
//                         setValue("item_name", suggestion);
//                         handleSuggestion("");
//                       }}
//                       >
//                       {suggestion}
//                     </p>
//                   )
//                 )
//               }
//               </div>
//             )
//           }
//         </label>

//         <label className="flex flex-col text-primary-200 text-xs">
//           <span className="flex flex-row font-bold">
//             Item Category <span className='text-red-900 text-sm mx-2'>*</span>
//             <InfoIcon className="h-3 w-3 my-auto mx-1 text-primary-300 cursor-pointer" />
//           </span>
//           <select
//             {...register("category")}
//             value={selectedCategory}
//             onChange={handleSelectChange}
//             className="formInput text-slate-500 bg-inherit w-full my-1"
//           >
//             <option value="" disabled>-- Category --</option>
//             {Object.keys(InventoryCategories).map((category, i) => (
//               <option key={i} value={category}>
//                 {capitalizeString(category)}
//               </option>
//             ))}
//           </select>
//           {errors.category && <span className="text-red-500 text-sm font-open-sans">{String(errors.category?.message)}</span>}
//         </label>

//         <div className="flex flex-row justify-between">
//           <label className="text-primary-200 text-xs w-3/5">
//             <span className="flex flex-row font-bold">
//               Item Quantity <span className='text-red-900 text-sm mx-2'>*</span>
//               {/* <InfoIcon className="h-3 w-3 my-auto mx-1 text-primary-300" /> */}
//             </span>
//             <input
//               {...register("total_quantity", { valueAsNumber: true })}
//               className="formInput bg-inherit w-full my-1"
//               type="number"
//               step={1}
//               min={0}
//             />
//             {errors.total_quantity && <span className="text-red-500 text-sm font-open-sans">{String(errors.total_quantity?.message)}</span>}
//           </label>

//           <label className="flex flex-col text-primary-200 text-xs w-2/6 ">
//             <span className="flex flex-row font-bold">
//               Measurement Unit <span className='text-red-900 text-sm mx-2'>*</span>
//               <InfoIcon className="h-3 w-3 my-auto mx-1 text-primary-300 cursor-pointer" />
//             </span>
//             <select
//               {...register("measure_unit")}
//               value={selectedMeasureUnit}
//               onChange={handleSelectChange}
//               className="formInput text-slate-500 bg-inherit w-full my-1"
//             >
//               <option value="" disabled>-- Unit --</option>
//               {Object.keys(MeasurementUnits).map((measureUnit, i) => (
//                 <option key={i} value={measureUnit}>
//                   {capitalizeString(getEnumValue(measureUnit))} ({measureUnit})
//                 </option>
//               ))}
//             </select>
//             {errors.measure_unit && <span className="text-red-500 text-sm font-open-sans">{String(errors.measure_unit?.message)}</span>}
//           </label>
//         </div>

//         <label className="text-primary-200 text-xs">
//           <span className="flex flex-row font-bold">
//             Unit Price (USD) <span className='text-red-900 text-sm mx-2'>*</span>
//             <InfoIcon className="h-3 w-3 my-auto mx-1 text-primary-300 cursor-pointer" />
//           </span>
//           <input
//             {...register("unit_price", { valueAsNumber: true })}
//             placeholder="Rate ($)"
//             className="formInput bg-inherit w-full my-1"
//             type="number"
//             step={0.01}
//             min={0}
//           />
//           {errors.unit_price && <span className="text-red-500 text-sm font-open-sans">{String(errors.unit_price?.message)}</span>}
//         </label>

//         <label className="text-primary-200 text-xs">
//           <span className="flex flex-row font-bold">
//             Item Image 
//             <InfoIcon className="h-3 w-3 my-auto mx-1 text-primary-300 cursor-pointer" />
//           </span>
//           <div className="flex flex-col border-primary-200 border-[1px] rounded-xl text-center my-1 p-1 cursor-pointer">
//             {
//                 (selectedImage) ?  (
//                     <img
//                       id="image-input-logo"
//                       src={selectedImage}
//                       alt="Kitchen Logo"
//                       className="relative m-auto max-w-sm max-h-32"
//                   />) : (<>
//                   <UploadIcon className="text-primary-200 hover:text-primary-300 m-auto" />
//                   Choose File 
//                   </>)
//             }
//             <input 
//               {...register("image_url")}
//               type="file" 
//               className="invisible" 
//               onChange={handleImageChange}
//             />
//           </div>
//           {errors.image_url && <span className="text-red-500 text-sm font-open-sans">{String(errors.image_url?.message)}</span>}
//         </label>

//         <button
//           type="submit"
//           className="buttonsPrimary h-10 text-xl"
//           data-testid="submit-button"
//         >
//           Add
//         </button>
//       </form>
//     </div>
//   );
// };

type AddMemberFormProps = {
  isOpen: boolean
  handleClose: () => void;
  handleAddMember: (kitchen: DSCommunitySchema | {}) => void;
  
};

const AddMemberForm: React.FC<AddMemberFormProps> = (props) => {
  return <CreateMembership isOpen={props.isOpen} handleClose={props.handleClose}/>;
};

type AddReservationFormProps = {
  handleClose: () => void;
  isOpen?: boolean
  resourceId: string
}

export const AddReservationForm: React.FC<AddReservationFormProps> = (props) => {
  return <StartKitchenBooking isOpen={props.isOpen} handleClose={props.handleClose} resourceId={props.resourceId} />;
};


type DeleteDataItemType = {
  id: string
  item_name: string
  date?: string
  name?: string
}

type DeleteConfirmationFormProps = {
  dataName: string
  data: DeleteDataItemType
  handleClose: () => void;
  handleDelete: (dataId: string) => void;
};

const DeleteConfirmationForm: React.FC<DeleteConfirmationFormProps> = (props) => {
  let { dataName, data, handleClose, handleDelete } = props;

  useEffect(() => {}, [dataName, data]);

  var deletionItemName = ''
  if (dataName === 'resource' || data?.name) {
    deletionItemName = data?.name ? ` ${data.name}` : ''
  } else if (data?.item_name || data?.date) {
    deletionItemName = data?.item_name ? ` ${data.item_name}` : ((data?.date) ? ` ${moment(data.date).format('ddd MMM DD, YYYY')}` : '')
  }

  return (
    <div className="flex flex-col container w-full">
      <h1 className="text-[24px] text-black font-bold font-raleway">
        Delete {dataName ? capitalizeWords(dataName) : 'Resource'}?
      </h1>
      <div className="text-[16px] w-full text-[#333333] my-2 font-open-sans">
        Are you sure you want to delete the {dataName ? dataName.toLowerCase(): 'resource'}:<b>{deletionItemName}</b>? This action is permanent and cannot be undone.
      </div>
      <div className="flex flex-row justify-between w-full gap-x-1 my-2">
        <button
          className="w-1/2 font-raleway font-semibold text-center text-[#333333] bg-white border-[#E9E9E9] border-[1px] rounded "
          onClick={handleClose}
        >
          No, Cancel
        </button>
        <button
          className="w-1/2 h-10 text-center text-white bg-[#D42620] rounded-md font-raleway font-semibold"
          onClick={()=>handleDelete(data.id)}
          data-testid="delete-button"
        >
          Yes, Confirm
        </button>
      </div>
    </div>
  );
};

type NewMessageFormProps = {
  username: string
  handleClose: () => void;
};

const NewMessageForm: React.FC<NewMessageFormProps> = (props) => {
  const navigate = useNavigate();
  const { handleClose, username } = props;
  // const { currentChatMember } = useMemberState();
  const { memberships: membershipsOnly } = useLoadMemberships();
  const { membershipRequests } = useLoadRequests();

  let memberships = membershipsOnly
  if (membershipRequests){
    memberships = membershipsOnly ? [...membershipsOnly, ...membershipRequests] : membershipRequests
  }

  const { switchMember } = useLoadMessages();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [imageError, setImageError] = useState(false);

  const [selectedMember, setSelectedMember] = useState<MembershipSchema | null>(null);
  const [message, setMessage] = useState("");


  useEffect(() => {
    const currentMember = memberships?.find((member: MembershipSchema) => username && member.username === username);
    if (currentMember) {
      setSelectedMember(currentMember as MembershipSchema);
    }
  }, [username, memberships]);

  // Filtered members for search
  const filteredMembers = memberships?.filter((member: any) => {
    const fullName = `${member.first_name || ''} ${member.last_name || ''}`.toLowerCase();
    return fullName.includes(search.toLowerCase());
  });

  // Handle member select
  const handleSelectMember = (member: any) => {
    setSelectedMember(member);
    setDropdownOpen(false);
  };

  // Handle send message
  const handleSendMessage = () => {
    if (selectedMember?.uid && message.trim()) {
      switchMember(selectedMember.uid);
      // Here you would send the message (not implemented in this snippet)
      handleClose();
      handleSubmitMessage();
      navigate('/messages')
    }
  };

  var { communityId, messageThread, updateMessageThread } = useMemberState();
  const { sendMessage } = useSendMessage();

  const handleSubmitMessage = async () => {
    if (selectedMember?.uid && message.trim()) {
      let messages: MessageSchema[] = [
        ...messageThread,
        {
          content: message,
          type: "sent",
          read_status: false,
          username: selectedMember.username,
          uid: selectedMember.uid,
          community_id: communityId,
          created: new Date().toISOString(),
        },
      ];
      updateMessageThread(messages);
      sendMessage(selectedMember.uid, message);
      setMessage("");
    }
  };

  // Avatar rendering
  const renderAvatar = (member: any) => {
    if (member.avatar && !imageError) {
      return (
        <img
          src={member.avatar}
          alt="avatar"
          className="w-8 h-8 rounded-full object-fit object-center"
          onError={() => setImageError(true)}
        />
      );
    }
    return (
      <UserAvatar
        firstName={member?.first_name}
        lastName={member?.last_name}
        username={member?.username}
        email={member?.email}
        size="sm"
      />
    );
  };

  // Full name rendering
  const getFullName = (member: any) =>
    `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.username;

  return (
    <div className="overflow-y-auto flex-1 py-4 px-8">
        {/* Close button */}
        <span onClick={handleClose} className="ml-auto w-full flex cursor-pointer">
            <Cancel className="text-[#A1A1A1] hover:text-[#777] h-[18px] w-[18px] ml-auto" />
        </span>
        {/* Title */}
        <h2 className="text-[18px] font-semibold text-gray-900 text-left font-raleway border-b-[1px] border-[#E9E9E9] pb-2">Send a Message</h2>
        {/* Member Dropdown */}
        <div className="my-4">
          <label className="block text-sm font-raleway font-medium text-gray-700 mb-1">Member</label>
          <div className="relative font-open-sans">
            {/* Collapsed: show selected member */}
            {!dropdownOpen && selectedMember ? (
              <button
                className="w-full flex items-center gap-3 px-4 py-2 bg-gray-100 rounded-lg border border-gray-200 text-left focus:outline-none"
                onClick={() => setDropdownOpen(true)}
              >
                {renderAvatar(selectedMember)}
                <span className="font-medium text-gray-900">{getFullName(selectedMember)}</span>
                <svg className="ml-auto w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
              </button>
            ) : (
              // Expanded: show dropdown
              <>
                <button
                  className="w-full flex items-center gap-3 px-4 py-2 bg-gray-100 rounded-lg border border-gray-200 text-left focus:outline-none"
                  onClick={() => setDropdownOpen((v) => !v)}
                >
                  <span className="text-gray-400">Select member</span>
                  <svg className="ml-auto w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </button>
                {dropdownOpen && (
                  <div className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    <div className="p-2">
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-200 rounded mb-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                        placeholder="Search member name"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <ul>
                      {filteredMembers && filteredMembers.length > 0 ? (
                        filteredMembers.map((member: any) => (
                          <li
                            key={member.username}
                            className="flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSelectMember(member)}
                          >
                            {renderAvatar(member)}
                            <span className="font-medium text-gray-900">{getFullName(member)}</span>
                          </li>
                        ))
                      ) : (
                        <li className="px-4 py-2 text-gray-400">No members found</li>
                      )}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {/* Message Textarea */}
        <div className="mb-6">
          <label className="block font-raleway font-medium text-sm text-gray-700 mb-1">Message</label>
          <textarea
            className="w-full font-open-sans min-h-[80px] px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200 placeholder:text-[#808080]/50 resize-none"
            placeholder="Enter your message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>
        {/* Send Button */}
        <button
          className="w-full font-raleway bg-[#013847] hover:bg-[#014D52] text-white text-lg font-medium py-3 rounded-lg transition disabled:opacity-50"
          onClick={handleSendMessage}
          disabled={!selectedMember || !message.trim()}
        >
          Send Message
        </button>
    </div>
   
  );
};





type ResourceInfoFormSection = 'full' | 'core' | 'funding_listing' | 'funding_partner' | 'partner_setup'

type ResourceInfoFormProps = {
  resource: ResourceSchema
  // currentResource: ResourceSchema
  resourceType: ResourceType
  updateResourceData?: (resource: ResourceSchema, resourceOptionTypes?: ResourceOptionsPropsType) => void
  // setResource: React.Dispatch<React.SetStateAction<ResourceSchema>>
  updateResourceOptions: (updateData: ResourceOptionsPropsType) => void
  section?: ResourceInfoFormSection
}
export const ResourceInfoForm: React.FC<ResourceInfoFormProps> = ({
  resourceType,
  resource,
  updateResourceData,
  updateResourceOptions,
  section = 'full',
}) => {
  // const zipCodeRegex = /^\d{5}(-\d{4})?$/;

  const [currentResource, setCurrentResource] = useState<ResourceSchema>(resource)
  const [currentResourceOptions, setResourceOptions] = useState<ResourceOptionType>(currentResource?.resource_options as ResourceOptionType);
  // const [openFloorPlanMapper, setOpenFloorPlanMapper] = useState<boolean>(false);

  // console.log("resource?.resource_options", resource?.resource_options)
  // console.log("currentResourceOptions", currentResourceOptions)

  const [selectedDays, setSelectedDays] = useState<selectedDaysType>({} as selectedDaysType)
  const [customAvailabilityModal, setCustomAvailabilityModal] = useState<boolean>(false)
  const [blockedTimes, setBlockedTimes] = useState<ClosedDatesType[]>(resource?.resource_options?.closed_dates || []);
  // const [selectedDates, setSelectedDates] = useState<Date[]>([]); // single or range
  // const [timeSlots, setTimeSlots] = useState<{ start: string; end: string }[]>([{ start: "08:00", end: "08:00" }]);
  // const [calendarMonth, setCalendarMonth] = useState(() => {
  //   const now = new Date();
  //   return new Date(now.getFullYear(), now.getMonth(), 1);
  // });
  const currentResourceName = currentResource?.name ? currentResource?.name : ''

  // console.log("currentResourceOptions", currentResourceOptions)
  // console.log("\nblockedTimes", blockedTimes)
  // console.log("\nresource?.resource_options?.closed_date", resource?.resource_options?.closed_dates)

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;

    // Handle checkbox separately so we can rely on the `checked` property that React provides
    if (type === "checkbox") {
      const { checked } = e.target as HTMLInputElement;
      setCurrentResource((prev) => ({
        ...prev,
        [name]: checked,
      }));
      return;
    }

    if (type === "number") {
      // const { value } = e.target;
      if (name === "length" || name === "width" || name === "max_capacity" || name === "buffer_time") {
        setResourceOptions((prev) => ({
          ...prev,
          floor_plan: {
            ...(prev?.floor_plan || {}),
            [name]: value === "" ? undefined : Number(value) as number,
          } as FloorPlanSchema,
        } as ResourceOptionType));
      } else {
        setCurrentResource((prev) => ({
          ...prev,
          [name]: value === "" ? undefined : Number(value),
        }));
      }
      return;
    }

    // Handle inputs/selects that use the `value` attribute
    // const { value } = e.target;
    if (name) {
      setCurrentResource((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  }

  const handleSave = () => {
    if (!areObjectsEqual(currentResource || {}, resource || {})){
      handleSaveChanges()
    }
    
    if (!areObjectsEqual(currentResourceOptions || {}, resource?.resource_options || {})){
      handleOptionsSaveChanges()
    }
  }

  const handleSaveChanges = () => {
    var changes = getChangedAttributes<ResourceSchema>(resource as ResourceSchema, currentResource as ResourceSchema);
    // console.log("changes", changes)
    if (Object.keys(changes).length > 0 && updateResourceData !== undefined){
      updateResourceData(changes)
    }
  }

  const handleOptionsSaveChanges = () => {   
    var optionsChanges = currentResourceOptions
    if (optionsChanges && optionsChanges?.last_updated){
      delete optionsChanges['last_updated']
    }
    const updateOptions = {
      resourceOptions: optionsChanges,
      selectedDays: selectedDays
    }
    // console.log("updateOptions", updateOptions)
    if (updateResourceOptions !== undefined){
      // console.log("updateResourceOptions updateOptions", updateOptions)
      updateResourceOptions(updateOptions)
    }
  }


  // const resourceName = currentResource?.name ? currentResource?.name : ''
  // const resourceDescription = currentResource?.description ? currentResource?.description : ''


  useEffect(
    () => {
      document.title = `${resourceType ? capitalizeString(resourceType) : "Resource"} | Details`;
      setCurrentResource(resource)
      setResourceOptions(resource?.resource_options as ResourceOptionType)
      const hasBlock = (val: any) => {
        if (!val) return false;
        // Handle array case (new format)
        if (Array.isArray(val)) {
          const hasBlock = val.length > 0 && val.some(blk => blk && blk.open_time && blk.close_time);
          // console.log('Has block (array):', hasBlock);
          return hasBlock;
        }
        // Handle single object case (legacy format)
        const blk = val[0];
        const hasBlock = !!(blk && blk.open_time && blk.close_time);
        // console.log('Has block (object):', hasBlock);
        return hasBlock;
      };
      const defaultSelectedDays = {
        sun: hasBlock(resource?.resource_options?.sun),
        mon: hasBlock(resource?.resource_options?.mon),
        tue: hasBlock(resource?.resource_options?.tue),
        wed: hasBlock(resource?.resource_options?.wed),
        thu: hasBlock(resource?.resource_options?.thu),
        fri: hasBlock(resource?.resource_options?.fri),
        sat: hasBlock(resource?.resource_options?.sat)
      }
      setSelectedDays(defaultSelectedDays)
      setBlockedTimes(resource?.resource_options?.closed_dates || [])
    }, // eslint-disable-next-line
    [resource, resourceType]
  );

  // console.log("resource?.resource_options?.sun", typeof resource?.resource_options?.sun)
  // console.log("selectedDays", selectedDays)

  /** Public price language derived from the resource rate plans plus any trial length. */
  const derivedPricingSummary = useMemo(
    () => buildFundingPricingSummary(currentResource?.plans, currentResource?.funding_profile?.trial_days),
    [currentResource?.plans, currentResource?.funding_profile?.trial_days]
  );

  // Keep the stored pricing summary aligned with the rates it is generated from.
  useEffect(() => {
    if (resourceType !== 'funding') return;
    setCurrentResource((prev) => {
      if (!prev?.funding_profile) return prev;
      if ((prev.funding_profile.pricing_summary || '') === derivedPricingSummary) return prev;
      return { ...prev, funding_profile: { ...prev.funding_profile, pricing_summary: derivedPricingSummary } };
    });
  }, [derivedPricingSummary, resourceType]);

  const hasCostOrAmount = ["documentation", "training", "expert", "funding"].includes(resourceType as ResourceType)

  const showCore = section === 'full' || section === 'core';
  const showFundingListing = resourceType === 'funding' && (section === 'full' || section === 'funding_listing');
  const showFundingPartner = resourceType === 'funding'
    && currentResource?.funding_profile?.kind === 'financial_intelligence'
    && (section === 'full' || section === 'funding_partner');
  const showGenericPartnerSetup = resourceType !== 'funding'
    && (section === 'full' || section === 'partner_setup');
  const isFundingCoreLayout = resourceType === 'funding' && section === 'core';
  const isSplitFundingSection = section === 'funding_listing' || section === 'funding_partner' || section === 'partner_setup';
  const isFundingSplitSection = (resourceType === 'funding' && section !== 'full') || section === 'partner_setup';
  const isAiriWheelsListing = isAiriWheelsResource(currentResource);
  const isFinancialIntelligence = currentResource?.funding_profile?.kind === 'financial_intelligence';
  const fundingFieldClass = 'border-[#E4EEF1] h-[51px] border-[1px] w-full rounded-lg';
  const fundingLabelClass = 'text-muted text-[14px] font-raleway block mb-1.5';
  const coreColumnClass = resourceType === 'kitchen'
    ? 'w-6/12'
    : (resourceType === 'funding' && section !== 'full' ? 'w-11/12' : 'w-11/12');
  const closeDateLabel = isFinancialIntelligence ? 'Listing close date' : 'Application close date';
  const closeDateTooltip = isFinancialIntelligence
    ? 'Optional. Last day members can start this partner offer. Leave empty to keep the listing open.'
    : 'Optional. Last day this offer accepts applications. Leave empty to keep the listing open.';

  const sectionHint: Record<ResourceInfoFormSection, string> = {
    full: 'View and edit your resource details.',
    core: 'Name, description, and location for this fund. Listing identity is also editable on Listing & eligibility.',
    funding_listing: 'Kind, eligibility, pricing, and pantry listing fields.',
    funding_partner: 'Partner signup, webhook, and API handoff URLs.',
    partner_setup: 'Partner signup, webhook, and fulfillment mode for this resource.',
  };

  const fundingListingFields = showFundingListing && (
    <div className={`flex flex-col bg-white my-6 p-6 w-11/12 mx-auto rounded-2xl border border-[#E4EEF1] ${section === 'full' ? 'w-full' : ''}`}>
      {section === 'funding_listing' && (
        <p className="mb-4 text-sm font-open-sans text-[#4D4D4D]/70">{sectionHint.funding_listing}</p>
      )}
      <p className="page-kicker">Funding offer</p>
      <p className="text-ink text-[16px] font-raleway mb-3 mt-1">Listing details</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <FieldLabelWithTooltip label="Kind" tooltip="Capital funds collect applications in HomeCook. Financial intelligence listings use partner handoff." />
          <select
            className="border-[#E4EEF1] h-[51px] border-[1px] w-full rounded-lg"
            value={currentResource?.funding_profile?.kind || 'capital'}
            onChange={(e) => setCurrentResource((prev) => ({
              ...prev,
              funding_profile: { ...(prev.funding_profile || { kind: 'capital', fulfillment_mode: 'application' }), kind: e.target.value as any, fulfillment_mode: e.target.value === 'financial_intelligence' ? 'api_handoff' : 'application' }
            }))}
          >
            <option value="capital">Capital</option>
            <option value="financial_intelligence">Financial intelligence</option>
          </select>
        </div>
        <div>
          <FieldLabelWithTooltip label="Subtype" tooltip="The funding category members see in pantry filters." />
          <select
            className="border-[#E4EEF1] h-[51px] border-[1px] w-full rounded-lg"
            name="resource_subtype"
            value={currentResource?.resource_subtype || ''}
            onChange={handleChange}
          >
            <option value="">Select type</option>
            {Object.values((currentResource?.funding_profile?.kind === 'financial_intelligence') ? FoodBusinessFinancialIntelligenceCategories : FoodBusinessFundingCategories).map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-muted text-[14px] font-raleway">Website</label>
          <input className="border-[#E4EEF1] h-[51px] border-[1px] w-full rounded-lg" name="website" value={currentResource?.website || ''} onChange={handleChange} />
        </div>
        <div>
          <FieldLabelWithTooltip label={closeDateLabel} tooltip={closeDateTooltip} />
          <input type="date" className="border-[#E4EEF1] h-[51px] border-[1px] w-full rounded-lg" name="end_time" value={(currentResource?.end_time || '').slice(0, 10)} onChange={handleChange} />
        </div>
        {isFinancialIntelligence && (
          <>
            <div className="sm:col-span-2">
              <FieldLabelWithTooltip
                label="Partner display name"
                tooltip="Public title shown on the pantry offer band. Defaults to the resource name when empty."
              />
              <input
                className="border-[#E4EEF1] h-[51px] border-[1px] w-full rounded-lg"
                value={currentResource?.funding_profile?.partner?.display_name || ''}
                onChange={(e) => setCurrentResource((prev) => ({
                  ...prev,
                  funding_profile: {
                    ...(prev.funding_profile as any),
                    partner: { ...(prev.funding_profile?.partner || {}), display_name: e.target.value },
                  },
                }))}
              />
            </div>
            <div className="sm:col-span-2">
              <FieldLabelWithTooltip label="Fulfillment mode" tooltip="How members receive this offer after they qualify: application, referral handoff, API handoff, or external trial." />
              <select
                className="border-[#E4EEF1] h-[51px] border-[1px] w-full rounded-lg"
                value={currentResource?.funding_profile?.fulfillment_mode || 'api_handoff'}
                onChange={(e) => setCurrentResource((prev) => ({
                  ...prev,
                  funding_profile: { ...(prev.funding_profile as any), fulfillment_mode: e.target.value as any }
                }))}
              >
                {FULFILLMENT_MODE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            {isAiriWheelsListing && (
            <div className="sm:col-span-2">
              <p className="text-muted text-[14px] font-raleway mb-2">Product wheels</p>
              <div className="flex flex-wrap gap-2">
                {FINANCIAL_WHEEL_OPTIONS.map((wheel) => {
                  const selected = (currentResource?.funding_profile?.wheels || []).includes(wheel.value);
                  return (
                    <FinancialWheelOption
                      key={wheel.value}
                      label={wheel.label}
                      checked={selected}
                      onChange={(checked) => setCurrentResource((prev) => {
                        const current = prev.funding_profile?.wheels || [];
                        const next = checked
                          ? [...current, wheel.value]
                          : current.filter((value) => value !== wheel.value);
                        return { ...prev, funding_profile: { ...(prev.funding_profile as any), wheels: next } };
                      })}
                    />
                  );
                })}
              </div>
            </div>
            )}
            <label className={`sm:col-span-2 inline-flex items-center gap-2 text-sm font-open-sans cursor-pointer ${
              currentResource?.funding_profile?.eligibility?.requires_active_sales !== false
                ? 'text-primary-300'
                : 'text-[#4D4D4D]'
            }`}>
              <input
                type="checkbox"
                checked={currentResource?.funding_profile?.eligibility?.requires_active_sales !== false}
                onChange={(e) => setCurrentResource((prev) => ({
                  ...prev,
                  funding_profile: {
                    ...(prev.funding_profile as any),
                    eligibility: { ...(prev.funding_profile?.eligibility || {}), requires_active_sales: e.target.checked }
                  }
                }))}
                className="h-4 w-4 rounded border-[#E4EEF1] text-primary-300 accent-primary-300"
              />
              Requires commercially active sales
            </label>
            <div className="sm:col-span-2">
              <label className="text-muted text-[14px] font-raleway">Eligibility notes</label>
              <textarea
                className="border-[#E4EEF1] border-[1px] w-full rounded-lg h-[80px]"
                value={currentResource?.funding_profile?.eligibility?.notes || ''}
                onChange={(e) => setCurrentResource((prev) => ({
                  ...prev,
                  funding_profile: {
                    ...(prev.funding_profile as any),
                    eligibility: { ...(prev.funding_profile?.eligibility || {}), notes: e.target.value }
                  }
                }))}
              />
            </div>
            <div className="sm:col-span-2">
              <FieldLabelWithTooltip label="Pricing summary" tooltip="Generated from this resource's rate plans and trial days. Update the rates to change it." />
              <input
                disabled
                readOnly
                data-testid="funding-pricing-summary"
                placeholder="Add a rate plan to generate pricing"
                className="border-[#E4EEF1] h-[51px] border-[1px] w-full rounded-lg bg-[#F7FAFB] text-[#4D4D4D] cursor-not-allowed"
                value={derivedPricingSummary}
              />
            </div>
            <div>
              <FieldLabelWithTooltip label="Trial days" tooltip="Optional partner trial length described to members. This does not create a HomeCook subscription." />
              <input
                type="number"
                min={0}
                className="border-[#E4EEF1] h-[51px] border-[1px] w-full rounded-lg"
                value={currentResource?.funding_profile?.trial_days || ''}
                onChange={(e) => setCurrentResource((prev) => ({
                  ...prev,
                  funding_profile: { ...(prev.funding_profile as any), trial_days: Number(e.target.value) || undefined }
                }))}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );

  const fundingPartnerFields = showFundingPartner && (
    <div className="flex flex-col bg-white my-3 p-6 w-11/12 mx-auto rounded-2xl border border-[#E4EEF1]">
      {section === 'funding_partner' && (
        <p className="mb-4 text-sm font-open-sans text-[#4D4D4D]/70">{sectionHint.funding_partner}</p>
      )}
      <p className="page-kicker">Partner integration</p>
      <p className="text-ink text-[16px] font-raleway mb-3 mt-1">Handoff URLs</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <FieldLabelWithTooltip label="Signup URL" tooltip="Partner destination members open after a consented intro. Same field as Settings → AiriWheels when this listing is bound." />
          <input
            className="border-[#E4EEF1] h-[51px] border-[1px] w-full rounded-lg"
            value={currentResource?.funding_profile?.partner?.signup_url || ''}
            onChange={(e) => setCurrentResource((prev) => ({
              ...prev,
              funding_profile: { ...(prev.funding_profile as any), partner: { ...(prev.funding_profile?.partner || {}), signup_url: e.target.value } }
            }))}
          />
        </div>
        <div>
          <FieldLabelWithTooltip label="Webhook URL" tooltip="HTTPS endpoint HomeCook calls with signed partner events. Same endpoint as Settings → AiriWheels. Secrets stay on the server." />
          <input
            className="border-[#E4EEF1] h-[51px] border-[1px] w-full rounded-lg"
            value={currentResource?.funding_profile?.partner?.webhook_url || ''}
            onChange={(e) => setCurrentResource((prev) => ({
              ...prev,
              funding_profile: { ...(prev.funding_profile as any), partner: { ...(prev.funding_profile?.partner || {}), webhook_url: e.target.value } }
            }))}
          />
        </div>
      </div>
    </div>
  );

  const genericPartnerSetupFields = showGenericPartnerSetup && (
    <div className="flex flex-col bg-white my-3 p-6 w-11/12 mx-auto rounded-2xl border border-[#E4EEF1]" data-testid="generic-partner-setup">
      {section === 'partner_setup' && (
        <p className="mb-4 text-sm font-open-sans text-[#4D4D4D]/70">{sectionHint.partner_setup}</p>
      )}
      <p className="page-kicker">Partner integration</p>
      <p className="text-ink text-[16px] font-raleway mb-3 mt-1">Handoff & fulfillment</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <FieldLabelWithTooltip label="Fulfillment mode" tooltip="How members receive this partner offer after they qualify." />
          <select
            className="border-[#E4EEF1] h-[51px] border-[1px] w-full rounded-lg"
            value={currentResource?.partner_handoff?.fulfillment_mode || 'api_handoff'}
            onChange={(e) => setCurrentResource((prev) => ({
              ...prev,
              partner_handoff: {
                ...(prev.partner_handoff || {}),
                fulfillment_mode: e.target.value as any,
                partner: { ...(prev.partner_handoff?.partner || {}) },
              },
            }))}
          >
            {FULFILLMENT_MODE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div>
          <FieldLabelWithTooltip label="Signup URL" tooltip="Partner destination members open after a consented intro." />
          <input
            className="border-[#E4EEF1] h-[51px] border-[1px] w-full rounded-lg"
            value={currentResource?.partner_handoff?.partner?.signup_url || ''}
            onChange={(e) => setCurrentResource((prev) => ({
              ...prev,
              partner_handoff: {
                ...(prev.partner_handoff || {}),
                partner: { ...(prev.partner_handoff?.partner || {}), signup_url: e.target.value },
              },
            }))}
          />
        </div>
        <div>
          <FieldLabelWithTooltip label="Webhook URL" tooltip="HTTPS endpoint HomeCook calls with signed partner events. Secrets stay on the server." />
          <input
            className="border-[#E4EEF1] h-[51px] border-[1px] w-full rounded-lg"
            value={currentResource?.partner_handoff?.partner?.webhook_url || ''}
            onChange={(e) => setCurrentResource((prev) => ({
              ...prev,
              partner_handoff: {
                ...(prev.partner_handoff || {}),
                partner: { ...(prev.partner_handoff?.partner || {}), webhook_url: e.target.value },
              },
            }))}
          />
        </div>
        {isContactBuilderResource(currentResource) && (
          <>
            <div className="sm:col-span-2">
              <FieldLabelWithTooltip label="Platform URL" tooltip="DSCVRY page that opens in a new tab when a contact list is handed off. The project id is attached. This is not the Airtable Business Hub URL — pantry members enter their own Airtable hub URL and API key when they choose Load venture." />
              <input
                className="border-[#E4EEF1] h-[51px] border-[1px] w-full rounded-lg"
                placeholder={DSCVRY_REGISTER_URL}
                value={currentResource?.partner_handoff?.partner?.hub_url || ''}
                onChange={(e) => setCurrentResource((prev) => ({
                  ...prev,
                  partner_handoff: {
                    ...(prev.partner_handoff || {}),
                    partner: { ...(prev.partner_handoff?.partner || {}), hub_url: e.target.value },
                  },
                }))}
                data-testid="dscvry-platform-url"
              />
              <p className="mt-1 text-xs font-open-sans text-[#4D4D4D]/70">Opens on handoff (DSCVRY app). Not used for Airtable Load. Leave empty to use the DSCVRY founder app.</p>
            </div>
            <DscvryWebhookConnect
              resourceId={currentResource?.id}
              resourceCommunityId={currentResource?.community_id}
              signupUrl={currentResource?.partner_handoff?.partner?.signup_url}
              webhookUrl={currentResource?.partner_handoff?.partner?.webhook_url}
            />
          </>
        )}
      </div>
    </div>
  );


  return (
  <>
    <div className={`flex flex-row bg-white w-full border-t-[#E6E6E6] border-t-[1px] ${isFundingSplitSection ? 'h-[50px] justify-end' : 'h-[50px]'}`}>
      {!isFundingSplitSection && (
        <p className="mx-[24px] my-auto font-open-sans text-[16px] text-[#4D4D4D]/70">
          {sectionHint[section]}
        </p>
      )}
      {
        (!areObjectsEqual(currentResourceOptions || {}, resource?.resource_options || {}) || !areObjectsEqual(currentResource || {}, resource || {})) ? (
        <div className="flex ml-auto mx-[24px] h-auto font-open-sans">
          <button 
              className="text-primary-200 font-open-sans h-[43px] px-2 border-[1px] border-[#E9E9E9] ml-auto rounded-lg my-auto text-[16px]"
              onClick={handleSave}
            >
              Save Changes
          </button>
          {/* Add Cancel button and reset to api data */}
        </div>
        ) : (
          <></>
        )
      }
    </div> 
    <div className={`flex ${isSplitFundingSection || isFundingCoreLayout ? 'flex-col' : 'flex-row'}`}>
      {showCore && (
      <div className={`flex flex-col ${coreColumnClass} mx-auto font-open-sans h-auto`}>
        {isFundingCoreLayout ? (
          <div className="my-6 w-full rounded-2xl border border-[#E4EEF1] bg-white p-6 space-y-6">
            <p className="text-sm font-open-sans text-[#4D4D4D]/70">{sectionHint.core}</p>
            <div className="flex items-center gap-3">
              <label htmlFor="requires_membership" className={`${fundingLabelClass} mb-0`}>Requires Membership?</label>
              <input
                id="requires_membership"
                type="checkbox"
                name="requires_membership"
                checked={currentResource?.requires_membership || false}
                onChange={handleChange}
                className="h-[18px] w-[18px] rounded-md border-[#E4EEF1] text-primary-300 accent-primary-300"
              />
            </div>
            <>
            <div>
              <label htmlFor="name" className={fundingLabelClass}>{capitalizeString(resourceType)} Name</label>
              <input id="name" name="name" value={currentResourceName} onChange={handleChange} className={fundingFieldClass} />
            </div>
            <div className={`grid gap-4 ${currentResource?.funding_profile?.kind !== 'financial_intelligence' ? 'sm:grid-cols-[2fr_1fr]' : ''}`}>
              <div>
                <label htmlFor="description" className={fundingLabelClass}>{capitalizeString(resourceType)} Description</label>
                <textarea id="description" name="description" value={currentResource?.description ?? ''} onChange={handleChange} className="border-[#E4EEF1] h-[120px] border-[1px] w-full rounded-lg" />
              </div>
              {currentResource?.funding_profile?.kind !== 'financial_intelligence' && (
                <div>
                  <label htmlFor="total_amount" className={fundingLabelClass}>{capitalizeString(resourceType)} Amount ($)</label>
                  <input id="total_amount" name="total_amount" step={0.10} min={0} type="number" placeholder="0.00" value={typeof currentResource?.total_amount === 'number' ? currentResource.total_amount.toFixed(2) : currentResource?.total_amount || 0} onChange={handleChange} className={fundingFieldClass} />
                </div>
              )}
            </div>
            <div className="space-y-4">
              <div>
                <label htmlFor="address" className={fundingLabelClass}>Address</label>
                <input id="address" name="address" value={currentResource?.address ?? ''} onChange={handleChange} className={fundingFieldClass} />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label htmlFor="city" className={fundingLabelClass}>City</label>
                  <input id="city" type="text" placeholder="Enter City" className={fundingFieldClass} name="city" value={currentResource?.city ? currentResource?.city : ''} onChange={handleChange} />
                </div>
                <div>
                  <label htmlFor="state" className={fundingLabelClass}>State</label>
                  <select id="state" name="state" value={currentResource?.state ?? ''} className={fundingFieldClass} onChange={handleChange}>
                    <option value="">-- Select State --</option>
                    <option value="nationwide">All</option>
                    {(usStates && usStates.length > 0) && usStates.map((state, i) => (
                      <option value={state} key={i}>{USStatesEnum[state]?.fullName}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="zipcode" className={fundingLabelClass}>Zipcode</label>
                  <input id="zipcode" type="text" placeholder="Enter Zipcode" className={fundingFieldClass} name="zipcode" value={currentResource?.zipcode ? currentResource?.zipcode : ''} onChange={handleChange} />
                </div>
              </div>
            </div>
              </>
          </div>
        ) : (
        <>
        <div className="flex flex-col bg-white my-3 p-3 w-full rounded-lg">
          <div className="flex flex-row items-center justify-start">
            <label htmlFor="requires_membership" className="text-[#808080] text-[15px] font-medium font-raleway m-2 text-left">Requires Membership?</label>
            <input 
              id="requires_membership"
              type="checkbox" 
              name="requires_membership" 
              checked={currentResource?.requires_membership || false} 
              onChange={handleChange} 
              className="border-[#E9E9E9] text-primary-300 m-2 border-[1px] rounded-md h-[18px] w-[18px]"  
            />
          </div>
        </div>

        <div className="flex flex-col bg-white my-3  h-[100px] w-full rounded-lg">
          <div className="m-[24px] my-auto">
            <label htmlFor="name" className="text-[#808080] text-[16px] font-raleway my-2">{capitalizeString(resourceType)} Name</label>
            <input id="name" name="name" value={currentResourceName} onChange={handleChange} className="border-[#E9E9E9] h-[51px] border-[1px] w-full rounded-md"  />
          </div>
        </div>

        <div className="flex flex-row bg-white my-3 w-full rounded-lg">
          <div className={`m-[24px] ${(hasCostOrAmount) ? 'w-2/3' : 'w-full '}`}>
            <label htmlFor="description" className="text-[#808080] text-[16px] font-raleway my-2">{capitalizeString(resourceType)} Description</label>
            <textarea id="description" name="description" value={currentResource?.description ?? ''} onChange={handleChange} className={`border-[#E9E9E9] h-[120px] border-[1px] w-full rounded-md`} />
          </div>

          { 
            (hasCostOrAmount && resourceType === "funding" && resource?.funding_profile?.kind !== 'financial_intelligence') && (
              <div className="m-[24px] w-1/3">
                <p className="text-[#808080] text-[16px] font-raleway my-2">{capitalizeString(resourceType)} Amount ($)</p>
                <input name="total_amount" step={0.10} min={0} type="number" placeholder="0.00" value={typeof currentResource?.total_amount === 'number' ? currentResource.total_amount.toFixed(2) : currentResource?.total_amount || 0} onChange={handleChange} className="border-[#E9E9E9] h-[51px] border-[1px] w-full rounded-md"  />
              </div>
            )
          }
          { 
            (hasCostOrAmount && resourceType !== "funding") && (
              <div className="m-[24px] w-1/3">
                <p className="text-[#808080] text-[16px] font-raleway my-2">{capitalizeString(resourceType)} Cost ($)</p>
                <input name="total_amount" step={0.10} min={0} type="number" placeholder="0.00" value={typeof currentResource?.total_amount === 'number' ? currentResource.total_amount.toFixed(2) : currentResource?.total_amount || 0} onChange={handleChange} className="border-[#E9E9E9] h-[51px] border-[1px] w-full rounded-md"  />
              </div>
            )
          }
        </div>

        <div className="flex flex-col bg-white my-3 px-3 w-full rounded-lg">
          <div className={`m-[24px]`}>
            <label htmlFor="address" className="text-[#808080] text-[16px] font-raleway my-2">Address</label>
            <input id="address" name="address" value={currentResource?.address ?? ''} onChange={handleChange} className="border-[#E9E9E9] h-[51px] border-[1px] w-full rounded-md"  />
          </div>
          <div className='flex flex-row my-auto m-[24px]'>
            <div className='my-4 mr-1 w-1/3'>
              <label htmlFor="city" className="text-[#808080] text-[16px] font-raleway my-2">City</label>
              <input 
                id="city"
                type='text' 
                placeholder='Enter City' 
                className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
                name="city"
                value={currentResource?.city ? currentResource?.city : ''}
                onChange={handleChange}
              />
            </div>
            <div className='my-4 mr-1 w-1/3'>
              <label htmlFor="state" className="text-[#808080] text-[16px] font-raleway my-2">State</label>
              <select 
                id="state"
                name="state" 
                value={currentResource?.state ?? ''} 
                className="border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]"
                onChange={handleChange}
              >
                <option value="">-- Select State --</option>
                <option value="nationwide">All</option>
                {
                    (usStates && usStates.length > 0) && usStates.map((state, i) => (
                        <option value={state} key={i}>{USStatesEnum[state]?.fullName}</option>
                    ))
                }
              </select>
            </div>
            <div className='my-4 w-1/3'>
              <label htmlFor="zipcode" className="text-[#808080] text-[16px] font-raleway my-2">Zipcode</label>
              <input 
                id="zipcode"
                type='text' 
                placeholder='Enter Zipcode' 
                className='border-[#E9E9E9] border-[1px] w-full rounded-md h-[50px] text-[16px]'
                name="zipcode"
                value={currentResource?.zipcode ? currentResource?.zipcode : ''}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
        </>
        )}
      </div>
      )}

      {section === 'full' && resourceType === "funding" && (
        <div className="flex flex-col w-full">
          {fundingListingFields}
          {fundingPartnerFields}
        </div>
      )}
      {section === 'funding_listing' && fundingListingFields}
      {section === 'funding_partner' && fundingPartnerFields}
      {(section === 'partner_setup' || section === 'full') && genericPartnerSetupFields}

      { 
        (resourceType === "kitchen") && (
          <div className="flex flex-col w-5/12 mx-auto font-open-sans h-auto">
            <div className="flex flex-col bg-white my-3 h-auto w-full rounded-lg">
              <div className="m-[24px]">
                <div className="flex flex-row">
                  <p className="text-[#808080] text-[18px] font-raleway my-auto">Operating Hours</p>
                  <div className="ml-auto my-auto">
                    <button className="bg-white text-sm font-raleway border-0 text-primary-200 px-4 py-2 font-medium hover:text-primary-300" onClick={() => setCustomAvailabilityModal(true)}>
                      <AddCircle className="mr-1 my-auto" style={{ fontSize: 15 }} /> Add Custom Availability
                    </button>
                  </div>
                </div>
                <OperatingHoursList 
                  resourceOptions={currentResourceOptions} 
                  setResourceOptions={setResourceOptions} 
                  selectedDays={selectedDays}
                  setSelectedDays={setSelectedDays}
                />
              </div>
            </div>
            <div className="flex flex-col bg-white w-full rounded-lg mb-3">
              <div className="flex flex-row mt-[24px] mb-[8px]">
                <label className="text-[#808080] text-[18px] font-raleway mx-[24px] my-auto">Floor Plan</label>
                <div className="ml-auto my-auto">
                  <button className="bg-white text-sm font-raleway border-0 text-primary-200 px-4 py-2 font-medium hover:text-primary-300" onClick={() => null}>
                    <OpenInNew className="mr-1 my-auto" style={{ fontSize: 15 }} /> Open Floor Plan Mapper
                  </button>
                </div>
                {/* <InfoIcon className="h-3 w-3 my-auto mx-1 text-primary-300" /> */}
              </div>
              <div className="flex flex-row mb-[24px]">
                <div className="mx-[24px] w-1/2">
                  <label htmlFor="length" className="text-[#808080]/70 text-[14px] font-raleway my-2">Length (ft)</label>
                  <input
                    id="length"
                    name="length"
                    type="number"
                    min={0}
                    step={1}
                    placeholder="0"
                    value={currentResourceOptions?.floor_plan?.length ?? 0}
                    onChange={handleChange}
                    className="border-[#E9E9E9] h-[51px] border-[1px] w-full rounded-md"
                  />
                </div>
                <div className="mx-[24px] w-1/2">
                  <label htmlFor="width" className="text-[#808080]/70 text-[14px] font-raleway my-2">Width (ft)</label>
                  <input
                    id="width"
                    name="width"
                    type="number"
                    min={0}
                    step={1}
                    placeholder="0"
                    value={currentResourceOptions?.floor_plan?.width ?? 0}
                    onChange={handleChange}
                    className="border-[#E9E9E9] h-[51px] border-[1px] w-full rounded-md"
                  />
                </div>
              </div>

              <div className="flex flex-row mb-[24px]">
                <div className="mx-[24px] w-1/2">
                  <label htmlFor="max_capacity" className="text-[#808080]/70 text-[14px] font-raleway my-2">Max Capacity (people)</label>
                  <input
                    id="max_capacity"
                    name="max_capacity"
                    type="number"
                    min={0}
                    step={1}
                    placeholder="0"
                    value={
                      currentResourceOptions?.floor_plan != null
                        ? (currentResourceOptions.floor_plan.max_capacity ?? 0)
                        : (currentResource?.capacity ?? 0)
                    }
                    onChange={handleChange}
                    className="border-[#E9E9E9] h-[51px] border-[1px] w-full rounded-md"
                  />
                </div>
                <div className="mx-[24px] w-1/2">
                  <label htmlFor="buffer_time" className="text-[#808080]/70 text-[14px] font-raleway my-2">Buffer Time (minutes)</label>
                  <input
                    id="buffer_time"
                    name="buffer_time"
                    type="number"
                    min={0}
                    step={1}
                    placeholder="0"
                    value={
                      currentResourceOptions?.floor_plan != null
                        ? (currentResourceOptions.floor_plan.buffer_time ?? 0)
                        : (currentResource?.buffer_time ?? 0)
                    }
                    onChange={handleChange}
                    className="border-[#E9E9E9] h-[51px] border-[1px] w-full rounded-md"
                  />
                </div>
              </div>
            </div>
            {/* <div className="flex flex-row bg-white w-full rounded-lg">
              <div className="m-[24px] w-1/2">
                <label htmlFor="capacity" className="text-[#808080] text-[16px] font-raleway my-2">Capacity (people)</label>
                <input
                  id="capacity"
                  name="capacity"
                  type="number"
                  min={0}
                  step={1}
                  placeholder="0"
                  value={currentResource?.capacity ?? ""}
                  onChange={handleChange}
                  className="border-[#E9E9E9] h-[51px] border-[1px] w-full rounded-md"
                />
              </div>
              <div className="m-[24px] w-1/2">
                <label htmlFor="buffer_time" className="text-[#808080] text-[16px] font-raleway my-2">Buffer Time (minutes)</label>
                <input
                  id="buffer_time"
                  name="buffer_time"
                  type="number"
                  min={0}
                  step={1}
                  placeholder="0"
                  value={currentResource?.buffer_time ?? ""}
                  onChange={handleChange}
                  className="border-[#E9E9E9] h-[51px] border-[1px] w-full rounded-md"
                />
              </div>
            </div> */}
          </div>
        )
      }

    </div>
    {
      customAvailabilityModal && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center`}>
        <div className="fixed inset-0 bg-white bg-opacity-90 z-40" onClick={() => setCustomAvailabilityModal(false)}></div>
    
          <dialog
            data-testid="view-login-modal"
            className="z-50 top-20 sm:w-[478px] h-auto bg-[#fff] p-4 rounded-xl shadow-[#00000029] shadow-[0_0px_8px_rgba(0,0,0,0.16)]"
            open={customAvailabilityModal}
            onClose={() => setCustomAvailabilityModal(false)}
          >
      
            <CustomAvailabilityForm blockedTimes={blockedTimes} setResourceOptions={setResourceOptions} setBlockedTimes={setBlockedTimes} isOpen={customAvailabilityModal} setIsOpen={setCustomAvailabilityModal} />
            
          </dialog>
        </div>
      )
    }
  </>
  )
}

type CustomAvailabilityFormProps = {
  isOpen: boolean
  blockedTimes: ClosedDatesType[]
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  setBlockedTimes: React.Dispatch<React.SetStateAction<ClosedDatesType[]>>
  setResourceOptions: React.Dispatch<React.SetStateAction<ResourceOptionType>>
}

const CustomAvailabilityForm: React.FC<CustomAvailabilityFormProps> = ({ blockedTimes, setIsOpen, setBlockedTimes, setResourceOptions }) => {
  // State for step: 0 = initial, 1 = date/time picker, 2 = review
  const [step, setStep] = useState(2);
  // State for current selection
  const [selectedDates, setSelectedDates] = useState<Date[]>([]); // single or range
  const [timeSlots, setTimeSlots] = useState<ClosedTimeBlockType[]>([{ start_time: "08:00", end_time: "20:00" }]);
  const [timeError, setTimeError] = useState<string>("");
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  // Helpers for formatting
  const formatDate = (date: Date) => moment(date).format('ddd, MMM D, YYYY');
  const formatTime = (time: string) => moment(time, 'HH:mm').format('hh:mmA');
  
  const formatDateRange = (dates: Date[]) => {
    if (dates.length === 1) return formatDate(dates[0]);
    if (dates.length > 1) {
      const start_time = moment(dates[0]);
      const end_time = moment(dates[dates.length - 1]);
      if (start_time.isSame(end_time, 'day')) {
        return start_time.format('ddd, MMM D, YYYY');
      } else if (start_time.isSame(end_time, 'month') && start_time.isSame(end_time, 'year')) {
        return `${start_time.format('MMM D')} - ${end_time.format('D, YYYY')}`;
      } else if (start_time.isSame(end_time, 'year')) {
        return `${start_time.format('MMM D')} - ${end_time.format('MMM D, YYYY')}`;
      } else {
        return `${start_time.format('MMM D, YYYY')} - ${end_time.format('MMM D, YYYY')}`;
      }
    }
    return "";
  };

  // Calendar logic
  const getDaysInMonth = (month: Date) => {
    const year = month.getFullYear();
    const monthIdx = month.getMonth();
    // const firstDay = new Date(year, monthIdx, 1);
    const lastDay = new Date(year, monthIdx + 1, 0);
    const days = [];
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push(new Date(year, monthIdx, d));
    }
    return days;
  };
  const days = getDaysInMonth(calendarMonth);
  const firstDayOfWeek = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1).getDay();

  // Handlers
  const handleAddBlockedTime = () => {
    setStep(1);
    setSelectedDates([]);
    setTimeSlots([{ start_time: "08:00", end_time: "20:00" }]);
  };
  const handleDateClick = (date: Date) => {
    if (selectedDates.length === 0) {
      setSelectedDates([date]);
    } else if (selectedDates.length === 1) {
      // Range selection
      if (date < selectedDates[0]) {
        setSelectedDates([date, selectedDates[0]]);
      } else if (date > selectedDates[0]) {
        setSelectedDates([selectedDates[0], date]);
      } else {
        setSelectedDates([date]);
      }
    } else {
      setSelectedDates([date]);
    }
  };
  const handleTimeChange = (idx: number, field: "start_time" | "end_time", value: string) => {
    const newTimeSlots = [...timeSlots];
    const time = moment(value, "HH:mm");
    const minutes = time.minutes();
    
    // Round to nearest 30 minutes
    if (minutes % 30 !== 0) {
      const roundedMinutes = Math.round(minutes / 30) * 30;
      time.minutes(roundedMinutes);
      value = time.format("HH:mm");
    }
    
    newTimeSlots[idx] = { ...newTimeSlots[idx], [field]: value };
    
    const start = moment(newTimeSlots[idx].start_time, "HH:mm");
    const end = moment(newTimeSlots[idx].end_time, "HH:mm");
    
    if (end.isSameOrBefore(start)) {
      setTimeError("End time must be after start time");
      return;
    }
    
    setTimeError("");
    setTimeSlots(newTimeSlots);
  };
  const handleAddTimeSlot = () => {
    setTimeSlots((slots) => [...slots, { start_time: "08:00", end_time: "20:00" }]);
  };
  const handleRemoveTimeSlot = (idx: number) => {
    setTimeSlots((slots) => slots.filter((_, i) => i !== idx));
  };
  const handleApply = () => {
    if (selectedDates.length > 0 && timeSlots.length > 0) {
      if (isOverlapping(selectedDates, timeSlots)) {
        setTimeError("This time slot overlaps with an existing blocked time");
        return;
      }
      
      setBlockedTimes((prev) => [
        ...prev,
        { dates: [...selectedDates], times: [...timeSlots] },
      ]);

      setResourceOptions((prev) => ({
        ...prev,
        closed_dates: [...(prev.closed_dates || []), { dates: [...selectedDates], times: [...timeSlots] }],
      }));
   
      setStep(2);   
    }
  };
  const handleRemoveBlocked = (idx: number) => {
    setBlockedTimes((prev) => prev.filter((_, i) => i !== idx));
    setResourceOptions((prev) => ({
      ...prev,
      closed_dates: prev.closed_dates?.filter((_, i) => i !== idx),
    }));
  };
  const handleBack = () => {
    setStep(2);
  };
  const handleCancel = () => {
    setIsOpen(false);
  };

  const isOverlapping = (newDates: Date[], newTimes: ClosedTimeBlockType[]): boolean => {
    if (!blockedTimes) return false;
    
    return blockedTimes.some(block => {
      // Check if dates overlap
      const hasDateOverlap = newDates.some(newDate => 
        block.dates.some(existingDate => 
          moment(newDate).isSame(existingDate, 'day')
        )
      );
      
      if (!hasDateOverlap) return false;
      
      // Check if times overlap
      return newTimes.some(newTime => 
        block.times.some(existingTime => {
          const newStart = moment(newTime.start_time, "HH:mm");
          const newEnd = moment(newTime.end_time, "HH:mm");
          const existingStart = moment(existingTime.start_time, "HH:mm");
          const existingEnd = moment(existingTime.end_time, "HH:mm");
          
          return (newStart.isBefore(existingEnd) && newEnd.isAfter(existingStart));
        })
      );
    });
  };

  // UI
  return (
    <div className="flex flex-col w-full max-w-md">
      {/* Step 0: Add Blocked Time */}
      {/* {step === 0 && (
        <>
          <h2 className="text-lg font-bold font-raleway mb-1 mt-2">Add Custom Availability</h2>
          <p className="text-[#4D4D4D] font-open-sans text-sm mb-6">Set specific dates and times for your business availability, including special hours or blocked times.</p>
          <button
            className="w-full font-raleway bg-[#014D52] text-white font-semibold rounded-md py-3 mb-3 text-base hover:bg-[#013847] transition"
            onClick={handleAddBlockedTime}
          >
            Add Blocked Time
          </button>
          <button
            className="w-full font-raleway border border-[#E9E9E9] text-[#014D52] font-semibold rounded-md py-3 text-base bg-white hover:bg-gray-50 transition"
            onClick={handleCancel}
          >
            Cancel
          </button>
        </>
      )} */}
      {/* Step 1: Date & Time Picker */}
      {step === 1 && (
        <>
          <h2 className="text-lg font-bold font-raleway mb-1 mt-2">Select Date and time</h2>
          <p className="text-[#4D4D4D] font-open-sans text-sm mb-4">Choose specific dates and set custom hours for availability or blocked times.</p>
          {/* Calendar */}
          <div className="bg-white rounded-lg border border-[#E9E9E9] p-3 mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium font-raleway text-[#4D4D4D]/70 mr-auto">{calendarMonth.toLocaleString("en-US", { month: "long", year: "numeric" })}</span>
              <div className="flex flex-row items-center gap-2 ml-auto">
                <div
                    onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
                    className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  >
                    <LeftArrow className=" text-black rounded-full  m-auto" />
                  </div>
                  <div
                    onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
                    className='rounded-full h-[32px] w-[32px] border-[1px] border-[#E9E9E9] p-2 hover:bg-gray-200 cursor-pointer my-auto m-1'
                  >
                      <RightArrow className="text-black rounded-full  m-auto" />
                  </div>
                {/* <button className="p-1 rounded hover:bg-gray-100">
                  &lt;
                </button>
                <button  className="p-1 rounded hover:bg-gray-100">
                  &gt;
                </button> */}
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-sm font-medium font-raleway text-[#4D4D4D] mb-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                <div className="w-8" key={d}>{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1 text-center">
              {Array(firstDayOfWeek).fill(null).map((_, i) => <div key={i}></div>)}
              {days.map((day, i) => {
                const isSelected = selectedDates.length === 1 && day.getTime() === selectedDates[0].getTime();
                const isInRange = selectedDates.length === 2 && day >= selectedDates[0] && day <= selectedDates[1];
                const isPastDate = moment(day).isBefore(moment(), 'day');
                return (
                  <button
                    key={i}
                    className={`rounded-md font-raleway w-8 h-8 flex items-center justify-center text-sm font-medium transition ${
                      isPastDate 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                        : isSelected || isInRange 
                          ? 'bg-[#014D52] text-white' 
                          : 'font-semibold border-[1px] border-[#E9E9E9] bg-white text-gray-700 hover:bg-[#E9E9E9]'
                    }`}
                    onClick={() => !isPastDate && handleDateClick(day)}
                    disabled={isPastDate}
                  >
                    {day.getDate()}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Time slots */}
          <div className="mb-4">
            <label className="block font-raleway text-sm font-medium mb-2">Set an available time</label>
            {timeError && (
              <p className="text-red-500 text-xs mb-2 font-open-sans">{timeError}</p>
            )}
            {timeSlots.map((slot, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-2">
                <input
                  type="time"
                  value={slot.start_time}
                  onChange={e => handleTimeChange(idx, "start_time", e.target.value)}
                  className="border font-open-sans border-[#E9E9E9] rounded-md px-2 py-1 w-auto"
                  step="1800"
                />
                <span className="mx-1">-</span>
                <input
                  type="time"
                  value={slot.end_time}
                  onChange={e => handleTimeChange(idx, "end_time", e.target.value)}
                  className="border font-open-sans border-[#E9E9E9] rounded-md px-2 py-1 w-auto"
                  step="1800"
                />
                {timeSlots.length > 1 && (
                  <button onClick={() => handleRemoveTimeSlot(idx)} className="text-red-500 hover:text-red-700 ml-2" title="Remove"><span role="img" aria-label="delete"><DeleteIcon className="text-[#A1A1A1]" /></span></button>
                )}
              </div>
            ))}
            <button onClick={handleAddTimeSlot} className="text-[#014D52] font-open-sans font-semibold text-sm flex items-center gap-1 mt-1"><span role="img" aria-label="add">➕</span> Add</button>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              className="w-1/2 border font-raleway border-[#E9E9E9] text-[#014D52] font-semibold rounded-md py-2 text-base bg-white hover:bg-gray-50 transition"
              onClick={handleBack}
            >
              Cancel
            </button>
            <button
              className="w-1/2 bg-[#014D52] font-raleway text-white font-semibold rounded-md py-2 text-base hover:bg-[#013847] transition"
              onClick={handleApply}
              disabled={selectedDates.length === 0 || timeSlots.length === 0}
            >
              Apply
            </button>
          </div>
        </>
      )}
      {/* Step 2: Review & List */}
      {step === 2 && (
        <>
          <h2 className="text-lg font-bold font-raleway mb-1 mt-2">Add Custom Availability</h2>
          <p className="text-[#4D4D4D] font-open-sans text-sm mb-4">Set specific dates and times for your business availability, including special hours or blocked times.</p>
          <div className="flex flex-col gap-2 mb-4">
            {blockedTimes.map((block, idx) => (
              <div key={idx} className="flex items-center justify-between border-[1px] border-[#E9E9E9] rounded-md px-3 py-2">
                <div className="flex flex-row w-11/12">
                  <div className="font-medium text-sm font-open-sans">{formatDateRange(block.dates)}</div>
                  <div className="text-sm text-gray-700 font-open-sans mx-auto">
                    {block.times.map((t: ClosedTimeBlockType, i: number) => (
                      <div key={i}>{formatTime(t.start_time)} - {formatTime(t.end_time)}</div>
                    ))}
                  </div>
                </div>
                <button onClick={() => handleRemoveBlocked(idx)} className="text-red-500 hover:text-red-700 ml-2" title="Remove"><span role="img" aria-label="delete"><DeleteIcon className="text-[#A1A1A1]" /></span></button>
              </div>
            ))}
          </div>
          <button
            className="w-full font-raleway bg-[#014D52] text-white font-semibold rounded-md py-3 mb-3 text-base hover:bg-[#013847] transition"
            onClick={handleAddBlockedTime}
          >
            Add Blocked Time
          </button>
          <button
            className="w-full font-raleway border border-[#E9E9E9] text-[#014D52] font-semibold rounded-md py-3 text-base bg-white hover:bg-gray-50 transition"
            onClick={handleCancel}
          >
            Cancel
          </button>
        </>
      )}
    </div>
  );
}


type ResourceFeesFormProps = {
  // communityId?: string
  resourceId?: string
  resourceFees: FeeSchema[]
  setIsOpenFee: React.Dispatch<React.SetStateAction<boolean>>
  handleDeleteFeeModal: (feeId: string) => void
}
export const  ResourceFeesForm: React.FC<ResourceFeesFormProps> = ({resourceId, resourceFees, setIsOpenFee, handleDeleteFeeModal}) => {

  // const { fees } = useLoadFees()
  // const {  primaryCommunity }= useLoadCommunity()

  
  const [selectedFees, setSelectedFees] = useState<FeeSchema[]>(resourceFees)

  const { isLoading } = useResourceHook({resourceId})

  // const handleUpdateResourceFees = () => {
  //   // console.log("selectedPlans", selectedPlans)
  //   if(selectedFees.length > 0){
  //     var feeIds = selectedFees.map(fee => fee.id ? fee.id : "");
  //     updateResourceFees(feeIds)
  //   } else {
  //     updateResourceFees([])
  //   }
  // }

  // Check if item already exists in the array & add item if it doesn't exist
  const addSelection = (addedFee: FeeSchema) => {
    const { name, id } = addedFee
    const exists = selectedFees.find((item: FeeSchema) => (item.name === name || item.id === id));
    if (!exists) {
      addedFee.resource_id = resourceId
      setSelectedFees(
        [
          ...selectedFees, 
          addedFee
        ]
      )
    }
  };

  // Remove item by filtering out the item with the given name
  const removeSelection = (feeId: string) => {
    if (feeId){
      setSelectedFees(selectedFees.filter((item: FeeSchema) => item.id !== feeId))
    }
  };


  useEffect(() => {
    document.title = "HomeCook | Member Plans";
    setSelectedFees(resourceFees)
  }, [resourceFees]);

  


  // const feeChangesExist = (): boolean => {
  //   // const resourcePlanIds = resourcePlans.map(plan => plan?.id ? plan.id : "");
  //   const resourceFeeIds = resourceFees && resourceFees.length > 0 ? resourceFees.map(fee => fee?.id).filter((id): id is string => id !== undefined) : [] as string[];
  //   // const selectedPlanIds = selectedPlans.map(plan => plan?.id ? plan.id : "");
  //   const selectedFeeIds = selectedFees && selectedFees.length > 0 ? selectedFees.map(fee => fee?.id).filter((id): id is string => id !== undefined) : [] as string[];
  //   const set1 = new Set(resourceFeeIds);
  //   const set2 = new Set(selectedFeeIds);
  //   return set1.size === set2.size && Array.from(set1).every(value => set2.has(value));
  // };

  if (isLoading){
    return (
      <LoadingScreen bgColor="white" height="h-auto" />
    )
  }

  return (
    <>
      <div className="flex flex-row bg-white h-[75px] w-full border-t-[#E6E6E6] border-t-[1px] ">
        <p className="mx-[24px] my-auto font-open-sans text-[16px] text-[#4D4D4D]/70">
          Manage resource fees here.
        </p>  

      </div>
      <div className="flex flex-row bg-white my-3  rounded-lg w-11/12 mx-auto font-open-sans h-auto">
        <div className="m-[24px] w-full my-auto">
          {/* <p className="text-[#808080] text-[16px] font-raleway my-2">Fees</p> */}
          {
            (resourceFees && resourceFees.length > 0) ? (
              resourceFees.map((fee: FeeSchema, i: number)=> (
                  (selectedFees && selectedFees.length > 0 && selectedFees.some((f: FeeSchema) => fee.name === f.name)) ? (
                    <div 
                      key={i} 
                      className={`flex flex-row bg-[#E6EBED] text-primary-200 text-[16px] font-open-sans rounded-md my-1 p-2 h-[59px] cursor-pointer`}
                      onClick={() => removeSelection(fee.id ? fee.id : "")}
                    >
                        {/* <CheckBox className="mx-2 my-auto" style={{height: 20, width: 20}} /> */}
                        <p className="my-auto mx-2 font-medium">
                          {fee.name}
                          <span className="text-primary-200/40 mx-1 text-[15px] font-normal">
                            &nbsp; ${fee.rate.toFixed(2)}
                          </span>
                        </p>
                        <button onClick={() => handleDeleteFeeModal(fee.name)} className="mx-2 ml-auto" title="Remove">
                          <span role="img" aria-label="delete" className="my-auto">
                            <Delete className="text-red-500 hover:text-red-700" style={{height: 18, width: 18}} />
                          </span>
                        </button>
                    </div>
                  ) : (
                      <div 
                        key={i} 
                        className={`flex flex-row border-[1px] border-[#E9E9E9] text-[16px] font-open-sans rounded-md my-1 p-2 h-[59px] cursor-pointer`}
                        onClick={() => addSelection(fee)}
                      >
                        {/* <CheckBoxOutlineBlank className="mx-2 text-[#CFCFCF] my-auto" style={{height: 20, width: 20}} /> */}
                        <p className="my-auto mx-2 font-medium">
                          {fee.name} 
                          <span className="text-[#CFCFCF] mx-1 text-[15px] font-normal">
                            &nbsp; ${fee.rate.toFixed(2)}
                          </span>
                        </p>
                        <button onClick={() => handleDeleteFeeModal(fee.name)} className="mx-2 ml-auto" title="Remove">
                          <span role="img" aria-label="delete" className="my-auto">
                            <Delete className="text-red-500 hover:text-red-700" style={{height: 18, width: 18}} />
                          </span>
                        </button>
                    </div>
                  )
                ))
            ) : (<></>)
          }
          <div 
            className={`flex flex-row bg-[#F8F8F8] text-[16px] font-open-sans rounded-lg my-2 p-2 h-[59px] cursor-pointer`}
            onClick={()=>setIsOpenFee(true)}
          >
            <AddCircle className="my-auto mx-2" style={{height: 17, width: 17}} />
            <p className="my-auto text-[16px] font-open-sans text-black">Add Custom Fee</p>
          </div>
        </div>
      </div>
    </>
  )
}



type ResourceRulesFormProps = {
  resourceId?: string
  resourceRules: string[]
}
export const  ResourceRulesForm: React.FC<ResourceRulesFormProps> = ({resourceId, resourceRules}) => {
  
  const { isLoading, updateResourceRules, isError } = useResourceHook({resourceId})
  const [selectedRules, setSelectedRules] = useState<string[]>(resourceRules)

  useEffect(() => {
    document.title = "HomeCook | Resource Rules";
    setSelectedRules(resourceRules)
  }, [resourceRules]);

  if (isLoading){
    return (
      <LoadingScreen bgColor="white" height="h-auto" />
    )
  }

  const saveSelectedRules = async () => {
    if (!areObjectsEqual(resourceRules, selectedRules)){
      const result = await updateResourceRules(selectedRules)
      if (result){
        toast.success("Rules updated successfully!")
      } else if (isError){
        toast.error("Error updating rules. Please try again.")
      } else {
        toast.error("Error updating rules. Please try again.")
      }
      
    }
  }

  return (
    <>
      <div className="flex flex-row bg-white h-[75px] w-full border-t-[#E6E6E6] border-t-[1px] ">
        <p className="mx-[24px] my-auto font-open-sans text-[16px] text-[#4D4D4D]/70">
          Manage resource rules here.
        </p>  

        {
          (
            (!areObjectsEqual(selectedRules, resourceRules))
          ) ? (
            <div className="flex ml-auto mx-[24px] h-auto font-open-sans">
              <button
                className="text-primary-200 h-[43px] px-2 border-[1px] border-[#E9E9E9] ml-auto rounded-lg my-auto text-[16px]"
                onClick={saveSelectedRules}
              >
                Save Changes
              </button>
            </div>
          ) : (
            <></>
          )
        }
      </div>
      <div className="flex flex-row bg-white my-3  rounded-lg w-11/12 mx-auto font-open-sans h-auto">
      <div className="flex flex-col m-[24px] w-full my-auto">
          <div className="grid grid-flow-row gap-y-2">
            {selectedRules && selectedRules.length > 0 && selectedRules.map((rule: string, index: number) => (
              <div key={index} className="flex flex-row items-center gap-x-2">
                <input
                  className="border-[1px] border-[#E9E9E9] bg-inherit w-full my-2 rounded-lg px-2 py-2"
                  value={rule}
                  onChange={(e)=>{
                    const next = [...(selectedRules || [])];
                    next[index] = e.target.value;
                    setSelectedRules(next);
                  }}
                  placeholder={`Add rule #${index+1}`}
                />
                <button
                  type="button"
                  className="flex items-center justify-center rounded-lg h-[34px] w-[34px] border-[1px] border-[#E9E9E9]/90 hover:bg-gray-200"
                  onClick={()=>{
                    const next = [...(selectedRules || [])];
                    next.splice(index, 1);
                    setSelectedRules(next);
                  }}
                  title="Remove rule"
                >
                  <DeleteIcon className="text-[#A1A1A1] m-auto" style={{height: 18, width: 18}}/>
                </button>
              </div>
            ))}
          <div 
            className={`flex flex-row bg-[#F8F8F8] text-[16px] font-open-sans rounded-lg my-2 p-2 h-[59px] cursor-pointer`}
            onClick={()=> setSelectedRules([...(selectedRules||[]), ''])}
          >
            <AddCircle className="my-auto mx-2" style={{height: 17, width: 17}} />
            <p className="my-auto text-[16px] font-open-sans text-black">Add New Rule</p>
          </div>

            
          </div>
        </div>
      </div>
    </>
  )
}



type ResourceDiscountsFormProps = {
  resourceId?: string
  resourceDiscounts: DiscountSchema[]
  setIsOpenDiscount: React.Dispatch<React.SetStateAction<boolean>>
  handleDeleteDiscount: (discountName: string) => void
}


export const ResourceDiscountsForm: React.FC<ResourceDiscountsFormProps> = ({resourceId, resourceDiscounts, setIsOpenDiscount, handleDeleteDiscount}) => {

  // const [selectedDiscounts, setSelectedDiscounts] = useState<DiscountSchema[]>(resourceDiscounts)
  const selectedDiscounts = resourceDiscounts

  const { isLoading } = useResourceHook({resourceId})

  // const formatDate = (date: Date) => moment(date).format('ddd, MMM D, YYYY');

  if (isLoading){
    return (
      <LoadingScreen bgColor="white" height="h-auto" />
    )
  }

  return (
    <>
      <div className="flex flex-row bg-white h-[75px] w-full border-t-[#E6E6E6] border-t-[1px] ">
        <p className="mx-[24px] my-auto font-open-sans text-[16px] text-[#4D4D4D]/70">
          Manage resource discounts here.
        </p>
      </div>
      <div className="flex flex-row bg-white my-3  rounded-lg w-11/12 mx-auto font-open-sans h-auto">
        <div className="m-[24px] w-full my-auto">
          {/* <p className="text-[#808080] text-[16px] font-raleway my-2">Discounts</p> */}
          {
            (resourceDiscounts && resourceDiscounts.length > 0) ? (
              resourceDiscounts.map((discount: DiscountSchema, i: number)=> (
                  (selectedDiscounts && selectedDiscounts.length > 0 && selectedDiscounts.some((d: DiscountSchema) => discount.name === d.name)) ? (
                    <div 
                      key={i} 
                      className={`flex flex-row bg-[#E6EBED] text-primary-200 text-[16px] font-open-sans rounded-md my-1 p-2 h-[59px] justify-between cursor-pointer`}
                    >
                        {/* <CheckBox className="mx-2 my-auto" style={{height: 20, width: 20}} /> */}
                        <div className="flex flex-row my-auto mx-2">
                          <p className="my-auto">{discount.name}</p>
                          <span className="my-auto mx-3 pr-3 text-sm bg-primary-300 text-white font-open-sans font-medium rounded-lg p-1 w-auto">
                            &nbsp; {discount.discount_type === 'percentage' ? `${discount.rate.toFixed(0)}% Off` : `- $${discount.rate.toFixed(2)}`}
                          </span>
                          <span className="my-auto mx-3 pr-3 text-xs text-primary-200/50 font-open-sans font-normal">
                          {/* {discount.start_date ? moment(discount.start_date).format('MM/DD/YYYY') : ''} - {discount.end_date ? moment(discount.end_date).format('MM/DD/YYYY') : ''} */}
                          {moment(discount.start_date).year() === moment().year() ? moment(discount.start_date).format('ddd MMM Do') : moment(discount.start_date).format('MMM YYYY')} - {moment(discount.end_date).year() === moment().year() ? moment(discount.end_date).format('ddd MMM Do') : moment(discount.end_date).format('MMM YYYY')}
                          </span>
                        </div>
                        <button onClick={() => handleDeleteDiscount(discount.name)} className="m-2" title="Remove">
                          <span role="img" aria-label="delete" className="my-auto">
                            <Delete className="text-red-500 hover:text-red-700" style={{height: 18, width: 18}} />
                          </span>
                        </button>
                    </div>
                  ) : (
                      <></>
                  )
                ))
            ) : (<></>)
          }
          <div 
            className={`flex flex-row bg-[#F8F8F8] text-[16px] font-open-sans rounded-lg my-2 p-2 h-[59px] cursor-pointer`}
            onClick={()=>setIsOpenDiscount(true)}
          >
            <AddCircle className="my-auto mx-2" style={{height: 17, width: 17}} />
            <p className="my-auto text-[16px] font-open-sans text-black">Add Resource Discount</p>
          </div>
        </div>
      </div>
    </>
  )
}



type ResourcePlanFormProps = {
  // communityId?: string
  resourceId?: string
  resourcePlans: ResourcePlanSchema[]
  resourceType?: ResourceType
  setIsOpenPlan: React.Dispatch<React.SetStateAction<boolean>>
}
export const ResourcePlansForm: React.FC<ResourcePlanFormProps> = ({resourceId, resourcePlans, resourceType, setIsOpenPlan}) => {

  const { plans } = useLoadPlans()
  // const {  primaryCommunity }= useLoadCommunity()

  
  const [selectedPlans, setSelectedPlans] = useState<ResourcePlanSchema[]>(resourcePlans)

  const { updateResourcePlans, isLoading } = useResourceHook({resourceId})

  const handleUpdateResourcePlans = () => {
    // console.log("selectedPlans", selectedPlans)
    if(selectedPlans.length > 0){
      var planIds = selectedPlans.map(plan => plan.plan_id ? plan.plan_id : "");
      updateResourcePlans(planIds)
    } else {
      updateResourcePlans([])
    }
  }

  // Check if item already exists in the array & add item if it doesn't exist
  const addSelection = (addedPlan: ResourcePlanSchema) => {
    const { plan_name, plan_id } = addedPlan
    const exists = selectedPlans.find((item: ResourcePlanSchema) => (item.plan_name === plan_name || item.plan_id === plan_id));
    if (!exists) {
      addedPlan.resource_id = resourceId
      setSelectedPlans(
        [
          ...selectedPlans, 
          addedPlan
        ]
      )
    }
  };

  // Remove item by filtering out the item with the given name
  const removeSelection = (planId: string) => {
    if (planId){
      setSelectedPlans(selectedPlans.filter((item: PlanSchema) => item.plan_id !== planId))
    }
  };


  useEffect(() => {
    document.title = "HomeCook | Member Plans";
  }, []);

  


  const planChangesExist = (): boolean => {
    // const resourcePlanIds = resourcePlans.map(plan => plan?.id ? plan.id : "");
    const resourcePlanIds = resourcePlans.map(plan => plan?.plan_id).filter((id): id is string => id !== undefined);
    // const selectedPlanIds = selectedPlans.map(plan => plan?.id ? plan.id : "");
    const selectedPlanIds = selectedPlans.map(plan => plan?.plan_id).filter((id): id is string => id !== undefined);
    const set1 = new Set(resourcePlanIds);
    const set2 = new Set(selectedPlanIds);
    return set1.size === set2.size && Array.from(set1).every(value => set2.has(value));
  };

  if (isLoading){
    return (
      <LoadingScreen bgColor="white" height="h-auto" />
    )
  }

  return (
    <>
      <div className="flex flex-row bg-white h-[75px] w-full border-t-[#E6E6E6] border-t-[1px] ">
        <p className="mx-[24px] my-auto font-open-sans text-[16px] text-[#4D4D4D]/70">
          Manage resource plans here.
        </p>  
        {
          (!planChangesExist()) ? (
          <div className="flex ml-auto mx-[24px] h-auto font-open-sans">
            <button 
              className="text-primary-200 h-[43px] px-2 border-[1px] border-[#E9E9E9] font-open-sans ml-auto rounded-lg my-auto text-[16px] mx-[24px]"
                // className="bg-primary-200 text-white font-open-sans h-[43px] px-2 border-[1px] border-[#E9E9E9] ml-auto rounded-lg my-auto text-[16px]"
                onClick={handleUpdateResourcePlans}
              >
                Save Changes
            </button>
          </div>
          ) : (
            <></>
          )
        }
      </div>
      <div className="flex flex-row bg-white my-3  rounded-lg w-11/12 mx-auto font-open-sans h-auto">
        <div className="m-[24px] w-full my-auto">
          <p className="text-[#808080] text-[16px] font-raleway my-2">Membership Plans</p>
          {
            (plans && plans.length > 0) ? (
              plans.map((plan, i)=> (
                  (selectedPlans && selectedPlans.length > 0 && selectedPlans.some((p) => plan.plan_name === p.plan_name)) ? (
                    <div 
                      key={i} 
                      className={`flex flex-row bg-[#E6EBED] text-primary-200 text-[16px] font-open-sans rounded-md my-1 p-2 h-[59px] cursor-pointer`}
                      onClick={() => removeSelection(plan.plan_id ? plan.plan_id : "")}
                    >
                        <CheckBox className="mx-2 my-auto" style={{height: 20, width: 20}} />
                        <p className="my-auto">
                          {plan.plan_name}
                          <span className="text-primary-200/40 mx-1 text-xs">
                            ({plan.plan_recurrence && capitalizeString(plan.plan_recurrence)}: ${styleThousandCommas(plan.plan_rate || 0)})
                          </span>
                        </p>
                    </div>
                  ) : (
                      <div 
                        key={i} 
                        className={`flex flex-row border-[1px] border-[#E9E9E9] text-[16px] font-open-sans rounded-md my-1 p-2 h-[59px] cursor-pointer`}
                        onClick={() => addSelection({ ...plan, plan_type: plan.plan_type ?? "membership" })}
                      >
                        <CheckBoxOutlineBlank className="mx-2 text-[#CFCFCF] my-auto" style={{height: 20, width: 20}} />
                        <p className="my-auto">
                          {plan.plan_name} 
                          <span className="text-[#CFCFCF] mx-1 text-xs">
                            ({plan.plan_recurrence && capitalizeString(plan.plan_recurrence)}: ${styleThousandCommas(plan.plan_rate || 0)})
                          </span>
                        </p>
                    </div>
                  )
                ))
            ) : (<></>)
          }
          <div 
            className={`flex flex-row bg-[#F8F8F8] text-[16px] font-open-sans rounded-lg my-2 p-2 h-[59px] cursor-pointer`}
            onClick={()=>setIsOpenPlan(true)}
          >
            <AddCircle className="my-auto mx-2" style={{height: 17, width: 17}} />
            <p className="my-auto text-[16px] font-open-sans text-black">Add {resourceType ? capitalizeString(resourceType) : 'Resource'} Plan</p>
          </div>
        </div>
      </div>
    </>
  )
}



type ResourceRequirementsFormProps = {
  // communityId: string
  resourceId: string
  resourceRequirements: RequirementSchema[]
  communityRequirements: RequirementSchema[]
  setIsAddingCustomRequirement: React.Dispatch<React.SetStateAction<boolean>>
}

export const ResourceRequirementsForm: React.FC<ResourceRequirementsFormProps> = ({resourceId, communityRequirements, resourceRequirements, setIsAddingCustomRequirement}) => {

  
  // const { requirements } = useLoadRequirements()
  const [selectedRequirements, setSelectedRequirements] = useState<RequirementSchema[]>(resourceRequirements)

  const { updateResourceRequirements, isLoading, isError, isSuccess } = useResourceHook({resourceId})

  const handleUpdateResourceRequirements = () => {
    if(selectedRequirements.length > 0){
      var requirementIds = selectedRequirements.map(requirement => requirement.code ? requirement.code : undefined).filter((code): code is string => code !== undefined);
      updateResourceRequirements(requirementIds)
    } else {
      updateResourceRequirements([])
    }
    if(isError){
      toast.error("Error updating requirements. Please try again.")
    } else{
      toast.success("Requirements updated successfully!")
    }
    setIsAddingCustomRequirement(false)
  }


  const requirementChangesExist = (): boolean => {
    // console.log("resourceRequirements", resourceRequirements)
    const resourceRequirementIds = resourceRequirements.map(req => req?.code).filter((code): code is string => code !== undefined);
    // console.log("resourceRequirementIds", resourceRequirementIds)
    // console.log("selectedRequirements", selectedRequirements)
    const selectedRequirementIds = selectedRequirements.map(req => req?.code).filter((code): code is string => code !== undefined);
    // console.log("selectedRequirementIds", selectedRequirementIds)
    const set1 = new Set(resourceRequirementIds);
    const set2 = new Set(selectedRequirementIds);
    return set1.size === set2.size && Array.from(set1).every(value => set2.has(value));
  };

  // Check if item already exists in the array & add item if it doesn't exist
  const addSelection = (addedRequirement: RequirementSchema) => {
    const { name, code } = addedRequirement
    const exists = selectedRequirements.find((item: RequirementSchema) => item.name === name);
    if (!exists) {
      setSelectedRequirements(
        [
          ...selectedRequirements, 
          {
            name, 
            code,
            // community_id: communityId,
            resource_id: resourceId ? resourceId : ""
          }
        ]
      )
    }
  };

  // Remove item by filtering out the item with the given name
  const removeSelection = (requirementName: string) => {
    setSelectedRequirements(selectedRequirements.filter((item: RequirementSchema) => item.name !== requirementName))
  };


  useEffect(() => {
    document.title = "HomeCook | Resource Requirements";
  }, []);

  if (isLoading){
    return (
      <LoadingScreen bgColor="white" height="h-auto" />
    )
  } 
  return (
    <>
      <div className="flex flex-row bg-white h-[75px] w-full border-t-[#E6E6E6] border-t-[1px] ">
        <p className="mx-[24px] my-auto font-open-sans text-[16px] text-[#4D4D4D]/70">
          Choose requirements from the list below, or add your own.
        </p>  

        {        
          (
            (!requirementChangesExist())
          ) ? (
            <button 
              className="text-primary-200 h-[43px] px-2 border-[1px] border-[#E9E9E9] font-open-sans ml-auto rounded-lg my-auto text-[16px] mx-[24px]"
              // className="bg-primary-200 text-white ml-auto w-auto px-3 py-2 rounded-md text-[16px] font-raleway my-auto mx-[24px]"
              onClick={handleUpdateResourceRequirements}
            >
              {/* <CheckCircle className="my-auto mx-2 text-white" style={{height: 17, width: 17}} /> */}
              Save Changes
            </button>
          ) : (<></>)
        }
      </div>
      <div className="flex flex-col w-11/12 bg-white mx-auto my-6 rounded-md h-[600px]">
        {
          (isSuccess && !isError && !isLoading) && (
            <div className="flex flex-row w-auto font-raleway text-gray-600 text-center mx-[24px] my-4 h-[50px] bg-green-200 rounded-md">
              <p className="m-auto">Update Successful</p>
            </div>
          )
        }
        {
          (isError) && (
            <div className="flex flex-row w-auto font-raleway text-gray-700 text-center mx-[24px] my-4 h-[50px] bg-red-200 rounded-md">
              <p className="m-auto">Update Failed</p>
            </div>
          )
        }
        <div className="flex flex-col w-auto mx-[24px] my-4 h-[777px] overflow-y-scroll">
            {
            (communityRequirements && communityRequirements?.length > 0) ? (
              communityRequirements.map((req, i: number)=> (

                (selectedRequirements && selectedRequirements.length > 0 && selectedRequirements.some((requirement: RequirementSchema) => requirement.code === req.code)) ? (
                  <div 
                    key={i} 
                    className={`flex flex-row bg-[#E6EBED] text-primary-200 text-[16px] font-open-sans rounded-md my-1 p-2 h-[59px] cursor-pointer`}
                    onClick={() => removeSelection(req?.name as string)}
                  >
                      <CheckBox className="mx-2 my-auto" style={{height: 20, width: 20}} />
                      <p className="my-auto">{req?.name}</p>
                      {req.validity && (
                        <span className="ml-2 text-xs text-primary-300">{req.validity}</span>
                      )}
                  </div>
                ) : (
                    <div 
                      key={i} 
                      className={`flex flex-row border-[1px] hover:bg-[#E9E9E9] border-[#E9E9E9] text-[16px] font-open-sans rounded-md my-1 p-2 h-[59px] cursor-pointer`}
                      onClick={() => addSelection(req)}
                    >
                      <CheckBoxOutlineBlank 
                        className="mx-2 text-[#CFCFCF] my-auto" 
                        style={{height: 20, width: 20}} 
                      />
                      <p className="my-auto">{req?.name}</p>
                      {req.validity && (
                        <span className="ml-2 text-xs text-primary-300">{req.validity}</span>
                      )}
                  </div>
                )
              ))
            ) : (<></>)
            }
        </div>
        <div onClick={() => setIsAddingCustomRequirement(true)} className={`flex flex-row bg-[#F8F8F8] text-[16px] font-open-sans rounded-lg m-2 p-2 h-[59px] cursor-pointer`}>
          <AddCircle className="my-auto mx-2" style={{height: 17, width: 17}} />
          <p className="my-auto text-[16px] font-open-sans text-black">Add Custom Requirement</p>
        </div>
      </div>
    </>
  )
}



type ResourceImagesFormProps = {
  resource: ResourceSchema
  // setResource: React.Dispatch<React.SetStateAction<ResourceSchema>>
  currentResource: ResourceSchema
  resourceType: ResourceType
  updateResourceData?: (resource: ResourceSchema, resourceOptionTypes?: ResourceOptionsPropsType) => Promise<boolean | void> | boolean | void
  // handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>)=> void
}
export const ResourceImagesForm: React.FC<ResourceImagesFormProps> = ({resourceType, resource, currentResource, updateResourceData}) => {

  resource = resource ? resource : currentResource

  // Initialize bgPhotos array (up to 5) with backwards compatibility for bgPhoto (singular)
  const initializeBgPhotos = () => {
    if (resource?.bgPhotos && resource.bgPhotos.length === 5) {
      return resource.bgPhotos;
    }
    // Backwards compatibility: if bgPhoto exists but bgPhotos doesn't, use bgPhoto
    const existingPhotos = resource?.bgPhotos || (resource?.bgPhoto ? [resource.bgPhoto] : []);
    // Pad to 5 elements with empty strings
    const paddedPhotos = [...existingPhotos];
    while (paddedPhotos.length < 5) {
      paddedPhotos.push("");
    }
    return paddedPhotos.slice(0, 5);
  };

  const bgPhotos = initializeBgPhotos();
  const [selectedbgPhotos, setSelectedbgPhotos] = useState<string[]>(bgPhotos);
  const [selectedLogo, setSelectedLogo] = useState(resource.logo);

  useEffect(() => {
    const refreshedPhotos = initializeBgPhotos();
    setSelectedbgPhotos(refreshedPhotos);
    setSelectedLogo(resource.logo);
  }, [resource?.bgPhotos, resource?.bgPhoto, resource?.logo]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {name, files} = event.target
    const file = files?.[0]
    
    if (file) {
      // Read the selected image file as a data URL
      const reader = new FileReader();
      reader.onload = (e) => {
        if (name === "logo") {
          setSelectedLogo(e.target?.result as string);
        } else if (name.startsWith("bgPhoto")) {
          // Extract index from name (format: "bgPhoto-0", "bgPhoto-1", etc.)
          const indexMatch = name.match(/bgPhoto-(\d+)/);
          if (indexMatch) {
            const index = parseInt(indexMatch[1], 10);
            if (index >= 0 && index < 5) {
              // Update the specific index in the array
              setSelectedbgPhotos(prev => {
                const updated = [...prev];
                updated[index] = e.target?.result as string;
                return updated;
              });
            }
          }
        }
      };
      reader.readAsDataURL(file);
    } else {
      if (name === "logo") {
        setSelectedLogo("");
      } else if (name.startsWith("bgPhoto")) {
        // Extract index from name
        const indexMatch = name.match(/bgPhoto-(\d+)/);
        if (indexMatch) {
          const index = parseInt(indexMatch[1], 10);
          if (index >= 0 && index < 5) {
            // Clear the specific index
            setSelectedbgPhotos(prev => {
              const updated = [...prev];
              updated[index] = "";
              return updated;
            });
          }
        }
      }
    }
    handleFileChange(event);
  };


  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = event.target;
    if (name && files && files.length > 0) {
      // Note: The resource state stores data URLs (strings), not File objects
      // This function is kept for compatibility but files are handled via state
      if (name === "logo") {
        // Logo file is handled separately - we store the data URL in selectedLogo state
        // The actual file can be accessed via the event if needed for upload
      } else if (name.startsWith("bgPhoto")) {
        // The data URLs are stored in selectedbgPhotos state
      }
    }
  };

  const handleRemovePhoto = (index: number) => {
    setSelectedbgPhotos(prev => prev.map((photo, i) => i === index ? "" : photo));
  };

  const handleSaveChanges = async () => {
    const normalizeImageFields = (res?: ResourceSchema | null): ResourceSchema => {
      const normalized: ResourceSchema = { ...(res || {} as ResourceSchema) };
      normalized.logo = normalized.logo || "";

      const derivedBgPhotos =
        Array.isArray(normalized.bgPhotos) && normalized.bgPhotos.length > 0
          ? normalized.bgPhotos
          : normalized.bgPhoto
            ? [normalized.bgPhoto]
            : [];

      normalized.bgPhotos = derivedBgPhotos
        .filter((photo) => typeof photo === "string" && photo.trim() !== "")
        .slice(0, 5);

      if (normalized.bgPhotos.length === 1) {
        normalized.bgPhoto = normalized.bgPhotos[0];
      } else if (normalized.bgPhotos.length > 1) {
        delete normalized.bgPhoto;
      } else {
        normalized.bgPhoto = undefined;
      }

      return normalized;
    };

    const baseResource = resource ? { ...resource } : { ...(currentResource || {} as ResourceSchema) };
    const originalResource = normalizeImageFields(currentResource);

    const filteredPhotos = selectedbgPhotos
      .filter((photo) => photo && photo.trim() !== "")
      .slice(0, 5);

    const updatedResource: ResourceSchema = {
      ...baseResource,
      logo: selectedLogo || "",
      bgPhotos: filteredPhotos || [],
      bgPhoto: filteredPhotos.length === 1 ? filteredPhotos[0] : undefined,
    };

    if (filteredPhotos.length > 1) {
      delete updatedResource.bgPhoto;
    }

    const normalizedUpdatedResource = normalizeImageFields(updatedResource);

    const changes = getChangedAttributes<ResourceSchema>(
      originalResource,
      normalizedUpdatedResource
    );

    if (Object.keys(changes).length > 0 && updateResourceData !== undefined) {
      const result = await updateResourceData(normalizedUpdatedResource);
      const didSucceed = result === false ? false : true;

      if (didSucceed) {
        const paddedPhotos = [...(normalizedUpdatedResource.bgPhotos || [])];
        while (paddedPhotos.length < 5) {
          paddedPhotos.push("");
        }
        setSelectedbgPhotos(paddedPhotos);
        setSelectedLogo(normalizedUpdatedResource.logo || "");
      }
    }
  }

  useEffect(() => {
    document.title = "HomeCook | Logo & Images";
  }, [resource]);

  return ( 
    <>
      <div className="flex flex-row bg-white h-[75px] w-full border-t-[#E6E6E6] border-t-[1px] ">
        <p className="mx-[24px] my-auto font-open-sans text-[16px] text-[#4D4D4D]/70">
            View and edit your resource images.
        </p>
        {
          (() => {
            // Filter selectedbgPhotos the same way as in handleSaveChanges for accurate comparison
            const filteredSelectedPhotos = selectedbgPhotos.filter(photo => photo && photo.trim() !== "").slice(0, 5);
            
            // Backwards compatibility: if bgPhotos exists, use it; otherwise check bgPhoto (singular)
            const currentResourcePhotos = currentResource?.bgPhotos || (currentResource?.bgPhoto ? [currentResource.bgPhoto] : []);
            const currentBgPhotos = currentResourcePhotos.filter(photo => photo && photo.trim() !== "");
            
            const hasBgPhotosChanged = !areObjectsEqual(currentBgPhotos, filteredSelectedPhotos.length > 0 ? filteredSelectedPhotos : []);
            const hasLogoChanged = !areObjectsEqual(currentResource?.logo || "", selectedLogo || "");
            
            return (hasBgPhotosChanged || hasLogoChanged) ? (
              <div className="flex ml-auto mx-[24px] h-auto font-open-sans">
                <button
                  className="text-primary-200 h-[43px] px-2 border-[1px] border-[#E9E9E9] ml-auto rounded-lg my-auto text-[16px]"
                  onClick={handleSaveChanges}
                >
                  Save Changes
                </button>
              </div>
            ) : (
              <></>
            );
          })()
        }
      </div> 
      <div className="flex flex-col w-11/12 mx-auto font-open-sans h-auto">
        <div className="flex flex-col bg-white my-3 h-[324px] w-full rounded-lg">
          <div className="flex flex-col m-[24px]">
            <p className="text-[16px] font-raleway font-semibold text-black">Upload Logo</p>
            <p className="text-[16px] font-open-sans font-light text-[#4D4D4D]">Upload a logo to represent your {resourceType}.</p>
            <>
            <div className="flex flex-row text-2xl text-[#000] h-[206px] border-[1px] rounded-lg border-[#E9E9E9] my-4">
              <CustomAddImageBox name="logo" height="158px" width="200px" selectedImage={selectedLogo} setSelectedImage={setSelectedLogo} handleChange={handleImageChange} />
              <div className="flex flex-col font-open-sans text-[16px] text-[#4D4D4D] my-auto mx-2">
                <span><CheckCircle className="text-primary-400 mx-1" /> Accepted formats: JPEG (.jpg, .jpeg), PNG (.png), SVG (.svg)</span>
                <span><CheckCircle className="text-primary-400 mx-1" /> At least 500x500 pixels</span>
                <span><CheckCircle className="text-primary-400 mx-1" /> Maximum file size: 5MB</span>
                <span><CheckCircle className="text-primary-400 mx-1" /> Transparent background preferred for versatility</span>
              </div>
            </div>
            </>
          </div>
        </div>

        <div className="flex flex-col bg-white my-3 h-[324px] w-full rounded-lg">
          <div className="flex flex-col m-[24px]">
          <p className="text-[16px] font-raleway font-semibold text-black">Upload {capitalizeString(resourceType || 'resource')} Images</p>
          <p className="text-[16px] font-open-sans font-light text-[#4D4D4D]">Upload clear images that showcase your {resourceType ? resourceType : 'resource'}.</p>
            <>
            <div className="flex flex-row text-2xl text-[#000] w-full h-[206px] border-[1px] rounded-lg border-[#E9E9E9] my-4 p-2">
              {
                selectedbgPhotos.map((bgPhoto, index) => (
                  <div key={index} className="flex flex-col w-full h-[158px] m-auto relative">
                    <CustomAddImageBox 
                      key={index} 
                      name={`bgPhoto-${index}`}
                      height="158px" 
                      width="100%" 
                      selectedImage={bgPhoto} 
                      setSelectedImage={(image) => setSelectedbgPhotos(prev => prev.map((photo, i) => i === index ? (image as string) : photo))} 
                      handleChange={handleImageChange} 
                    />
                    {bgPhoto && bgPhoto.trim() !== "" && (
                      <button
                        onClick={() => handleRemovePhoto(index)}
                        className="absolute top-0 right-0 bg-black rounded-full p-1 mr-3 mt-1 flex items-center justify-center hover:bg-gray-800 transition-colors z-10"
                        title="Remove image"
                        type="button"
                      >
                        <Cancel className="text-white" style={{height: 16, width: 16}} />
                      </button>
                    )}
                  </div>
                ))
              }
            </div>
            </>
          </div>
        </div>
      </div>
    </>
  )
}

export {
  AddKitchenForm,
  // AddReservationForm,
  DeleteConfirmationForm,
  NewMessageForm,
  AddMemberForm,
  // AddInventoryForm,
  AddRequirementForm,
  AddCredentialForm,
};

// Helper to round to fixed decimals
function roundTo(value: number, decimals: number) {
  return Number(Math.round(Number(value + 'e' + decimals)) + 'e-' + decimals);
}
